import React, { useState, useEffect, useCallback, useRef } from "react";
import * as AllIcons from "iconoir-react";
import { useAuth as useAuthHook, usePeople as usePeopleHook, useTasks as useTasksHook,
  useSwaps as useSwapsHook, useMeals as useMealsHook,
  useCategories as useCategoriesHook, useRecipes as useRecipesHook,
  useShoppingList as useShoppingListHook, useMealThemes as useMealThemesHook,
  useRunningLow as useRunningLowHook, useEnergy as useEnergyHook,
  useBinCollections as useBinCollectionsHook, useDIY as useDIYHook,
  useHearts as useHeartsHook,
  useTurnips as useTurnipsHook,
  importRecipeFromUrl
} from "./useHomeHQ";
import { PetDisplay, EggPicker, NamePetModal, usePets, getMoodForProgress, STAGE_NAMES, SPECIES } from "./PetSystem";
import { supabase } from "./supabaseClient";

/* ═══════════════════════════════════════════════════════════════════════
   ICON MAP — shorthand aliases for our snake_case names
   ═══════════════════════════════════════════════════════════════════════ */
const ICON_ALIAS = {
  home: "Home", check: "Check", plus: "Plus", settings: "Settings",
  edit: "EditPencil", trash: "Trash", sun: "SunLight", calendar: "Calendar",
  refresh: "Refresh", moon: "HalfMoon", people: "Group", star: "Star",
  leaf: "Leaf", wash: "Wash", washing_machine: "WashingMachine",
  paw: "Wolf", shop: "Shop", pizza_slice: "PizzaSlice",
  box: "Cube", spark: "Flash", arrow_left: "NavArrowLeft",
  clock: "Clock", close: "Xmark", list: "List",
  chevron_right: "NavArrowRight", shield: "ShieldCheck", timer: "Timer",
  mail: "Mail", chat: "ChatBubble", swap: "Shuffle", cutlery: "Cutlery", link: "Link",
  pot: "CoffeeCup", flash: "Flash",
};

// Build a list of all available icon names for the picker
const ALL_ICON_NAMES = Object.keys(AllIcons).filter(k =>
  k[0] === k[0].toUpperCase() && AllIcons[k] && k !== 'IconoirProvider' && k !== 'IconoirContext'
  && !k.endsWith('Solid')
).sort();

/* Custom Tanuki (cat) SVG icon */
function TanukiIcon({size=20,color="currentColor",style={}}){
  return(
    <svg width={size} height={size} viewBox="0 0 110 135" fill={color} stroke="none"
      style={{display:"inline-block",verticalAlign:"middle",flexShrink:0,...style}}>
      <path d="M99.94,62.49c-.23-.96-1.2-1.54-2.16-1.3l-4.47,1.11c-.6-3.25-1.84-6.34-3.65-9.1.26-9.73-1.37-19.42-4.8-28.53-.18-.45-.54-.8-.99-.98-.45-.18-.95-.17-1.39.03-6.54,2.94-12.69,11.17-16.14,16.45h0c-3.75-.58-7.54-.86-11.33-.84-3.79-.02-7.58.27-11.33.84-3.44-5.28-9.6-13.52-16.14-16.45h0c-.44-.2-.94-.21-1.39-.03-.45.18-.81.54-.99.98-3.43,9.11-5.07,18.8-4.8,28.53-1.81,2.76-3.05,5.85-3.65,9.1l-4.47-1.11c-.96-.24-1.93.35-2.16,1.3-.24.96.34,1.93,1.3,2.16l4.87,1.21c-.04.66-.06,1.34-.07,2.02,0,1.9.19,3.8.55,5.66l-5.36,1.33c-.96.23-1.54,1.2-1.3,2.16.23.96,1.2,1.54,2.16,1.3l5.42-1.34c3.05,9.02,12.05,19.43,37.37,19.43s34.32-10.41,37.37-19.43l5.42,1.34c.96.23,1.93-.35,2.16-1.3.24-.96-.34-1.93-1.3-2.16l-5.36-1.33h0c.36-1.87.55-3.76.55-5.66,0-.68-.02-1.36-.07-2.02l4.87-1.21c.96-.24,1.54-1.21,1.3-2.16h0Z"/>
      <path d="M56.78,69.59c.99,0,1.79-.8,1.79-1.79s-.8-1.79-1.79-1.79h-3.57c-.99,0-1.79.8-1.79,1.79s.8,1.79,1.79,1.79h.51l-.05,6.14h-.91c-.74,0-1.34.6-1.34,1.34s.6,1.34,1.34,1.34h4.47c.74,0,1.34-.6,1.34-1.34s-.6-1.34-1.34-1.34h-.88l.05-6.14h.38Z"/>
      <path d="M42.77,62.29c-.88,0-1.68.53-2.02,1.35-.34.82-.15,1.76.47,2.38s1.57.81,2.38.48c.82-.34,1.35-1.14,1.35-2.02,0-1.21-.98-2.18-2.19-2.18h0Z"/>
      <path d="M67.23,62.29c-.88,0-1.68.53-2.02,1.35-.34.82-.15,1.76.47,2.38s1.57.81,2.38.48c.82-.34,1.35-1.14,1.35-2.02,0-.58-.23-1.13-.64-1.54-.41-.41-.96-.64-1.55-.64h0Z"/>
    </svg>
  );
}

function TurnipIcon({size=20,color="#558b2f",style={}}){
  return(
    <svg width={size} height={size} viewBox="0 0 4445 4445" fill={color} stroke="none"
      style={{display:"inline-block",verticalAlign:"middle",flexShrink:0,fillRule:"evenodd",clipRule:"evenodd",...style}}>
      <path d="M811.097,1344.39c-259.987,306.709 -327.612,728.582 -203.635,1241.26c5.436,33.016 143.256,813.204 915.731,1193.11c228.249,130.973 498.237,351.465 520.893,458.232c17.722,83.21 94.447,142.57 179.393,142.57c84.441,0 157.469,-58.819 175.551,-141.336c23.521,-106.73 294.603,-327.946 512.989,-454.168c780.556,-385.171 920.119,-1164.99 923.274,-1185.78c126.408,-525.272 58.818,-946.969 -201.214,-1253.87c-269.701,-318.233 -686.508,-433.582 -1000.13,-474.106l269.051,-272.893c69.691,-70.742 69.003,-184.504 -1.739,-254.226c-70.923,-69.691 -184.505,-69.002 -254.226,1.74l-244.735,248.251l0.072,-348.487c0,-99.373 -80.418,-179.791 -179.791,-179.791c-99.373,0 -179.792,80.418 -179.792,179.791l0,348.487l-244.735,-248.251c-69.69,-70.742 -183.446,-71.467 -254.226,-1.74c-70.705,69.691 -71.43,183.447 -1.739,254.189l268.977,272.818c-313.593,40.518 -730.289,155.948 -1000.04,474.209l0.067,0Zm273.505,233.314c417.095,-493.961 1866.01,-483.996 2275.25,-0.87c181.712,214.36 224.009,529.661 123.253,949.474c-1.051,6.306 -112.704,630.629 -740.179,940.66c-70.742,40.734 -336.389,199.794 -520.021,398.864c-173.993,-190.117 -425.9,-344.284 -530.895,-404.301c-612.9,-301.774 -725.614,-909.756 -732.461,-947.525c-98.101,-406.946 -55.955,-721.912 125.035,-936.3l0.019,-0.002Z" fillRule="nonzero"/>
    </svg>
  );
}

/* Unified Icon component — resolves alias or PascalCase name from iconoir-react */
function Icon({name,size=20,color="currentColor",sw=1.8,style={}}){
  if(name==="tanuki") return <TanukiIcon size={size} color={color} style={style}/>;
  if(name==="turnip") return <TurnipIcon size={size} color={color} style={style}/>;
  // Resolve: try alias first, then direct PascalCase name
  const resolved = ICON_ALIAS[name] || name;
  const Comp = AllIcons[resolved];
  if(!Comp) return null;
  return <Comp width={size} height={size} color={color} strokeWidth={sw} style={{display:"inline-block",verticalAlign:"middle",flexShrink:0,...style}}/>;
}

/* ═══════════════════════════════════════════════════════════════════════
   ICON PICKER — searchable grid like iOS emoji picker
   ═══════════════════════════════════════════════════════════════════════ */
function IconPicker({value,onChange,color="var(--gold)"}){
  const[open,setOpen]=useState(false);
  const[search,setSearch]=useState("");
  const inputRef=useRef(null);

  // Filter icons by search
  const filtered=search.trim()
    ?ALL_ICON_NAMES.filter(n=>n.toLowerCase().includes(search.toLowerCase())).slice(0,200)
    :ALL_ICON_NAMES.slice(0,200);

  // Curated "popular for home" shown when no search
  const POPULAR=["Home","HomeSimpleDoor","HomeSimple","HomeTable","HouseRooms",
    "Bed","BedReady","Bathroom","Closet","Sofa","TwoSeaterSofa","SleeperChair","Desk","Table",
    "Lamp","SmallLamp","SmallLampAlt","LightBulb","LightBulbOn","DimmerSwitch",
    "Key","Lock","Door","Window","Balcony","Garage","Fence",
    "Wash","WashingMachine","Dishwasher","Iron","Soap","Shirt","Hanger",
    "CoffeeCup","Cutlery","PizzaSlice","BreadSlice","Egg","CrackedEgg","Cookie","IceCream","Chocolate",
    "Bbq","Fridge","Pot","Flask","Apple","OrangeSlice",
    "ShoppingBag","Cart","Shop","Bag","Gift","CreditCard","Coins","Wallet","PiggyBank",
    "Car","Bicycle","Bus","Garage","Gas","Motorcycle","Truck",
    "Leaf","Flower","Tree","AfricanTree","WateringSoil","SunLight","Cloud","Rain","Snow","Wind","Umbrella",
    "Wolf","Fishing","Running","Gym","Swimming","Yoga","Cycling","Treadmill","Walking","Stretching",
    "Star","Heart","BrightStar","Trophy","Medal","Crown","ThumbsUp",
    "Book","BookStack","GraduationCap","Puzzle","Gamepad","Music","MusicNote","Headset",
    "Calendar","Clock","Timer","Alarm","Bell","BellNotification","Mail","Phone",
    "Camera","VideoCamera","Microphone","Tv","ModernTv",
    "Wrench","Hammer","Tools","Ruler","Drill","Screw","PaintBrush","AngleTool",
    "Shield","ShieldCheck","WarningTriangle","FireFlame","Droplet","DropletHalf",
    "Bin","BinFull","BinHalf","Package","Box","Archive","Suitcase",
    "Flash","Battery","BatteryFull","PlugTypeG","SwitchOn","Wifi","AirConditioner",
    "Stroller","Crib","BirthdayCake","Bonfire","Candle",
    "Check","CheckCircle","Plus","Minus","Flag","Pin","Bookmark","Notes",
    "Group","User","UserPlus","Community","PeopleTag",
    "Refresh","Settings","Search","Eye","InfoCircle","HelpCircle",
    "Smoking","CigaretteSlash","Pill","Healthcare","HealthShield",
    "Delivery","DeliveryTruck","Safe","Calculator","Printer"];

  const displayList=search.trim()?filtered:POPULAR.filter(n=>AllIcons[n]);

  return(<div style={{position:"relative"}}>
    {/* Selected icon button */}
    <button onClick={()=>{setOpen(!open);setTimeout(()=>inputRef.current?.focus(),100);}} style={{
      width:48,height:48,borderRadius:12,border:open?`2.5px solid ${color}`:"2px solid var(--bg-muted)",
      background:open?color+"15":"var(--bg-card)",cursor:"pointer",
      display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
      <Icon name={value||"box"} size={22} color={color}/>
    </button>

    {/* Picker dropdown */}
    {open&&(
      <div style={{position:"fixed",top:"auto",left:16,right:16,bottom:80,zIndex:9999,width:"auto",maxWidth:360,maxHeight:340,
        background:"var(--bg-card)",borderRadius:16,boxShadow:"0 8px 32px rgba(30,20,8,.18)",
        border:"1.5px solid var(--bg-muted)",overflow:"hidden",display:"flex",flexDirection:"column"}}>
        {/* Search */}
        <div style={{padding:"10px 12px",borderBottom:"1px solid var(--bg-muted)"}}>
          <input ref={inputRef} value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search icons..." style={{width:"100%",padding:"8px 12px",borderRadius:10,
              border:"2px solid var(--bg-muted)",fontSize:13,background:"var(--bg)",outline:"none",
              fontFamily:"inherit"}}/>
        </div>
        {/* Grid */}
        <div style={{flex:1,overflow:"auto",padding:"8px",
          display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
          {displayList.map(iconName=>{
            const IconComp=AllIcons[iconName];
            if(!IconComp)return null;
            const isSelected=value===iconName||ICON_ALIAS[value]===iconName;
            return(<button key={iconName} onClick={()=>{onChange(iconName);setOpen(false);setSearch("");}}
              title={iconName.replace(/([A-Z])/g,' $1').trim()}
              style={{width:36,height:36,borderRadius:8,border:"none",cursor:"pointer",
                background:isSelected?color+"20":"transparent",
                display:"flex",alignItems:"center",justifyContent:"center",
                transition:"background .1s"}}>
              <IconComp width={18} height={18} color={isSelected?color:"var(--text-2)"} strokeWidth={1.8}/>
            </button>);
          })}
          {displayList.length===0&&(
            <div style={{gridColumn:"1/-1",padding:20,textAlign:"center",color:"var(--text-3)",fontSize:12}}>
              No icons match "{search}"</div>)}
        </div>
        {/* Close */}
        <button onClick={()=>{setOpen(false);setSearch("");}} style={{padding:"8px",
          background:"none",border:"none",borderTop:"1px solid var(--bg-muted)",cursor:"pointer",
          fontSize:11,fontWeight:600,color:"var(--text-3)"}}>Close</button>
      </div>
    )}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   APP CONTEXT — people + categories shared across all components
   ═══════════════════════════════════════════════════════════════════════ */
const AppContext=React.createContext({people:[],gp:()=>null,cats:[],gc:()=>null});
function useAppCtx(){return React.useContext(AppContext);}

/* ═══════════════════════════════════════════════════════════════════════
   STYLES
   ═══════════════════════════════════════════════════════════════════════ */
const CSS=`
@import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@300;400;500;600;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
:root{
  --bg:#F3EEE5;--bg-card:#FEFCF7;--bg-muted:#EAE4D8;
  --gold:#F5B731;--gold-light:#FDE8A0;--gold-deep:#D49B1A;--gold-bg:#FEF6E0;
  --green:#6EAA5C;--green-light:#B8E0A8;--green-bg:#E8F5E0;
  --coral:#E87461;--coral-light:#F5B0A0;--coral-bg:#FDECE8;
  --sky:#5BACD8;--sky-light:#A8D8F0;--sky-bg:#E4F2FB;
  --lilac:#9B82C8;--lilac-light:#CCBDE8;--lilac-bg:#EFEBF8;
  --peach:#F0A868;--peach-light:#F5D0A0;--peach-bg:#FDF2E4;
  --mint:#5CB89A;--mint-light:#A8E0C8;--mint-bg:#E4F5EC;
  --text:#1E1408;--text-2:#5C4A38;--text-3:#9E8E7A;
  --shadow-xs:0 1px 2px rgba(30,20,8,.04);--shadow-sm:0 2px 8px rgba(30,20,8,.06);
  --shadow-md:0 4px 20px rgba(30,20,8,.08);--shadow-lg:0 8px 32px rgba(30,20,8,.12);
  --r:16px;--r-lg:24px;--r-xl:32px;
}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'DM Sans',sans-serif;background:var(--bg);color:var(--text);-webkit-font-smoothing:antialiased}
h1,h2,h3,h4{font-family:'Fredoka',sans-serif;font-weight:600}
button,input,textarea,select{font-family:inherit}
@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes pop{0%{transform:scale(1)}50%{transform:scale(1.25)}100%{transform:scale(1)}}
@keyframes checkDraw{from{stroke-dashoffset:24}to{stroke-dashoffset:0}}
@keyframes confetti{0%{opacity:1;transform:translateY(0) rotate(0) scale(1)}100%{opacity:0;transform:translateY(-90px) rotate(540deg) scale(0)}}
@keyframes celebrate{0%{transform:scale(1)}25%{transform:scale(1.03)}75%{transform:scale(.98)}100%{transform:scale(1)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
@keyframes nowGlow{0%,100%{box-shadow:0 0 0 0 var(--gold)}50%{box-shadow:0 0 0 6px rgba(245,183,49,.15)}}
@keyframes slideUp{from{opacity:0;transform:translateY(100%)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes shakeNo{0%,100%{transform:translateX(0)}25%{transform:translateX(-4px)}75%{transform:translateX(4px)}}
@keyframes taskDone{0%{background:transparent}30%{background:var(--green-bg)}100%{background:transparent}}
@keyframes turnipFloat{0%{opacity:1;transform:translateY(0) scale(1)}100%{opacity:0;transform:translateY(-40px) scale(1.3)}}
@keyframes ringDraw{from{stroke-dashoffset:var(--ring-circ)}to{stroke-dashoffset:var(--ring-offset)}}
@keyframes gradientShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
@keyframes legendaryPulse{0%,100%{transform:scale(1);opacity:0.7}50%{transform:scale(1.15);opacity:1}}
@keyframes petBreathe{0%,100%{transform:translateY(0)}50%{transform:translateY(-1px)}}
@keyframes petHop{0%,100%{transform:translateY(0)}30%{transform:translateY(-3px)}50%{transform:translateY(0)}70%{transform:translateY(-1px)}}
@keyframes petWaddle{0%,100%{transform:rotate(0deg)}25%{transform:rotate(-3deg)}75%{transform:rotate(3deg)}}
@keyframes eggWobble{0%,100%{transform:rotate(0deg)}20%{transform:rotate(0deg)}25%{transform:rotate(-4deg)}30%{transform:rotate(4deg)}35%{transform:rotate(-2deg)}40%{transform:rotate(0deg)}}
.fu{animation:fadeUp .35s ease-out both}
.s1{animation:fadeUp .3s ease-out .03s both}.s2{animation:fadeUp .3s ease-out .06s both}
.s3{animation:fadeUp .3s ease-out .09s both}.s4{animation:fadeUp .3s ease-out .12s both}
.s5{animation:fadeUp .3s ease-out .15s both}
::-webkit-scrollbar{width:5px;height:5px}
::-webkit-scrollbar-thumb{background:var(--bg-muted);border-radius:3px}
`;

/* ═══════════════════════════════════════════════════════════════════════
   DATA — constants only (no more seed data, it's all in Supabase)
   ═══════════════════════════════════════════════════════════════════════ */

const REC=[
  {value:"daily",label:"Daily",icon:"sun",color:"var(--gold)"},
  {value:"every_other_day",label:"Every other day",icon:"refresh",color:"var(--peach)"},
  {value:"weekly",label:"Weekly",icon:"calendar",color:"var(--green)"},
  {value:"biweekly",label:"Bi-weekly",icon:"refresh",color:"var(--lilac)"},
  {value:"monthly",label:"Monthly",icon:"moon",color:"var(--sky)"},
];

// Categories are now loaded from Supabase — see useCategories hook
// Fallback for when categories haven't loaded yet
const DEFAULT_CATS=[
  {value:"other",label:"Other",icon:"box",color:"var(--text-3)",bg:"var(--bg-muted)"},
];

// Available colour schemes for the category picker
const COLOR_OPTIONS=[
  {color:"var(--gold)",bg:"var(--gold-bg)",label:"Gold"},
  {color:"var(--green)",bg:"var(--green-bg)",label:"Green"},
  {color:"var(--coral)",bg:"var(--coral-bg)",label:"Coral"},
  {color:"var(--sky)",bg:"var(--sky-bg)",label:"Sky"},
  {color:"var(--lilac)",bg:"var(--lilac-bg)",label:"Lilac"},
  {color:"var(--peach)",bg:"var(--peach-bg)",label:"Peach"},
  {color:"var(--mint)",bg:"var(--mint-bg)",label:"Mint"},
  {color:"var(--text-3)",bg:"var(--bg-muted)",label:"Grey"},
];

const DAYS=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHLY_OPTS=[
  {value:"first",label:"1st of the month"},
  {value:"mid",label:"Mid month (15th)"},
  {value:"last",label:"Last day of month"},
];

/* ═══════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════ */
const tkey=()=>new Date().toISOString().slice(0,10);
const grc=v=>REC.find(r=>r.value===v);
const nowMins=()=>{const n=new Date();return n.getHours()*60+n.getMinutes();};
const t2m=t=>{if(!t)return null;const[h,m]=t.split(":").map(Number);return h*60+m;};
const ft=t=>{if(!t)return"";const[h,m]=t.split(":");const hr=parseInt(h);return`${hr>12?hr-12:hr||12}:${m}${hr>=12?"pm":"am"}`;};

// Day number since epoch — used for alternating schedules
const daysSinceEpoch=(d)=>Math.floor((d||new Date())/86400000);

// Determine whose turn it is for a shared task today
function whoseTurn(task,dateOverride){
  if(!task.sharedWith) return task.assignee;
  const d=dateOverride||new Date();

  // Build the rotation list: 2 or 3 people
  const rotation=[task.assignee,task.sharedWith];
  if(task.sharedWith2) rotation.push(task.sharedWith2);
  const count=rotation.length;

  const rec=task.recurrence;
  if(rec==="daily"||rec==="every_other_day"){
    return rotation[daysSinceEpoch(d)%count];
  }
  if(rec==="weekly"||rec==="biweekly"){
    const jan1=new Date(d.getFullYear(),0,1);
    const weekNum=Math.ceil(((d-jan1)/86400000+jan1.getDay()+1)/7);
    return rotation[weekNum%count];
  }
  if(rec==="monthly"){
    return rotation[d.getMonth()%count];
  }
  return task.assignee;
}

function todayAll(ts){
  const now=new Date();
  const dow=now.getDay();
  const dom=now.getDate();
  const today=now.toISOString().slice(0,10);
  const lastDay=new Date(now.getFullYear(),now.getMonth()+1,0).getDate();
  const jan1=new Date(now.getFullYear(),0,1);
  const weekNum=Math.ceil(((now-jan1)/86400000+jan1.getDay()+1)/7);
  const dayNum=daysSinceEpoch(now);

  return ts.filter(t=>{
    if(t.scheduleType==="one_off") return t.oneOffDate===today;
    const rec=t.recurrence;
    if(rec==="daily") return true;
    if(rec==="every_other_day") return dayNum%2===(t.scheduleOffset||0);
    if(rec==="weekly") return t.scheduleDay!=null ? t.scheduleDay===dow : true;
    if(rec==="biweekly") return (t.scheduleDay!=null ? t.scheduleDay===dow : dow===6) && weekNum%2===0;
    if(rec==="monthly"){
      if(t.scheduleMonthly==="first") return dom===1;
      if(t.scheduleMonthly==="mid") return dom===15;
      if(t.scheduleMonthly==="last") return dom===lastDay;
      return dom===1;
    }
    return true;
  });
}
function sortTime(tasks){
  const a=tasks.filter(t=>t.startTime).sort((a,b)=>t2m(a.startTime)-t2m(b.startTime));
  return[...a,...tasks.filter(t=>!t.startTime)];
}

/* ═══════════════════════════════════════════════════════════════════════
   SMALL COMPONENTS
   ═══════════════════════════════════════════════════════════════════════ */
function Confetti({show}){
  if(!show)return null;
  const c=["var(--gold)","var(--green)","var(--coral)","var(--sky)","var(--lilac)","var(--peach)","var(--mint)"];
  return(<div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:9999}}>
    {Array.from({length:24},(_,i)=>(<div key={i} style={{
      position:"absolute",top:"40%",left:`${8+Math.random()*84}%`,
      width:8+Math.random()*6,height:8+Math.random()*6,
      borderRadius:Math.random()>.5?"50%":"2px",background:c[i%c.length],
      animation:`confetti ${.7+Math.random()*.5}s ease-out ${Math.random()*.3}s both`,
    }}/>))}
  </div>);
}

function Tick({done,color="var(--green)",onToggle,sz=24}){
  const[p,setP]=useState(false);
  return(<button onClick={e=>{e.stopPropagation();if(!done){setP(true);setTimeout(()=>setP(false),350);}onToggle();}} style={{
    width:sz,height:sz,minWidth:sz,borderRadius:7,
    border:done?"none":`2.5px solid var(--bg-muted)`,background:done?color:"transparent",
    display:"flex",alignItems:"center",justifyContent:"center",
    cursor:"pointer",transition:"all .2s",animation:p?"pop .3s ease":"none",
  }}>{done&&<svg width={sz*.55} height={sz*.55} viewBox="0 0 16 16" fill="none">
    <path d="M3 8.5L6.5 12L13 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{strokeDasharray:24,animation:"checkDraw .3s ease-out forwards"}}/>
  </svg>}</button>);
}

function RecBadge({value,task}){
  const r=grc(value);
  if(task?.scheduleType==="one_off"){
    const d=task.oneOffDate;
    const label=d?new Date(d+"T00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"}):"One-off";
    return(<span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 8px",borderRadius:10,
      fontSize:10,fontWeight:700,background:"var(--peach-bg)",color:"var(--peach)",letterSpacing:.3}}>
      <Icon name="calendar" size={10} color="var(--peach)" sw={2.2}/>{label}</span>);
  }
  let extra="";
  if(task?.scheduleDay!=null&&(value==="weekly"||value==="biweekly")) extra=` (${DAYS[task.scheduleDay]})`;
  if(task?.scheduleMonthly&&value==="monthly"){
    const m={first:"1st",mid:"15th",last:"Last"};
    extra=` (${m[task.scheduleMonthly]||""})`;
  }
  return(
  <span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 8px",borderRadius:10,
    fontSize:10,fontWeight:700,background:r?.color+"18",color:r?.color,letterSpacing:.3}}>
    <Icon name={r?.icon} size={10} color={r?.color} sw={2.2}/>{r?.label}{extra}</span>);}

function PersonChip({id}){const{gp}=useAppCtx();const p=gp(id);if(!p)return null;return(
  <span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 8px",borderRadius:10,
    fontSize:10,fontWeight:700,background:p.bg,border:`1.5px solid ${p.color}30`,color:p.color}}>
    <Icon name={p.icon} size={10} color={p.color} sw={2.2}/>{p.name}</span>);}

function TimeBadge({startTime,doneBy}){
  if(!startTime&&!doneBy)return null;
  return(<span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 8px",borderRadius:10,
    fontSize:10,fontWeight:700,background:"var(--peach-bg)",color:"var(--peach)",letterSpacing:.2}}>
    <Icon name="clock" size={10} color="var(--peach)" sw={2.2}/>{ft(startTime)}{doneBy?` - ${ft(doneBy)}`:""}</span>);}

function BitsCount({bits}){
  if(!bits||!bits.length)return null;
  return(<span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 8px",borderRadius:10,
    fontSize:10,fontWeight:700,background:"var(--mint-bg)",color:"var(--mint)"}}>
    <Icon name="list" size={10} color="var(--mint)" sw={2.2}/>{bits.length} bits</span>);}

function Ring({done,total,sz=60,sw=5,color="var(--gold)"}){
  const pct=total===0?0:Math.round(done/total*100);
  const r=(sz-sw*2)/2;const c=2*Math.PI*r;const off=c-(pct/100)*c;
  return(<div style={{position:"relative",width:sz,height:sz}}>
    <svg width={sz} height={sz} style={{transform:"rotate(-90deg)"}}>
      <circle cx={sz/2} cy={sz/2} r={r} fill="none" stroke="var(--bg-muted)" strokeWidth={sw}/>
      <circle cx={sz/2} cy={sz/2} r={r} fill="none" stroke={color} strokeWidth={sw}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        style={{"--ring-circ":c,"--ring-offset":off,animation:"ringDraw .8s ease-out forwards"}}/>
    </svg>
    <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",
      fontFamily:"'Fredoka'",fontWeight:600,fontSize:sz*.22,color:"var(--text)"}}>{pct}%</div>
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   TASK DETAIL CARD
   ═══════════════════════════════════════════════════════════════════════ */
function TaskDetailCard({task,onClose,onToggle,bitsChecked,onToggleBit,fromCheckbox,
  completionNote,setCompletionNote,onRequestSwap,onSkipTurn,swap,getNote,people,gp,personId,shoppingItems,runningLowItems}){
  const{gc}=useAppCtx();
  const cat=gc(task.category);
  const done=task.completions_today?.some(c=>c.person_id===personId);
  const bits=(task.bits||[]).map(b=>typeof b==="string"?b:b.label);
  const hasBits=bits.length>0;
  const checkedCount=bitsChecked.filter(Boolean).length;
  const allBitsOk=!hasBits||bits.every((_,i)=>bitsChecked[i]);
  const [justCompleted,setJustCompleted]=useState(false);
  const [showSwapInput,setShowSwapInput]=useState(false);
  const [swapReason,setSwapReason]=useState("");
  const existingNote=getNote?getNote(task.id):"";

  const handleBitToggle=(i)=>{
    if(done)return;
    const next=[...bitsChecked];next[i]=!next[i];
    onToggleBit(i);
    if(next.every(Boolean)&&bits.length>0){
      setTimeout(()=>{setJustCompleted(true);onToggle(task.id);setTimeout(()=>onClose(),900);},350);
    }
  };
  const handleComplete=()=>{setJustCompleted(true);onToggle(task.id);setTimeout(()=>onClose(),600);};

  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(30,20,8,.4)",
      backdropFilter:"blur(4px)",display:"flex",alignItems:"flex-end",justifyContent:"center",
      padding:"0 8px 0",animation:"fadeIn .2s ease-out"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:480,maxHeight:"88vh",overflow:"auto",
        background:"var(--bg-card)",borderRadius:"24px 24px 0 0",boxShadow:"var(--shadow-lg)",
        animation:"slideUp .3s ease-out",paddingBottom:"max(20px, env(safe-area-inset-bottom))"}}>
        {/* Header */}
        <div style={{background:justCompleted?"var(--green-bg)":cat?.bg,padding:"20px 24px 16px",
          borderRadius:"24px 24px 0 0",position:"relative",transition:"background .4s ease"}}>
          <div style={{width:40,height:4,borderRadius:2,background:cat?.color+"40",margin:"0 auto 14px"}}/>
          <button onClick={onClose} style={{position:"absolute",top:16,right:16,width:32,height:32,borderRadius:10,
            background:"var(--bg-card)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",
            justifyContent:"center",boxShadow:"var(--shadow-xs)"}}>
            <Icon name="close" size={16} color="var(--text-2)"/></button>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <div style={{width:40,height:40,borderRadius:12,background:"var(--bg-card)",
              display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"var(--shadow-xs)"}}>
              <Icon name={justCompleted?"check":cat?.icon} size={22} color={justCompleted?"var(--green)":cat?.color}/>
            </div>
            <div style={{flex:1}}>
              <h2 style={{fontSize:20,lineHeight:1.2,color:"var(--text)"}}>{task.title}</h2>
              {fromCheckbox&&hasBits&&!done&&!justCompleted&&(
                <p style={{fontSize:12,color:"var(--text-3)",marginTop:3}}>Confirm each bit is done</p>)}
            </div>
          </div>
          {!fromCheckbox&&(<div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
            <RecBadge value={task.recurrence} task={task}/><TimeBadge startTime={task.startTime} doneBy={task.doneBy}/>
            <PersonChip id={whoseTurn(task)}/>
            {task.sharedWith&&(()=>{
              const ids=[task.assignee,task.sharedWith,task.sharedWith2].filter(Boolean);
              return(<div style={{display:"flex",marginLeft:2}}>
                {ids.map((pid,i)=>{const p=gp(pid);return p?<div key={pid} style={{
                  width:22,height:22,borderRadius:7,background:p.bg,border:"2px solid var(--bg-card)",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  marginLeft:i>0?-6:0,zIndex:ids.length-i}}>
                  <Icon name={p.icon} size={10} color={p.color}/></div>:null;})}</div>);})()}
            <div style={{display:"flex",alignItems:"center",gap:3,padding:"2px 8px",borderRadius:6,
              background:"#e8f5e9",marginLeft:"auto"}}>
              <TurnipIcon size={11} color="#558b2f"/>
              <span style={{fontSize:10,fontWeight:700,color:"#558b2f"}}>{task.turnip_value||5}</span>
            </div>
          </div>)}
        </div>
        {/* Body */}
        <div style={{padding:"20px 24px"}}>
          {justCompleted&&(<div className="fu" style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"12px 24px",borderRadius:14,
              background:"var(--green-bg)",color:"var(--green)",fontSize:17,fontWeight:700,fontFamily:"'Fredoka'"}}>
              <Icon name="check" size={20} color="var(--green)" sw={2.5}/>Done!</div></div>)}
          {!fromCheckbox&&task.details&&!justCompleted&&(
            <p style={{fontSize:14,color:"var(--text-2)",lineHeight:1.5,marginBottom:20}}>{task.details}</p>)}

          {/* Shopping list for shopping tasks */}
          {task.category==="shopping"&&shoppingItems&&shoppingItems.length>0&&!justCompleted&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--peach)",marginBottom:8,
                display:"flex",alignItems:"center",gap:5}}>
                <Icon name="shop" size={14} color="var(--peach)"/>Shopping List ({shoppingItems.filter(i=>!i.in_pantry).length} items)
              </div>
              <div style={{maxHeight:200,overflow:"auto",borderRadius:10,border:"1px solid var(--bg-muted)",background:"var(--bg)"}}>
                {shoppingItems.filter(i=>!i.in_pantry).map(item=>(
                  <div key={item.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",
                    borderBottom:"1px solid var(--bg-muted)",fontSize:13,
                    textDecoration:item.checked?"line-through":"none",
                    color:item.checked?"var(--text-3)":"var(--text)",opacity:item.checked?.5:1}}>
                    <span style={{flex:1}}>{item.name}</span>
                    {item.quantity&&<span style={{fontSize:11,color:"var(--text-3)"}}>{item.quantity}</span>}
                    <span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"var(--bg-muted)",
                      color:"var(--text-3)"}}>{item.category}</span>
                  </div>))}
              </div>
            </div>
          )}

          {/* Running low items in shopping tasks */}
          {task.category==="shopping"&&runningLowItems&&runningLowItems.filter(i=>!i.checked).length>0&&!justCompleted&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--coral)",marginBottom:8,
                display:"flex",alignItems:"center",gap:5}}>
                <Icon name="shop" size={14} color="var(--coral)"/>Running Low ({runningLowItems.filter(i=>!i.checked).length})
              </div>
              <div style={{maxHeight:150,overflow:"auto",borderRadius:10,border:"1px solid var(--coral-light)",background:"var(--coral-bg)"}}>
                {runningLowItems.filter(i=>!i.checked).map(item=>(
                  <div key={item.id} style={{padding:"7px 10px",borderBottom:"1px solid var(--coral-light)",
                    fontSize:13,color:"var(--text)",display:"flex",alignItems:"center",gap:6}}>
                    <Icon name="check" size={10} color="var(--coral)" sw={1.5}/>
                    <span style={{flex:1}}>{item.name}</span>
                  </div>))}
              </div>
            </div>
          )}

          {hasBits&&!justCompleted&&(<div style={{marginBottom:20}}>
            {fromCheckbox&&(<div style={{display:"flex",alignItems:"center",gap:6,marginBottom:14,
              padding:"10px 14px",borderRadius:12,transition:"background .3s",
              background:checkedCount===0?"var(--peach-bg)":checkedCount<bits.length?"var(--gold-bg)":"var(--green-bg)"}}>
              <Icon name={checkedCount<bits.length?"shield":"check"} size={15}
                color={checkedCount===0?"var(--peach)":checkedCount<bits.length?"var(--gold-deep)":"var(--green)"}/>
              <span style={{fontSize:13,fontWeight:600,
                color:checkedCount===0?"var(--peach)":checkedCount<bits.length?"var(--gold-deep)":"var(--green)"}}>
                {checkedCount===0?"Check each bit to confirm":checkedCount<bits.length?`${checkedCount} of ${bits.length} confirmed`:"All confirmed!"}</span>
            </div>)}
            {!fromCheckbox&&(<div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12}}>
              <Icon name="list" size={16} color="var(--mint)"/><h3 style={{fontSize:15,color:"var(--text-2)"}}>Task Bits</h3>
              <span style={{fontSize:11,color:"var(--text-3)",fontWeight:600,marginLeft:"auto"}}>{checkedCount}/{bits.length}</span></div>)}
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {bits.map((bit,i)=>{const checked=bitsChecked[i];return(
                <button key={i} onClick={()=>handleBitToggle(i)} className={`s${(i%5)+1}`} style={{
                  display:"flex",alignItems:"center",gap:12,padding:"13px 14px",borderRadius:12,
                  background:checked?"var(--green-bg)":"var(--bg)",
                  border:checked?`1.5px solid var(--green-light)`:`1.5px solid var(--bg-muted)`,
                  cursor:done?"default":"pointer",transition:"all .2s",textAlign:"left",opacity:done?.6:1}}>
                  <div style={{width:24,height:24,minWidth:24,borderRadius:7,
                    border:checked?"none":"2.5px solid var(--bg-muted)",background:checked?"var(--green)":"transparent",
                    display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s"}}>
                    {checked&&<svg width={13} height={13} viewBox="0 0 16 16" fill="none">
                      <path d="M3 8.5L6.5 12L13 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{strokeDasharray:24,animation:"checkDraw .3s ease-out forwards"}}/></svg>}
                  </div>
                  <span style={{fontSize:14,fontWeight:checked?600:500,color:checked?"var(--green)":"var(--text)",
                    textDecoration:checked?"line-through":"none",transition:"all .2s",flex:1}}>{bit}</span>
                  {fromCheckbox&&!checked&&<Icon name="chevron_right" size={14} color="var(--bg-muted)"/>}
                </button>);})}
            </div>
          </div>)}

          {/* Note input — shown when completing or after completion */}
          {(justCompleted||done)&&!fromCheckbox&&(
            <NoteInput note={completionNote} setNote={setCompletionNote}/>
          )}

          {/* Existing note display */}
          {existingNote&&!justCompleted&&(
            <div style={{marginTop:12,padding:"10px 14px",borderRadius:10,background:"var(--mint-bg)",
              border:"1.5px solid var(--mint-light)"}}>
              <div style={{fontSize:11,fontWeight:600,color:"var(--mint)",marginBottom:3,
                display:"flex",alignItems:"center",gap:4}}>
                <Icon name="chat" size={11} color="var(--mint)"/>Note</div>
              <div style={{fontSize:13,color:"var(--text-2)"}}>{existingNote}</div>
            </div>
          )}

          {/* Complete buttons */}
          {!justCompleted&&!done&&!hasBits&&!swap&&(
            <button onClick={handleComplete} style={{width:"100%",padding:"15px",borderRadius:14,border:"none",
              background:"var(--green)",color:"white",fontSize:16,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:8}}>
              <Icon name="check" size={18} color="white" sw={2.5}/>Mark as complete</button>)}
          {!justCompleted&&!done&&hasBits&&!fromCheckbox&&!swap&&(
            <button onClick={()=>{if(!allBitsOk)return;handleComplete();}} style={{
              width:"100%",padding:"15px",borderRadius:14,border:"none",marginTop:8,
              background:allBitsOk?"var(--green)":"var(--bg-muted)",color:allBitsOk?"white":"var(--text-3)",
              fontSize:16,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",transition:"all .3s",opacity:allBitsOk?1:.6,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              <Icon name="check" size={18} color={allBitsOk?"white":"var(--text-3)"} sw={2.5}/>
              {allBitsOk?"Mark as complete":"Complete all bits first"}</button>)}

          {/* Can't do it today — auto-skip for shared tasks, swap request for solo */}
          {!done&&!justCompleted&&!swap&&!fromCheckbox&&(
            <div style={{marginTop:10}}>
              {task.sharedWith?(
                /* Shared task — show who it passes to and skip immediately */
                <div>
                  {!showSwapInput?(
                    <button onClick={()=>setShowSwapInput(true)} style={{width:"100%",padding:"12px",borderRadius:12,
                      border:"1.5px solid var(--peach-bg)",background:"transparent",cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                      fontSize:13,fontWeight:600,color:"var(--peach)",transition:"all .2s"}}
                      onMouseEnter={e=>{e.currentTarget.style.background="var(--peach-bg)";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
                      <Icon name="swap" size={15} color="var(--peach)"/>Can't do this today
                    </button>
                  ):(
                    <div style={{padding:"14px",borderRadius:12,background:"var(--peach-bg)",
                      border:"1.5px solid var(--peach-light)"}}>
                      {(()=>{
                        const rotation=[task.assignee,task.sharedWith];
                        if(task.sharedWith2) rotation.push(task.sharedWith2);
                        const currentIdx=rotation.indexOf(whoseTurn(task));
                        const nextPerson=rotation[(currentIdx+1)%rotation.length];
                        const nextName=gp(nextPerson)?.name||"next person";
                        return(<>
                          <div style={{fontSize:13,color:"var(--text-2)",marginBottom:10,display:"flex",alignItems:"center",gap:5}}>
                            <Icon name="swap" size={14} color="var(--peach)"/>
                            This will pass to <strong style={{color:"var(--text)"}}>{nextName}</strong>
                          </div>
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>{
                              onSkipTurn(task.id,nextPerson);
                              setShowSwapInput(false);onClose();
                            }} style={{flex:1,padding:"10px",borderRadius:10,border:"none",background:"var(--peach)",
                              color:"white",fontSize:13,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer"}}>
                              Pass to {nextName}</button>
                            <button onClick={()=>setShowSwapInput(false)}
                              style={{padding:"10px 16px",borderRadius:10,border:"none",background:"var(--bg-muted)",
                                fontSize:13,fontWeight:600,cursor:"pointer",color:"var(--text-3)"}}>Cancel</button>
                          </div>
                        </>);
                      })()}
                    </div>
                  )}
                </div>
              ):(
                /* Solo task — swap request to admin */
                <div>
                  {!showSwapInput?(
                    <button onClick={()=>setShowSwapInput(true)} style={{width:"100%",padding:"12px",borderRadius:12,
                      border:"1.5px solid var(--peach-bg)",background:"transparent",cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                      fontSize:13,fontWeight:600,color:"var(--peach)",transition:"all .2s"}}
                      onMouseEnter={e=>{e.currentTarget.style.background="var(--peach-bg)";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
                      <Icon name="swap" size={15} color="var(--peach)"/>Can't do this today
                    </button>
                  ):(
                    <div style={{padding:"14px",borderRadius:12,background:"var(--peach-bg)",
                      border:"1.5px solid var(--peach-light)"}}>
                      <div style={{fontSize:12,fontWeight:600,color:"var(--peach)",marginBottom:8,
                        display:"flex",alignItems:"center",gap:5}}>
                        <Icon name="swap" size={13} color="var(--peach)"/>Request a swap
                      </div>
                      <input placeholder="Reason (optional)" value={swapReason}
                        onChange={e=>setSwapReason(e.target.value)}
                        style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1.5px solid var(--peach-light)",
                          fontSize:13,background:"var(--bg-card)",outline:"none",fontFamily:"inherit",marginBottom:8}}/>
                      <div style={{display:"flex",gap:6}}>
                        <button onClick={()=>{
                          onRequestSwap(task.id,task.assignee,swapReason);
                          setShowSwapInput(false);setSwapReason("");onClose();
                        }} style={{flex:1,padding:"10px",borderRadius:10,border:"none",background:"var(--peach)",
                          color:"white",fontSize:13,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer"}}>
                          Send request</button>
                        <button onClick={()=>{setShowSwapInput(false);setSwapReason("");}}
                          style={{padding:"10px 16px",borderRadius:10,border:"none",background:"var(--bg-muted)",
                            fontSize:13,fontWeight:600,cursor:"pointer",color:"var(--text-3)"}}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Swap pending indicator */}
          {swap&&!done&&!justCompleted&&(
            <div style={{marginTop:10,padding:"12px 16px",borderRadius:12,background:"var(--peach-bg)",
              border:"1.5px solid var(--peach-light)",textAlign:"center"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                fontSize:14,fontWeight:600,color:"var(--peach)"}}>
                <Icon name="swap" size={15} color="var(--peach)"/>Swap requested
              </div>
              <div style={{fontSize:12,color:"var(--text-3)",marginTop:4}}>
                Waiting for admin to reassign
              </div>
            </div>
          )}

          {done&&!justCompleted&&(<div style={{textAlign:"center",padding:"12px 0"}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"10px 20px",borderRadius:12,
              background:"var(--green-bg)",color:"var(--green)",fontSize:15,fontWeight:700,fontFamily:"'Fredoka'"}}>
              <Icon name="check" size={18} color="var(--green)" sw={2.5}/>Completed</div>
            <button onClick={()=>onToggle(task.id)} style={{display:"block",margin:"12px auto 0",background:"none",
              border:"none",cursor:"pointer",fontSize:12,color:"var(--text-3)",fontWeight:600,textDecoration:"underline"}}>Undo</button>
          </div>)}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   TIMELINE ROW
   ═══════════════════════════════════════════════════════════════════════ */
function TimelineRow({task,onToggle,onEdit,showPerson,isLast,onTap}){
  const{gc}=useAppCtx();
  const done=task.completions_today?.length>0;const cat=gc(task.category);
  const hasTm=!!task.startTime;const nm=nowMins();const tm=t2m(task.startTime);
  const isCur=hasTm&&tm<=nm&&(!task.doneBy||t2m(task.doneBy)>=nm);
  const bits=task.bits||[];const hasBits=bits.length>0;
  const[justDone,setJustDone]=useState(false);

  const handleCheck=()=>{
    if(!done){
      setJustDone(true);
      setTimeout(()=>setJustDone(false),1200);
    }
    if(done){onToggle(task.id);return;}
    if(hasBits){onTap(task,true);return;}
    onToggle(task.id);
  };

  return(
    <div style={{display:"flex",gap:0,position:"relative",
      animation:justDone?"taskDone .6s ease":"none"}}>
      {justDone&&<div style={{position:"absolute",right:16,top:6,zIndex:10,
        animation:"turnipFloat 1s ease-out forwards",pointerEvents:"none",
        display:"flex",alignItems:"center",gap:3}}>
        <span style={{fontSize:13,fontWeight:800,color:"#558b2f"}}>+{task.turnip_value||5}</span>
        <TurnipIcon size={14} color="#558b2f"/></div>}
      <div style={{width:48,flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center"}}>
        <div style={{fontSize:10,fontWeight:700,fontFamily:"'Fredoka'",
          color:hasTm?(done?"var(--text-3)":isCur?"var(--gold-deep)":"var(--text-2)"):"var(--text-3)",
          marginBottom:4,height:14,display:"flex",alignItems:"center",letterSpacing:.3}}>
          {hasTm?ft(task.startTime):""}</div>
        <div style={{width:done?10:isCur?14:10,height:done?10:isCur?14:10,borderRadius:"50%",flexShrink:0,zIndex:2,
          background:done?"var(--green)":isCur?"var(--gold)":hasTm?"var(--bg-muted)":"transparent",
          border:done?"none":isCur?"3px solid var(--gold-light)":hasTm?"2.5px solid var(--bg-muted)":`2px dashed var(--bg-muted)`,
          animation:isCur?"nowGlow 2s ease-in-out infinite":"none",transition:"all .3s"}}/>
        {!isLast&&<div style={{flex:1,width:0,minHeight:12,borderLeft:`2px dotted ${done?"var(--green-light)":"var(--bg-muted)"}`}}/>}
      </div>
      <div style={{flex:1,marginBottom:isLast?0:8,marginLeft:4,padding:"12px 14px",
        background:done?"var(--bg-muted)":isCur?"var(--gold-bg)":"var(--bg-card)",borderRadius:"var(--r)",
        borderLeft:`4px solid ${done?"var(--green)":isCur?"var(--gold)":cat?.color||"var(--bg-muted)"}`,
        boxShadow:done?"none":isCur?"0 2px 12px rgba(245,183,49,.12)":"var(--shadow-xs)",
        opacity:done?.5:1,transition:"all .25s"}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
          <div style={{paddingTop:1}}><Tick done={done} color="var(--green)" onToggle={handleCheck}/></div>
          <div onClick={()=>onTap(task)} style={{flex:1,minWidth:0,cursor:"pointer"}}>
            <div style={{fontWeight:600,fontSize:14,textDecoration:done?"line-through":"none",
              color:done?"var(--text-3)":"var(--text)",marginBottom:3,lineHeight:1.3,display:"flex",alignItems:"center",gap:6}}>
              <Icon name={cat?.icon} size={14} color={done?"var(--text-3)":cat?.color} sw={2}/>{task.title}</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
              <RecBadge value={task.recurrence} task={task}/><TimeBadge startTime={task.startTime} doneBy={task.doneBy}/>
              <BitsCount bits={bits}/>{showPerson&&<PersonChip id={whoseTurn(task)}/>}
              {task.sharedWith&&<span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:6,
                background:"var(--lilac-bg)",color:"var(--lilac)"}}>SHARED</span>}</div>
          </div>
          <div onClick={()=>onTap(task)} style={{cursor:"pointer",padding:"4px 0"}}>
            <Icon name="chevron_right" size={16} color="var(--text-3)" style={{opacity:.4}}/></div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   TIMELINE MARKERS & LIST
   ═══════════════════════════════════════════════════════════════════════ */
function NowMarker(){
  const n=new Date();const h=n.getHours(),m=n.getMinutes();
  const l=`${h>12?h-12:h||12}:${m.toString().padStart(2,"0")}${h>=12?"pm":"am"}`;
  return(<div style={{display:"flex",gap:0,alignItems:"center",margin:"6px 0"}}>
    <div style={{width:48,display:"flex",justifyContent:"center"}}>
      <div style={{fontSize:9,fontWeight:800,fontFamily:"'Fredoka'",color:"var(--gold-deep)",
        background:"var(--gold-light)",padding:"2px 6px",borderRadius:6,letterSpacing:.4}}>{l}</div></div>
    <div style={{flex:1,marginLeft:4,height:2,background:"linear-gradient(90deg, var(--gold) 0%, transparent 100%)",
      borderRadius:1,animation:"pulse 2s ease-in-out infinite"}}/></div>);}

function AnytimeDivider(){return(
  <div style={{display:"flex",gap:0,alignItems:"center",margin:"8px 0 4px"}}>
    <div style={{width:48,display:"flex",justifyContent:"center"}}>
      <div style={{fontSize:9,fontWeight:700,fontFamily:"'Fredoka'",color:"var(--text-3)",
        padding:"2px 6px",borderRadius:6,background:"var(--bg-muted)",letterSpacing:.4}}>Anytime</div></div>
    <div style={{flex:1,marginLeft:4,height:1,borderTop:"1.5px dashed var(--bg-muted)"}}/></div>);}

function TimelineList({tasks,onToggle,onEdit,showPerson,onTapTask}){
  const sorted=sortTime(tasks);const timed=sorted.filter(t=>t.startTime);const untimed=sorted.filter(t=>!t.startTime);
  const nm=nowMins();let nowIdx=timed.length;
  for(let i=0;i<timed.length;i++){if(t2m(timed[i].startTime)>nm){nowIdx=i;break;}}
  const items=[];
  timed.forEach((t,i)=>{if(i===nowIdx)items.push({type:"now"});items.push({type:"task",task:t});});
  if(nowIdx>=timed.length)items.push({type:"now"});
  if(untimed.length>0)items.push({type:"anytime"});
  untimed.forEach(t=>items.push({type:"task",task:t}));
  return(<div style={{display:"flex",flexDirection:"column"}}>
    {items.map((it,i)=>{
      if(it.type==="now")return<NowMarker key="now"/>;
      if(it.type==="anytime")return<AnytimeDivider key="anytime"/>;
      const last=i===items.length-1||(i<items.length-1&&items[i+1].type!=="task");
      return<TimelineRow key={it.task.id} task={it.task} onToggle={onToggle} onEdit={onEdit}
        showPerson={showPerson} isLast={last} onTap={onTapTask}/>;})}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   HERO / CATBLOCKS
   ═══════════════════════════════════════════════════════════════════════ */
function Hero({tasks,personId,people,gp,onEditAvatar,pet,petMood}){
  const mine=todayAll(tasks).filter(t=>whoseTurn(t)===personId);
  const d=mine.filter(t=>t.completions_today?.length>0).length,tot=mine.length;
  const pct=tot===0?0:Math.round(d/tot*100);const ad=tot>0&&d===tot;
  let msg,sub;
  if(ad){msg="You're all done!";sub="Time to put your feet up";}
  else if(pct>=75){msg="Nearly there!";sub=`Just ${tot-d} left`;}
  else if(pct>=40){msg="Good progress";sub="Keep going";}
  else if(d>0){msg="Getting started";sub=`${tot-d} tasks waiting`;}
  else{msg=`${tot} tasks today`;sub="Let's get into it";}
  const h=new Date().getHours();const greet=h<12?"Good morning":h<18?"Good afternoon":"Good evening";
  const accent=ad?"var(--green)":"var(--gold-deep)";
  const person=gp(personId);
  return(<div className="fu" style={{
    background:ad?"linear-gradient(135deg,var(--green-bg),var(--mint-bg),var(--green-light))"
      :"linear-gradient(135deg,var(--gold-bg),var(--peach-bg),var(--gold-light))",
    borderRadius:"var(--r-xl)",padding:"20px 20px",position:"relative",overflow:"hidden",
    boxShadow:"var(--shadow-md)",animation:ad?"celebrate .6s ease":"none"}}>
    <div style={{position:"absolute",top:-20,right:-15,width:100,height:100,borderRadius:"50%",
      background:ad?"var(--green-light)":"var(--gold-light)",opacity:.35}}/>
    <div style={{position:"absolute",bottom:-30,left:20,width:70,height:70,borderRadius:"var(--r)",
      background:ad?"var(--mint-light)":"var(--peach-light)",opacity:.3,transform:"rotate(15deg)"}}/>
    <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:14}}>
      {/* Pet — tappable */}
      <div onClick={onEditAvatar} style={{cursor:"pointer",flexShrink:0,
        background:"var(--bg-card)",borderRadius:14,padding:6,boxShadow:"var(--shadow-xs)",
        border:"2px solid "+accent+"30",display:"flex",alignItems:"center",justifyContent:"center"}}>
        {pet?<PetDisplay pet={pet} size={56} mood={petMood}/>:
          <div style={{width:56,height:56,display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:28}}>🥚</div>}
      </div>
      {/* Text — left aligned */}
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:500,color:accent,opacity:.8,marginBottom:2}}>
          {greet}, {person?.name||"there"}</div>
        <h1 style={{fontSize:22,color:accent,marginBottom:1,lineHeight:1.2}}>{msg}</h1>
        <p style={{fontSize:13,color:accent,opacity:.7}}>{sub}</p>
        {pet&&pet.name&&<div style={{fontSize:11,color:accent,opacity:.6,marginTop:3,
          display:"flex",alignItems:"center",gap:4}}>
          {SPECIES[pet.species]?.emoji} {pet.name} · {STAGE_NAMES[pet.stage]}
        </div>}
      </div>
      {/* Ring — right */}
      <Ring done={d} total={tot} sz={64} sw={5} color={accent}/>
    </div>
  </div>);
}

function CatBlocks({tasks,personId}){
  const{cats}=useAppCtx();
  const mine=todayAll(tasks).filter(t=>whoseTurn(t)===personId);
  const usedCats=cats.filter(c=>mine.some(t=>t.category===c.value));
  if(!usedCats.length)return null;
  return(<div style={{display:"flex",gap:6,marginTop:14,justifyContent:"center",overflow:"hidden"}}>
    {usedCats.map(cat=>{
      const ct=mine.filter(t=>t.category===cat.value);
      const cd=ct.filter(t=>t.completions_today?.length>0).length;const ad=ct.length>0&&cd===ct.length;
      return(<div key={cat.value} style={{
        background:ad?"var(--green-bg)":cat.bg,borderRadius:10,padding:"6px 10px",
        display:"flex",alignItems:"center",gap:4,
        border:ad?`1.5px solid var(--green-light)`:`1.5px solid ${cat.color}18`,transition:"all .3s"}}>
        <Icon name={cat.icon} size={14} color={ad?"var(--green)":cat.color}/>
        <span style={{fontSize:11,fontWeight:700,color:ad?"var(--green)":cat.color}}>{cd}/{ct.length}</span>
      </div>);})}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   TABS: ME / TODAY / ADMIN
   ═══════════════════════════════════════════════════════════════════════ */
function MeTab({tasks,onToggle,onTapTask,personId,people,gp,onEditAvatar,pet,petMood}){
  const mine=todayAll(tasks).filter(t=>whoseTurn(t)===personId);
  const pending=mine.filter(t=>!(t.completions_today?.length>0));const completed=mine.filter(t=>t.completions_today?.length>0);
  const ad=mine.length>0&&pending.length===0;
  const[conf,setConf]=useState(false);const prev=useRef(pending.length);
  useEffect(()=>{
    if(prev.current>0&&pending.length===0&&mine.length>0){setConf(true);setTimeout(()=>setConf(false),1500);}
    prev.current=pending.length;
  },[pending.length,mine.length]);
  return(<div className="fu">
    <Confetti show={conf}/><Hero tasks={tasks} personId={personId} people={people} gp={gp}
      onEditAvatar={onEditAvatar} pet={pet} petMood={petMood}/>
    {!ad&&<CatBlocks tasks={tasks} personId={personId}/>}
    {mine.length===0&&(
      <div style={{textAlign:"center",padding:"40px 24px",marginTop:20}}>
        <Icon name="SunLight" size={44} color="var(--gold)" style={{opacity:.6,marginBottom:8}}/>
        <h3 style={{fontSize:18,fontFamily:"'Fredoka'",color:"var(--text-2)",marginBottom:6}}>No tasks today</h3>
        <p style={{fontSize:13,color:"var(--text-3)",maxWidth:240,margin:"0 auto"}}>
          Enjoy your free day! Check back tomorrow.</p>
      </div>)}
    {pending.length>0&&(<div style={{marginTop:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14,padding:"0 4px"}}>
        <h3 style={{fontSize:16,color:"var(--text-2)",display:"flex",alignItems:"center",gap:6}}>
          <Icon name="clock" size={16} color="var(--text-2)"/>Your day</h3>
        <span style={{fontSize:12,color:"var(--text-3)",fontWeight:600}}>{pending.length} remaining</span></div>
      <TimelineList tasks={pending} onToggle={onToggle} showPerson={false} onTapTask={onTapTask}/></div>)}
    {completed.length>0&&(<div style={{marginTop:24}}>
      <h3 style={{fontSize:14,color:"var(--text-3)",marginBottom:10,padding:"0 4px",
        display:"flex",alignItems:"center",gap:5}}>
        <Icon name="check" size={14} color="var(--text-3)"/>Done ({completed.length})</h3>
      <TimelineList tasks={completed} onToggle={onToggle} showPerson={false} onTapTask={onTapTask}/></div>)}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   ME WRAPPER — sub-tabs: Tasks / Home
   ═══════════════════════════════════════════════════════════════════════ */
function MeWrapper({tasks,onToggle,onTapTask,personId,people,gp,todayMeal,recipes,binCollections,energy,
  isObserver,hearts,onSendHeart,activePeople,turnips,onUpdatePerson,petHook}){
  const[sub,setSub]=useState(isObserver?"family":"tasks");
  const[showPetModal,setShowPetModal]=useState(false);
  const[showNameModal,setShowNameModal]=useState(false);
  const[showPetInfo,setShowPetInfo]=useState(false);
  const[showHatchAnim,setShowHatchAnim]=useState(false);
  const tabs=isObserver
    ?[{id:"family",label:"Family",icon:"Group"},{id:"turnips",label:"Turnips",icon:"turnip"},{id:"home",label:"Home",icon:"HomeSimpleDoor"}]
    :[{id:"tasks",label:"My Tasks",icon:"check"},{id:"turnips",label:"Turnips",icon:"turnip"},{id:"home",label:"Home",icon:"HomeSimpleDoor"}];

  const todayHearts=hearts.filter(h=>h.from_person===personId);
  const myPet=petHook.getPersonPet(personId);

  // Calculate mood from today's progress
  const myTasks=todayAll(tasks).filter(t=>whoseTurn(t)===personId);
  const doneCount=myTasks.filter(t=>t.completions_today?.length>0).length;
  const petMood=getMoodForProgress(doneCount,myTasks.length);

  // Check for hatch readiness
  const prevStage=useRef(myPet?.stage);
  useEffect(()=>{
    if(myPet&&prevStage.current===0&&myPet.stage===1){
      setShowHatchAnim(true);
      setTimeout(()=>{setShowHatchAnim(false);setShowNameModal(true);},2000);
    }
    if(myPet&&prevStage.current!==undefined&&myPet.stage>prevStage.current&&myPet.stage>1){
      // Evolution notification could go here
    }
    prevStage.current=myPet?.stage;
  },[myPet]);

  const handlePetTap=()=>{
    if(!myPet){
      setShowPetModal(true);
    } else if(myPet.stage===0 && myPet.tasks_completed>=5){
      // Ready to hatch!
      petHook.updatePet(myPet.id,{stage:1});
    } else if(myPet.stage===0){
      // Show egg progress
      setShowPetInfo(true);
    } else {
      // Show pet details
      setShowPetInfo(true);
    }
  };

  // Shared modal backdrop style
  const backdrop={position:"fixed",inset:0,zIndex:1000,background:"rgba(30,20,8,.55)",
    backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",
    padding:"20px",animation:"fadeIn .2s ease-out"};
  const modal={width:"100%",maxWidth:420,maxHeight:"85vh",overflow:"auto",
    background:"var(--bg-card)",borderRadius:24,
    boxShadow:"0 20px 60px rgba(0,0,0,.25)",animation:"slideUp .3s ease-out",
    padding:"28px 24px"};

  return(<div className="fu">
    {/* Egg Picker Modal — centered */}
    {showPetModal&&(
      <div style={backdrop} onClick={()=>setShowPetModal(false)}>
        <div onClick={e=>e.stopPropagation()} style={modal}>
          <EggPicker onPick={async(petData)=>{
            await petHook.createPet(personId,petData);
            setShowPetModal(false);
          }}/>
        </div>
      </div>
    )}

    {/* Name Pet Modal — centered */}
    {showNameModal&&myPet&&(
      <div style={backdrop}>
        <div onClick={e=>e.stopPropagation()} style={modal}>
          <NamePetModal species={myPet.species} onName={async(name)=>{
            await petHook.updatePet(myPet.id,{name});
            setShowNameModal(false);
          }}/>
        </div>
      </div>
    )}

    {/* Hatch Animation Overlay */}
    {showHatchAnim&&(
      <div style={{...backdrop,background:"rgba(255,255,255,.85)",flexDirection:"column",gap:16}}>
        <div style={{animation:"eggWobble .4s ease-in-out infinite",fontSize:64}}>🥚</div>
        <div style={{fontSize:20,fontFamily:"'Fredoka'",color:"var(--text)",animation:"fadeIn .5s ease-out"}}>
          Your egg is hatching!</div>
        <div style={{fontSize:14,color:"var(--text-3)"}}>Something's coming out...</div>
      </div>
    )}

    {/* Pet Info Modal — tap pet to see details */}
    {showPetInfo&&myPet&&(
      <div style={backdrop} onClick={()=>setShowPetInfo(false)}>
        <div onClick={e=>e.stopPropagation()} style={{...modal,textAlign:"center"}}>
          {/* Pet display */}
          <div style={{display:"flex",justifyContent:"center",padding:"16px 0 20px",
            background:"var(--bg-muted)",borderRadius:16,marginBottom:20}}>
            <PetDisplay pet={myPet} size={120} mood={petMood}/>
          </div>

          {myPet.stage===0?(
            <div>
              <h2 style={{fontSize:20,fontFamily:"'Fredoka'",marginBottom:6}}>Your Egg</h2>
              <p style={{fontSize:13,color:"var(--text-3)",marginBottom:20}}>
                Something's growing inside... keep completing tasks to find out what!</p>

              {/* Hatch progress bar */}
              <div style={{marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <span style={{fontSize:12,fontWeight:600,color:"var(--text-2)"}}>Hatch progress</span>
                  <span style={{fontSize:12,fontWeight:700,color:"var(--gold)"}}>{myPet.tasks_completed}/5 tasks</span>
                </div>
                <div style={{height:10,borderRadius:5,background:"var(--bg-muted)",overflow:"hidden"}}>
                  <div style={{height:"100%",borderRadius:5,width:`${Math.min(100,(myPet.tasks_completed/5)*100)}%`,
                    background:"linear-gradient(90deg,var(--gold),var(--peach))",transition:"width .3s"}}/>
                </div>
              </div>
              <p style={{fontSize:12,color:"var(--text-3)",marginTop:12}}>
                {myPet.tasks_completed>=5
                  ?"Your egg is ready to hatch! Tap it on the home screen!"
                  :`${5-myPet.tasks_completed} more task${5-myPet.tasks_completed!==1?"s":""} until hatching`}
              </p>
            </div>
          ):(
            <div>
              <h2 style={{fontSize:22,fontFamily:"'Fredoka'",marginBottom:2}}>
                {myPet.name||SPECIES[myPet.species]?.name}</h2>
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginBottom:16}}>
                <span style={{fontSize:14}}>{SPECIES[myPet.species]?.emoji}</span>
                <span style={{fontSize:13,color:"var(--text-3)"}}>{SPECIES[myPet.species]?.name}</span>
                <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,fontWeight:700,
                  background:myPet.stage>=4?"linear-gradient(135deg,#FFD700,#FFA000)":
                    myPet.stage>=3?"var(--green-bg)":"var(--bg-muted)",
                  color:myPet.stage>=4?"#5C3A00":myPet.stage>=3?"var(--green)":"var(--text-3)"}}>
                  {STAGE_NAMES[myPet.stage]}</span>
              </div>

              {/* Evolution progress */}
              {myPet.stage<4&&(
                <div style={{marginBottom:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                    <span style={{fontSize:12,fontWeight:600,color:"var(--text-2)"}}>
                      Next: {STAGE_NAMES[myPet.stage+1]}</span>
                    <span style={{fontSize:12,fontWeight:700,color:"var(--green)"}}>
                      {myPet.tasks_completed}/{[5,25,75,200][myPet.stage]} tasks</span>
                  </div>
                  <div style={{height:10,borderRadius:5,background:"var(--bg-muted)",overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:5,
                      width:`${Math.min(100,(myPet.tasks_completed/[5,25,75,200][myPet.stage])*100)}%`,
                      background:"linear-gradient(90deg,var(--green),var(--mint))",transition:"width .3s"}}/>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div style={{display:"flex",gap:12,justifyContent:"center",marginBottom:16}}>
                <div style={{padding:"10px 16px",borderRadius:12,background:"var(--bg-muted)",textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,fontFamily:"'Fredoka'",color:"var(--text)"}}>
                    {myPet.tasks_completed}</div>
                  <div style={{fontSize:10,color:"var(--text-3)"}}>tasks done</div>
                </div>
                <div style={{padding:"10px 16px",borderRadius:12,background:"var(--bg-muted)",textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,fontFamily:"'Fredoka'",color:"var(--text)"}}>
                    {STAGE_NAMES[myPet.stage]}</div>
                  <div style={{fontSize:10,color:"var(--text-3)"}}>stage</div>
                </div>
                <div style={{padding:"10px 16px",borderRadius:12,background:"var(--bg-muted)",textAlign:"center"}}>
                  <div style={{fontSize:18}}>
                    {petMood==="celebrating"?"😄":petMood==="excited"?"✨":petMood==="happy"?"😊":
                     petMood==="sleepy"?"😴":petMood==="sad"?"😢":"😐"}</div>
                  <div style={{fontSize:10,color:"var(--text-3)"}}>{petMood}</div>
                </div>
              </div>

              {myPet.stage>=4&&(
                <p style={{fontSize:12,color:"var(--gold)",fontWeight:600,marginTop:8}}>
                  ✨ Legendary status achieved! Your pet is fully evolved.</p>
              )}
            </div>
          )}

          <button onClick={()=>setShowPetInfo(false)} style={{
            width:"100%",marginTop:16,padding:14,borderRadius:14,border:"2px solid var(--bg-muted)",
            background:"var(--bg-card)",cursor:"pointer",fontSize:14,fontWeight:600,
            fontFamily:"'Fredoka'",color:"var(--text-3)"}}>Close</button>
        </div>
      </div>
    )}

    <div style={{display:"flex",gap:6,marginBottom:16}}>
      {tabs.map(t=>(
        <button key={t.id} onClick={()=>setSub(t.id)} style={{
          flex:1,padding:"10px 8px",borderRadius:12,border:"none",cursor:"pointer",
          fontSize:13,fontWeight:600,fontFamily:"'Fredoka'",
          background:sub===t.id?"var(--gold)":"var(--bg-muted)",
          color:sub===t.id?"var(--text)":"var(--text-3)",
          display:"flex",alignItems:"center",justifyContent:"center",gap:5,
          transition:"all .15s",boxShadow:sub===t.id?"var(--shadow-xs)":"none"}}>
          <Icon name={t.icon} size={14} color={sub===t.id?"var(--text)":"var(--text-3)"}/>{t.label}
        </button>))}
    </div>

    {/* Observer: Family view — see everyone's progress and send hearts */}
    {sub==="family"&&isObserver&&(
      <div>
        <div style={{textAlign:"center",padding:"16px 20px 20px"}}>
          <div style={{fontSize:14,color:"var(--text-3)",marginBottom:4}}>Welcome back</div>
          <h2 style={{fontSize:24,fontFamily:"'Fredoka'",color:"var(--text)",marginBottom:8}}>
            {gp(personId)?.name} 💛</h2>
          <p style={{fontSize:13,color:"var(--text-3)"}}>Here's how everyone's getting on today</p>
        </div>

        {activePeople.map(p=>{
          const mine=todayAll(tasks).filter(t=>whoseTurn(t)===p.id);
          const done=mine.filter(t=>t.completions_today?.length>0).length;
          const total=mine.length;
          const pct=total>0?Math.round(done/total*100):0;
          const heartsSent=todayHearts.filter(h=>h.to_person===p.id).length;

          return(<div key={p.id} style={{padding:"16px 18px",borderRadius:"var(--r)",
            background:"var(--bg-card)",border:"1.5px solid var(--bg-muted)",
            boxShadow:"var(--shadow-xs)",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <div style={{width:38,height:38,borderRadius:10,background:p.bg,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Icon name={p.icon} size={20} color={p.color}/></div>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:700,color:"var(--text)"}}>{p.name}</div>
                <div style={{fontSize:12,color:"var(--text-3)"}}>{done}/{total} tasks done</div>
              </div>
              <button onClick={()=>onSendHeart(personId,p.id)} style={{
                padding:"8px 14px",borderRadius:10,border:"none",cursor:"pointer",
                background:heartsSent>0?"var(--coral-bg)":"var(--bg-muted)",
                display:"flex",alignItems:"center",gap:4,transition:"all .15s"}}>
                <span style={{fontSize:16}}>❤️</span>
                {heartsSent>0&&<span style={{fontSize:11,fontWeight:700,color:"var(--coral)"}}>{heartsSent}</span>}
              </button>
            </div>
            {total>0&&<div style={{height:6,borderRadius:3,background:"var(--bg-muted)",overflow:"hidden"}}>
              <div style={{height:"100%",borderRadius:3,width:`${pct}%`,
                background:pct===100?"var(--green)":p.color,transition:"width .3s"}}/></div>}
            {done===total&&total>0&&(
              <div style={{fontSize:11,fontWeight:600,color:"var(--green)",marginTop:6,
                display:"flex",alignItems:"center",gap:4}}>
                <Icon name="check" size={12} color="var(--green)"/>All done!</div>)}
          </div>);
        })}

        {/* Today's dinner */}
        {todayMeal&&(
          <div style={{marginTop:10,padding:"14px 18px",borderRadius:"var(--r-lg)",
            background:"linear-gradient(135deg,var(--coral-bg),var(--peach-bg))",
            border:"1.5px solid var(--coral-light)",
            display:"flex",alignItems:"center",gap:12}}>
            {(()=>{const r=todayMeal.recipe_id&&recipes.find(rx=>rx.id===todayMeal.recipe_id);
              return r?.image_url?<img src={r.image_url} alt="" style={{width:44,height:44,
                borderRadius:12,objectFit:"cover",flexShrink:0}}/>:
                <Icon name="cutlery" size={22} color="var(--coral)"/>;})()}
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--coral)",marginBottom:2}}>Tonight's dinner</div>
              <div style={{fontSize:17,fontWeight:700,fontFamily:"'Fredoka'",color:"var(--text)"}}>
                {todayMeal.title}</div>
            </div>
          </div>
        )}
      </div>
    )}

    {/* Active user: Tasks view */}
    {sub==="tasks"&&!isObserver&&(
      <div>
        <MeTab tasks={tasks} onToggle={onToggle} onTapTask={onTapTask}
          personId={personId} people={people} gp={gp}
          onEditAvatar={handlePetTap} pet={myPet} petMood={petMood}/>
        {todayMeal&&(
          <div style={{marginTop:20,padding:"14px 18px",borderRadius:"var(--r-lg)",
            background:"linear-gradient(135deg,var(--coral-bg),var(--peach-bg))",
            border:"1.5px solid var(--coral-light)",
            display:"flex",alignItems:"center",gap:12}}>
            {(()=>{const r=todayMeal.recipe_id&&recipes.find(rx=>rx.id===todayMeal.recipe_id);
              return r?.image_url?<img src={r.image_url} alt="" style={{width:44,height:44,
                borderRadius:12,objectFit:"cover",flexShrink:0}}/>:
                <Icon name="cutlery" size={22} color="var(--coral)"/>;})()}
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--coral)",marginBottom:2}}>Tonight's dinner</div>
              <div style={{fontSize:17,fontWeight:700,fontFamily:"'Fredoka'",color:"var(--text)"}}>
                {todayMeal.title}</div>
            </div>
          </div>
        )}

        {/* Show hearts received today */}
        {(()=>{const received=hearts.filter(h=>h.to_person===personId);
          return received.length>0?(
            <div style={{marginTop:12,padding:"10px 16px",borderRadius:12,
              background:"var(--coral-bg)",border:"1.5px solid var(--coral-light)",
              display:"flex",alignItems:"center",gap:6,fontSize:13,color:"var(--coral)",fontWeight:600}}>
              <span style={{fontSize:16}}>❤️</span>
              {received.length} heart{received.length!==1?"s":""} from {[...new Set(received.map(h=>gp(h.from_person)?.name))].filter(Boolean).join(" & ")}
            </div>):null;})()}
      </div>
    )}

    {/* Turnip Leaderboard */}
    {sub==="turnips"&&(
      <div>
        <div style={{textAlign:"center",padding:"10px 20px 20px"}}>
          <div style={{marginBottom:4}}><TurnipIcon size={42} color="#558b2f"/></div>
          <h2 style={{fontSize:22,fontFamily:"'Fredoka'",color:"var(--text)",marginBottom:4}}>Turnip Market</h2>
          <p style={{fontSize:12,color:"var(--text-3)"}}>Complete tasks. Earn turnips. Rule the leaderboard.</p>
        </div>

        {/* Leaderboard cards */}
        {(()=>{
          const totals=turnips.getTotals();
          const todayE=turnips.getTodayEarnings();
          const weekE=turnips.getWeekEarnings();
          const ranked=activePeople
            .map(p=>({...p,total:totals[p.id]||0,today:todayE[p.id]||0,week:weekE[p.id]||0}))
            .sort((a,b)=>b.total-a.total);
          const medals=["🥇","🥈","🥉"];

          return(<div style={{display:"flex",flexDirection:"column",gap:8}}>
            {ranked.map((p,i)=>{
              const isMe=p.id===personId;
              return(<div key={p.id} style={{
                padding:"16px 18px",borderRadius:"var(--r)",
                background:isMe?"var(--gold-bg)":"var(--bg-card)",
                border:isMe?"2px solid var(--gold-light)":"1.5px solid var(--bg-muted)",
                boxShadow:i===0?"0 2px 12px rgba(232,168,23,.15)":"var(--shadow-xs)",
                display:"flex",alignItems:"center",gap:12,
                transform:i===0?"scale(1.02)":"none",transition:"all .2s"}}>
                <div style={{fontSize:24,width:32,textAlign:"center",flexShrink:0}}>
                  {i<3?medals[i]:<span style={{fontSize:16,fontWeight:700,color:"var(--text-3)"}}>{i+1}</span>}
                </div>
                <div style={{width:40,height:40,borderRadius:11,background:p.bg,
                  display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                  border:i===0?`2px solid ${p.color}`:"none"}}>
                  <Icon name={p.icon} size={20} color={p.color}/></div>
                <div style={{flex:1}}>
                  <div style={{fontSize:15,fontWeight:700,color:"var(--text)"}}>{p.name}</div>
                  <div style={{fontSize:11,color:"var(--text-3)",display:"flex",gap:8,marginTop:2}}>
                    <span>Today: +{p.today}</span>
                    <span>This week: +{p.week}</span>
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:22,fontWeight:800,fontFamily:"'Fredoka'",color:"#558b2f"}}>
                    {p.total.toLocaleString()}</div>
                  <div style={{fontSize:10,color:"var(--text-3)"}}>turnips</div>
                </div>
              </div>);
            })}
          </div>);
        })()}

        {/* Recent earnings */}
        {(()=>{
          const recent=turnips.ledger.slice(0,8);
          if(!recent.length) return(<div style={{textAlign:"center",padding:"30px 20px",color:"var(--text-3)",fontSize:13}}>
            No turnips earned yet — get completing! <TurnipIcon size={14} color="#558b2f"/></div>);
          return(<div style={{marginTop:20}}>
            <h3 style={{fontSize:14,fontWeight:700,color:"var(--text-2)",marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
              Recent Harvests</h3>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {recent.map(e=>{
                const p=gp(e.person_id);
                const task=tasks.find(t=>t.id===e.task_id);
                return(<div key={e.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",
                  borderRadius:10,background:"var(--bg-card)",border:"1px solid var(--bg-muted)"}}>
                  <div style={{width:24,height:24,borderRadius:7,background:p?.bg||"var(--bg-muted)",
                    display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <Icon name={p?.icon||"user"} size={12} color={p?.color||"var(--text-3)"}/></div>
                  <span style={{flex:1,fontSize:12,color:"var(--text-2)"}}>{p?.name} — {task?.title||"Task"}</span>
                  <span style={{fontSize:13,fontWeight:700,color:"#558b2f",display:"flex",alignItems:"center",gap:3}}>+{e.amount} <TurnipIcon size={14} color="#558b2f"/></span>
                </div>);
              })}
            </div>
          </div>);
        })()}
      </div>
    )}

    {sub==="home"&&(
      <div>
        <BinCard collections={binCollections}/>
        <EnergyCard energy={energy}/>
      </div>
    )}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   HOUSE RULES TAB — the family commandments
   ═══════════════════════════════════════════════════════════════════════ */
const HOUSE_RULES=[
  {num:1,title:"Tidy as we go",desc:"Pick up after yourself — or even someone else.",icon:"Refresh",color:"var(--green)"},
  {num:2,title:"No dishes left overnight",desc:"Wash, clean, and put away. Kitchen ready for morning.",icon:"Dishwasher",color:"var(--sky)"},
  {num:3,title:"Shoes off at the door",desc:"Keeps the floors clean and the house tidy.",icon:"Sandals",color:"var(--gold)"},
  {num:4,title:"If you finish it, let us know",desc:"Last of the milk, toilet roll, bin bags? Add it to Low.",icon:"ChatBubbleWarning",color:"var(--coral)"},
  {num:5,title:"2 minutes? Do it now",desc:"If it takes less than 2 minutes, don't put it on a pile.",icon:"Timer",color:"var(--peach)"},
  {num:6,title:"Put things back where they belong",desc:"Not on the nearest surface. Everything has a home.",icon:"Home",color:"var(--mint)"},
  {num:7,title:"Your room, your responsibility",desc:"Bed made, floor clear, washing in the basket.",icon:"Bed",color:"var(--lilac)"},
  {num:8,title:"If you see it, sort it",desc:"Don't wait to be asked. Just do it.",icon:"Eye",color:"var(--green)"},
  {num:9,title:"Screens down at dinner",desc:"Phones away. Be present. Talk to each other.",icon:"SmartphoneDevice",color:"var(--sky)"},
  {num:10,title:"Be kind to each other",desc:"This is our home. We're a team.",icon:"Heart",color:"var(--coral)"},
];

function HouseRulesTab(){
  return(<div className="fu">
    {/* Header */}
    <div style={{textAlign:"center",padding:"10px 20px 24px",marginBottom:8}}>
      <div style={{fontSize:32,marginBottom:6}}>🏠</div>
      <h2 style={{fontSize:22,fontFamily:"'Fredoka'",color:"var(--text)",marginBottom:4}}>The House Rules</h2>
      <p style={{fontSize:13,color:"var(--text-3)",maxWidth:300,margin:"0 auto"}}>
        Our family commandments. Learn them, live them, love them.</p>
    </div>

    {/* Rules list */}
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {HOUSE_RULES.map(rule=>(
        <div key={rule.num} style={{
          display:"flex",alignItems:"flex-start",gap:14,padding:"16px 18px",
          borderRadius:"var(--r)",background:"var(--bg-card)",
          border:"1.5px solid var(--bg-muted)",boxShadow:"var(--shadow-xs)",
          borderLeft:`4px solid ${rule.color}`}}>
          {/* Number badge */}
          <div style={{width:36,height:36,borderRadius:10,background:rule.color+"15",
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Icon name={rule.icon} size={18} color={rule.color}/>
          </div>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
              <span style={{fontSize:11,fontWeight:800,color:rule.color,fontFamily:"'Fredoka'"}}>
                {rule.num}.</span>
              <span style={{fontSize:15,fontWeight:700,color:"var(--text)"}}>{rule.title}</span>
            </div>
            <p style={{fontSize:13,color:"var(--text-3)",margin:0,lineHeight:1.4}}>{rule.desc}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Footer */}
    <div style={{textAlign:"center",padding:"24px 20px 8px"}}>
      <p style={{fontSize:12,color:"var(--text-3)",fontStyle:"italic"}}>
        "A tidy home is a happy home"</p>
    </div>
  </div>);
}

function TodayTab({tasks,onToggle,onTapTask,personId,people,gp}){
  const all=todayAll(tasks);const td=all.filter(t=>t.completions_today?.length>0).length;
  const grouped=people.filter(p=>p.role!=="observer").map(p=>{const pt=all.filter(t=>whoseTurn(t)===p.id);
    return{...p,tasks:pt,done:pt.filter(t=>t.completions_today?.length>0).length,total:pt.length};});
  return(<div className="fu">
    <div style={{display:"flex",gap:6,marginBottom:20,justifyContent:"center"}}>
      {grouped.map(g=>{const ad=g.total>0&&g.done===g.total;return(
        <div key={g.id} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,flex:1,maxWidth:72}}>
          <div style={{position:"relative",width:44,height:44}}>
            <Ring done={g.done} total={g.total} sz={44} sw={3.5} color={ad?"var(--green)":g.color}/>
          </div>
          <div style={{fontSize:11,fontWeight:700,color:ad?"var(--green)":"var(--text-2)",
            textAlign:"center",lineHeight:1.1}}>{g.name}</div>
          <div style={{fontSize:10,color:ad?"var(--green)":"var(--text-3)",fontWeight:600}}>
            {ad?"Done!":g.total>0?`${g.done}/${g.total}`:"—"}</div>
        </div>);})}
    </div>
    <div style={{background:"var(--bg-card)",borderRadius:"var(--r)",padding:"14px 18px",marginBottom:24,
      display:"flex",alignItems:"center",gap:12,boxShadow:"var(--shadow-xs)"}}>
      <span style={{fontSize:13,fontWeight:600,color:"var(--text-2)",flex:1,display:"flex",alignItems:"center",gap:6}}>
        <Icon name="people" size={16} color="var(--text-2)"/>Household</span>
      <div style={{flex:2,height:10,borderRadius:10,background:"var(--bg-muted)",overflow:"hidden"}}>
        <div style={{height:"100%",borderRadius:10,width:`${all.length?(td/all.length)*100:0}%`,
          background:"linear-gradient(90deg,var(--gold),var(--green),var(--sky))",transition:"width .5s ease"}}/></div>
      <span style={{fontSize:13,fontWeight:700,color:"var(--text-2)",minWidth:36,textAlign:"right"}}>{td}/{all.length}</span>
    </div>
    {grouped.map(g=>{if(!g.tasks.length)return null;
      const pend=g.tasks.filter(t=>!(t.completions_today?.length>0));const dn=g.tasks.filter(t=>t.completions_today?.length>0);const ad=pend.length===0;
      return(<div key={g.id} style={{marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 12px 5px 8px",
            borderRadius:20,background:g.bg,border:`1.5px solid ${g.color}25`}}>
            <div style={{width:22,height:22,borderRadius:6,background:g.color+"18",
              display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Icon name={g.icon} size={12} color={g.color}/></div>
            <span style={{fontSize:13,fontWeight:700,color:g.color}}>{g.name}</span>
            <span style={{fontSize:11,fontWeight:600,color:ad?"var(--green)":g.color,marginLeft:2}}>
              {ad?"✓":`${dn.length}/${g.tasks.length}`}</span>
          </div>
          <div style={{flex:1,height:1,background:g.color+"20"}}/>
        </div>
        <TimelineList tasks={[...pend,...dn]} onToggle={onToggle} showPerson={false} onTapTask={onTapTask}/></div>);})}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   TASK FORM
   ═══════════════════════════════════════════════════════════════════════ */
function TaskForm({task,onSave,onCancel,onDelete,people}){
  const{cats}=useAppCtx();
  const[f,setF]=useState(()=>{
    const base=task||{title:"",details:"",category:"kitchen",
      scheduleType:"recurring",recurrence:"daily",scheduleDay:null,scheduleMonthly:null,scheduleOffset:0,oneOffDate:null,
      assignee:people?.[0]?.id||"",sharedWith:null,sharedWith2:null,startTime:null,doneBy:null,bits:[],turnipValue:5};
    return{...base,bits:(base.bits||[]).map(b=>typeof b==="string"?b:b.label),
      scheduleType:base.scheduleType||"recurring",
      scheduleDay:base.scheduleDay??null,scheduleMonthly:base.scheduleMonthly||null,
      scheduleOffset:base.scheduleOffset||0,oneOffDate:base.oneOffDate||null,
      turnipValue:base.turnipValue??base.turnip_value??5};
  });
  const[newBit,setNewBit]=useState("");
  const inp={width:"100%",padding:"11px 14px",borderRadius:12,border:"2px solid var(--bg-muted)",
    fontSize:14,background:"var(--bg-card)",outline:"none",transition:"border-color .2s"};
  const addBit=()=>{if(!newBit.trim())return;setF({...f,bits:[...f.bits,newBit.trim()]});setNewBit("");};
  const removeBit=i=>setF({...f,bits:f.bits.filter((_,j)=>j!==i)});

  const isOneOff=f.scheduleType==="one_off";
  const needsDay=!isOneOff&&(f.recurrence==="weekly"||f.recurrence==="biweekly");
  const needsMonthly=!isOneOff&&f.recurrence==="monthly";
  const needsOffset=!isOneOff&&f.recurrence==="every_other_day";

  return(<div className="fu" style={{background:"var(--bg-card)",borderRadius:"var(--r-lg)",padding:"24px 20px",boxShadow:"var(--shadow-md)"}}>
    <h3 style={{fontSize:20,marginBottom:18,display:"flex",alignItems:"center",gap:8}}>
      <Icon name={task?"edit":"plus"} size={20} color="var(--text)"/>{task?"Edit task":"New task"}</h3>
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* Title */}
      <div><label style={{fontSize:12,fontWeight:600,color:"var(--text-3)",display:"block",marginBottom:5}}>Task name</label>
        <input style={inp} placeholder="e.g. Clean the kitchen" value={f.title}
          onChange={e=>setF({...f,title:e.target.value})}
          onFocus={e=>e.target.style.borderColor="var(--gold)"} onBlur={e=>e.target.style.borderColor="var(--bg-muted)"}/></div>

      {/* Details */}
      <div><label style={{fontSize:12,fontWeight:600,color:"var(--text-3)",display:"block",marginBottom:5}}>Details</label>
        <textarea style={{...inp,minHeight:50,resize:"vertical"}} placeholder="Instructions..."
          value={f.details} onChange={e=>setF({...f,details:e.target.value})}
          onFocus={e=>e.target.style.borderColor="var(--gold)"} onBlur={e=>e.target.style.borderColor="var(--bg-muted)"}/></div>

      {/* Schedule Type: One-Off vs Recurring */}
      <div>
        <label style={{fontSize:12,fontWeight:600,color:"var(--text-3)",display:"block",marginBottom:6}}>Schedule</label>
        <div style={{display:"flex",gap:8,marginBottom:needsDay||needsMonthly||needsOffset||isOneOff?12:0}}>
          {[{v:"recurring",l:"Recurring",ic:"refresh"},{v:"one_off",l:"One-Off",ic:"calendar"}].map(opt=>(
            <button key={opt.v} onClick={()=>setF({...f,scheduleType:opt.v,
              recurrence:opt.v==="one_off"?null:(f.recurrence||"daily"),
              oneOffDate:opt.v==="one_off"?(f.oneOffDate||new Date().toISOString().slice(0,10)):null,
              scheduleDay:opt.v==="one_off"?null:f.scheduleDay,
              scheduleMonthly:opt.v==="one_off"?null:f.scheduleMonthly,
            })} style={{
              flex:1,padding:"12px 10px",borderRadius:12,cursor:"pointer",
              border:f.scheduleType===opt.v?`2.5px solid var(--gold)`:"2px solid var(--bg-muted)",
              background:f.scheduleType===opt.v?"var(--gold-bg)":"var(--bg-card)",
              fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:6,
              transition:"all .15s",
            }}>
              <Icon name={opt.ic} size={16} color={f.scheduleType===opt.v?"var(--gold-deep)":"var(--text-3)"}/>
              {opt.l}
            </button>
          ))}
        </div>

        {/* One-Off: Date picker */}
        {isOneOff&&(
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Date</div>
            <input type="date" style={{...inp,padding:"10px 14px",fontSize:13}} value={f.oneOffDate||""}
              onChange={e=>setF({...f,oneOffDate:e.target.value||null})}
              onFocus={e=>e.target.style.borderColor="var(--gold)"}
              onBlur={e=>e.target.style.borderColor="var(--bg-muted)"}/>
          </div>
        )}

        {/* Recurring: Frequency buttons */}
        {!isOneOff&&(
          <div>
            <div style={{display:"flex",gap:6}}>
              {REC.map(r=>(<button key={r.value} onClick={()=>{
                const updates={recurrence:r.value};
                if(r.value==="daily"){updates.scheduleDay=null;updates.scheduleMonthly=null;}
                if(r.value==="weekly"||r.value==="biweekly"){updates.scheduleMonthly=null;if(f.scheduleDay==null)updates.scheduleDay=1;}
                if(r.value==="monthly"){updates.scheduleDay=null;if(!f.scheduleMonthly)updates.scheduleMonthly="first";}
                setF({...f,...updates});
              }} style={{
                flex:1,padding:"10px 6px",borderRadius:12,cursor:"pointer",
                border:f.recurrence===r.value?`2.5px solid ${r.color}`:"2px solid var(--bg-muted)",
                background:f.recurrence===r.value?r.color+"15":"var(--bg-card)",fontSize:12,fontWeight:600,textAlign:"center",
                display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <Icon name={r.icon} size={18} color={r.color}/><span>{r.label}</span></button>))}
            </div>

            {/* Day of week picker for Weekly / Bi-weekly */}
            {needsDay&&(
              <div style={{marginTop:10}}>
                <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6}}>
                  Which day?
                </div>
                <div style={{display:"flex",gap:4}}>
                  {DAYS.map((d,i)=>(
                    <button key={i} onClick={()=>setF({...f,scheduleDay:i})} style={{
                      flex:1,padding:"8px 4px",borderRadius:10,cursor:"pointer",fontSize:11,fontWeight:700,
                      border:f.scheduleDay===i?`2.5px solid var(--green)`:"2px solid var(--bg-muted)",
                      background:f.scheduleDay===i?"var(--green-bg)":"var(--bg-card)",
                      color:f.scheduleDay===i?"var(--green)":"var(--text-3)",
                      transition:"all .15s",textAlign:"center",
                    }}>{d}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Day A / Day B picker for Every Other Day */}
            {needsOffset&&(
              <div style={{marginTop:10}}>
                <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6}}>
                  Which rotation?
                </div>
                <div style={{display:"flex",gap:6}}>
                  {[{value:0,label:"Day A",desc:"Today is "+(daysSinceEpoch(new Date())%2===0?"Day A":"Day B")},
                    {value:1,label:"Day B",desc:"Today is "+(daysSinceEpoch(new Date())%2===1?"Day B":"Day A")}].map(opt=>(
                    <button key={opt.value} onClick={()=>setF({...f,scheduleOffset:opt.value})} style={{
                      flex:1,padding:"10px 8px",borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,
                      border:(f.scheduleOffset||0)===opt.value?`2.5px solid var(--peach)`:"2px solid var(--bg-muted)",
                      background:(f.scheduleOffset||0)===opt.value?"var(--peach-bg)":"var(--bg-card)",
                      color:(f.scheduleOffset||0)===opt.value?"var(--peach)":"var(--text-3)",
                      transition:"all .15s",textAlign:"center",
                    }}>{opt.label}</button>
                  ))}
                </div>
                <div style={{fontSize:10,color:"var(--text-3)",marginTop:4,textAlign:"center"}}>
                  Today is {daysSinceEpoch(new Date())%2===0?"Day A":"Day B"} — split tasks across both to spread the load
                </div>
              </div>
            )}

            {/* Monthly option picker */}
            {needsMonthly&&(
              <div style={{marginTop:10}}>
                <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6}}>
                  When in the month?
                </div>
                <div style={{display:"flex",gap:6}}>
                  {MONTHLY_OPTS.map(mo=>(
                    <button key={mo.value} onClick={()=>setF({...f,scheduleMonthly:mo.value})} style={{
                      flex:1,padding:"10px 8px",borderRadius:10,cursor:"pointer",fontSize:11,fontWeight:600,
                      border:f.scheduleMonthly===mo.value?`2.5px solid var(--sky)`:"2px solid var(--bg-muted)",
                      background:f.scheduleMonthly===mo.value?"var(--sky-bg)":"var(--bg-card)",
                      color:f.scheduleMonthly===mo.value?"var(--sky)":"var(--text-3)",
                      transition:"all .15s",textAlign:"center",
                    }}>{mo.label}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Time */}
      <div><label style={{fontSize:12,fontWeight:600,color:"var(--text-3)",display:"flex",alignItems:"center",gap:5,marginBottom:6}}>
          <Icon name="clock" size={13} color="var(--text-3)"/>Time (optional)</label>
        <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
          <div style={{flex:1}}><div style={{fontSize:10,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Start</div>
            <input type="time" style={{...inp,padding:"10px 12px",fontSize:13}} value={f.startTime||""}
              onChange={e=>setF({...f,startTime:e.target.value||null})}/></div>
          <div style={{flex:1}}><div style={{fontSize:10,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Done by</div>
            <input type="time" style={{...inp,padding:"10px 12px",fontSize:13}} value={f.doneBy||""}
              onChange={e=>setF({...f,doneBy:e.target.value||null})}/></div>
          {(f.startTime||f.doneBy)&&(<button onClick={()=>setF({...f,startTime:null,doneBy:null})} style={{
            background:"var(--coral-bg)",border:"none",borderRadius:8,padding:"10px",cursor:"pointer",marginTop:14,
            display:"flex",alignItems:"center"}}><Icon name="trash" size={14} color="var(--coral)"/></button>)}
        </div></div>

      {/* Task Bits */}
      <div><label style={{fontSize:12,fontWeight:600,color:"var(--text-3)",display:"flex",alignItems:"center",gap:5,marginBottom:6}}>
          <Icon name="list" size={13} color="var(--mint)"/>Task Bits
          <span style={{fontWeight:400,color:"var(--text-3)",fontSize:11}}>(things that must be done)</span></label>
        {f.bits.map((b,i)=>(<div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <div style={{flex:1,padding:"9px 12px",borderRadius:10,background:"var(--bg)",fontSize:13,
            border:"1.5px solid var(--bg-muted)",display:"flex",alignItems:"center",gap:6}}>
            <Icon name="check" size={12} color="var(--mint)"/>{b}</div>
          <button onClick={()=>removeBit(i)} style={{background:"none",border:"none",cursor:"pointer",padding:4}}>
            <Icon name="close" size={14} color="var(--coral)"/></button></div>))}
        <div style={{display:"flex",gap:6}}>
          <input style={{...inp,flex:1,fontSize:13}} placeholder="Add a task bit..." value={newBit}
            onChange={e=>setNewBit(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();addBit();}}}
            onFocus={e=>e.target.style.borderColor="var(--mint)"} onBlur={e=>e.target.style.borderColor="var(--bg-muted)"}/>
          <button onClick={addBit} style={{padding:"10px 14px",borderRadius:12,background:"var(--mint-bg)",
            border:`1.5px solid var(--mint-light)`,cursor:"pointer",display:"flex",alignItems:"center"}}>
            <Icon name="plus" size={16} color="var(--mint)" sw={2.5}/></button></div></div>

      {/* Turnip Value */}
      <div><label style={{fontSize:12,fontWeight:600,color:"var(--text-3)",display:"flex",alignItems:"center",gap:5,marginBottom:6}}>
          <TurnipIcon size={14} color="#558b2f"/> Turnip Value
          <span style={{fontWeight:400,color:"var(--text-3)",fontSize:11}}>(reward for completing)</span></label>
        <div style={{display:"flex",gap:5}}>
          {[1,3,5,8,10,15,20].map(v=>(
            <button key={v} onClick={()=>setF({...f,turnipValue:v})} style={{
              padding:"8px 10px",borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:700,
              border:(f.turnipValue||5)===v?"2.5px solid #7cb342":"2px solid var(--bg-muted)",
              background:(f.turnipValue||5)===v?"#e8f5e9":"var(--bg-card)",
              color:(f.turnipValue||5)===v?"#558b2f":"var(--text-3)",
              transition:"all .15s",minWidth:36,textAlign:"center",
            }}>{v}</button>))}
        </div>
      </div>

      {/* Category */}
      <div><label style={{fontSize:12,fontWeight:600,color:"var(--text-3)",display:"block",marginBottom:6}}>Category</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {cats.map(c=>(<button key={c.value} onClick={()=>setF({...f,category:c.value})} style={{
            padding:"7px 12px",borderRadius:10,cursor:"pointer",
            border:f.category===c.value?`2.5px solid ${c.color}`:"2px solid transparent",
            background:c.bg,fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
            <Icon name={c.icon} size={12} color={c.color}/>{c.label}</button>))}</div></div>

      {/* Assign — tap one person or tap two to share/alternate */}
      <div><label style={{fontSize:12,fontWeight:600,color:"var(--text-3)",display:"block",marginBottom:6}}>
        Assign to {(f.sharedWith||f.sharedWith2)&&<span style={{fontWeight:400,fontSize:11,color:"var(--lilac)"}}> — alternates between {f.sharedWith2?"all three":"both"}</span>}
      </label>
        <div style={{display:"flex",gap:8}}>
          {people.map(p=>{
            const isAssignee=f.assignee===p.id;
            const isShared=f.sharedWith===p.id;
            const isShared2=f.sharedWith2===p.id;
            const isSelected=isAssignee||isShared||isShared2;
            return(<button key={p.id} onClick={()=>{
              if(isAssignee){
                // Deselecting primary — promote shared, then shared2
                if(f.sharedWith){setF({...f,assignee:f.sharedWith,sharedWith:f.sharedWith2||null,sharedWith2:null});}
                return;
              }
              if(isShared){
                // Deselecting shared — promote shared2
                setF({...f,sharedWith:f.sharedWith2||null,sharedWith2:null});return;
              }
              if(isShared2){
                setF({...f,sharedWith2:null});return;
              }
              // Selecting — fill first empty slot
              if(!f.assignee){setF({...f,assignee:p.id});}
              else if(!f.sharedWith){setF({...f,sharedWith:p.id});}
              else if(!f.sharedWith2){setF({...f,sharedWith2:p.id});}
            }} style={{
              flex:1,padding:"14px 8px",borderRadius:"var(--r)",cursor:"pointer",
              border:isSelected?`3px solid ${p.color}`:"2px solid var(--bg-muted)",
              background:isSelected?p.bg:"var(--bg-card)",fontSize:13,fontWeight:600,
              display:"flex",flexDirection:"column",alignItems:"center",gap:6,position:"relative"}}>
              <div style={{width:36,height:36,borderRadius:10,background:p.light||p.bg,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Icon name={p.icon} size={20} color={p.color}/></div>
              {p.name}
              {isSelected&&<div style={{position:"absolute",top:6,right:6,width:8,height:8,borderRadius:4,
                background:p.color}}/>}
            </button>);
          })}</div></div>

      {/* Save / Cancel */}
      <div style={{display:"flex",gap:8,marginTop:6}}>
        <button onClick={()=>{if(!f.title.trim())return;
          onSave({...f,id:task?.id||undefined});}}
          style={{flex:1,padding:"13px",borderRadius:12,background:"var(--gold)",border:"none",fontSize:15,
            fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",color:"var(--text)",
            display:"flex",alignItems:"center",justifyContent:"center",gap:6,transition:"background .2s"}}
          onMouseEnter={e=>e.currentTarget.style.background="var(--gold-deep)"}
          onMouseLeave={e=>e.currentTarget.style.background="var(--gold)"}>
          <Icon name="check" size={16} color="var(--text)" sw={2.5}/>{task?"Save changes":"Add task"}</button>
        <button onClick={onCancel} style={{padding:"13px 20px",borderRadius:12,background:"var(--bg-muted)",
          border:"none",fontSize:14,fontWeight:600,cursor:"pointer",color:"var(--text-2)"}}>Cancel</button></div>
      {task&&onDelete&&(<button onClick={()=>onDelete(task.id)} style={{width:"100%",padding:"11px",borderRadius:12,
        background:"var(--coral-bg)",border:`1.5px solid var(--coral-light)`,color:"var(--coral)",fontSize:13,
        fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
        <Icon name="trash" size={14} color="var(--coral)"/>Delete task</button>)}
    </div>
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   TOGGLE SWITCH
   ═══════════════════════════════════════════════════════════════════════ */
function Toggle({on,onToggle,color="var(--green)"}){
  return(
    <button onClick={onToggle} style={{
      width:44,height:26,borderRadius:13,padding:2,border:"none",cursor:"pointer",
      background:on?color:"var(--bg-muted)",transition:"background .2s",
      display:"flex",alignItems:"center",
    }}>
      <div style={{
        width:22,height:22,borderRadius:11,background:"white",
        boxShadow:"0 1px 3px rgba(0,0,0,.15)",
        transition:"transform .2s",
        transform:on?"translateX(18px)":"translateX(0)",
      }}/>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   NOTIFICATION SETTINGS (per-person email digest controls)
   ═══════════════════════════════════════════════════════════════════════ */
function NotificationSettings({people,onUpdatePerson}){
  const[localOverrides,setLocalOverrides]=useState({});

  const getVal=(personId,field)=>{
    const key=`${personId}:${field}`;
    if(key in localOverrides) return localOverrides[key];
    const p=people.find(pp=>pp.id===personId);
    return p?.[field]!==false;
  };

  const getBool=(personId,field)=>{
    const key=`${personId}:${field}`;
    if(key in localOverrides) return localOverrides[key];
    const p=people.find(pp=>pp.id===personId);
    return !!p?.[field];
  };

  const handleToggle=async(personId,field)=>{
    const key=`${personId}:${field}`;
    const current=getVal(personId,field);
    const next=!current;
    setLocalOverrides(prev=>({...prev,[key]:next}));
    await onUpdatePerson(personId,{[field]:next});
    setLocalOverrides(prev=>{const n={...prev};delete n[key];return n;});
  };

  const handleBoolToggle=async(personId,field)=>{
    const key=`${personId}:${field}`;
    const current=getBool(personId,field);
    const next=!current;
    setLocalOverrides(prev=>({...prev,[key]:next}));
    await onUpdatePerson(personId,{[field]:next});
    setLocalOverrides(prev=>{const n={...prev};delete n[key];return n;});
  };

  return(
    <div style={{marginBottom:28}}>

      {/* ── SMS Notifications ── */}
      <h3 style={{fontSize:16,color:"var(--text-2)",marginBottom:14,display:"flex",alignItems:"center",gap:7}}>
        <Icon name="Phone" size={17} color="var(--text-2)"/>SMS Notifications
      </h3>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:24}}>
        {people.map(p=>(
          <div key={p.id} style={{
            background:"var(--bg-card)",borderRadius:"var(--r)",padding:"16px 18px",
            boxShadow:"var(--shadow-xs)",border:`1.5px solid ${p.color}18`,
          }}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <div style={{width:32,height:32,borderRadius:9,background:p.bg,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Icon name={p.icon} size={16} color={p.color}/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:14}}>{p.name}</div>
                <div style={{fontSize:11,color:"var(--text-3)"}}>{p.phone||"No phone set"}</div>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <Icon name="sun" size={14} color="var(--gold)"/>
                  <span style={{fontSize:13,fontWeight:500,color:"var(--text-2)"}}>Morning SMS</span>
                </div>
                <Toggle on={getVal(p.id,"digest_morning")} color="var(--gold)"
                  onToggle={()=>handleToggle(p.id,"digest_morning")}/>
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <Icon name="moon" size={14} color="var(--lilac)"/>
                  <span style={{fontSize:13,fontWeight:500,color:"var(--text-2)"}}>Evening SMS</span>
                </div>
                <Toggle on={getVal(p.id,"digest_evening")} color="var(--lilac)"
                  onToggle={()=>handleToggle(p.id,"digest_evening")}/>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Email Notifications ── */}
      <h3 style={{fontSize:16,color:"var(--text-2)",marginBottom:14,display:"flex",alignItems:"center",gap:7}}>
        <Icon name="mail" size={17} color="var(--text-2)"/>Email Notifications
      </h3>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:24}}>
        {people.map(p=>(
          <div key={p.id} style={{
            background:"var(--bg-card)",borderRadius:"var(--r)",padding:"16px 18px",
            boxShadow:"var(--shadow-xs)",border:`1.5px solid ${p.color}18`,
          }}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <div style={{width:32,height:32,borderRadius:9,background:p.bg,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Icon name={p.icon} size={16} color={p.color}/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:14}}>{p.name}</div>
                <div style={{fontSize:11,color:"var(--text-3)"}}>{p.email||"No email set"}</div>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <Icon name="sun" size={14} color="var(--gold)"/>
                  <span style={{fontSize:13,fontWeight:500,color:"var(--text-2)"}}>Morning email</span>
                </div>
                <Toggle on={getBool(p.id,"email_morning")} color="var(--gold)"
                  onToggle={()=>handleBoolToggle(p.id,"email_morning")}/>
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <Icon name="moon" size={14} color="var(--lilac)"/>
                  <span style={{fontSize:13,fontWeight:500,color:"var(--text-2)"}}>Evening email</span>
                </div>
                <Toggle on={getBool(p.id,"email_evening")} color="var(--lilac)"
                  onToggle={()=>handleBoolToggle(p.id,"email_evening")}/>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Member Roles ── */}
      <h3 style={{fontSize:16,color:"var(--text-2)",marginBottom:14,display:"flex",alignItems:"center",gap:7}}>
        <Icon name="Group" size={17} color="var(--text-2)"/>Members
      </h3>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {people.map(p=><MemberCard key={p.id} person={p} onUpdate={onUpdatePerson}/>)}
      </div>
    </div>
  );
}

const PERSON_COLORS=[
  {color:"#E8A817",bg:"#fdf4e3",light:"#f5d98a"},
  {color:"#7CB342",bg:"#e8f5e9",light:"#a5d6a7"},
  {color:"#42A5F5",bg:"#e3f2fd",light:"#90caf9"},
  {color:"#AB47BC",bg:"#f3e5f5",light:"#ce93d8"},
  {color:"#EF5350",bg:"#ffebee",light:"#ef9a9a"},
  {color:"#E88B9C",bg:"#fce4ec",light:"#f8bbd0"},
  {color:"#FF7043",bg:"#fbe9e7",light:"#ffab91"},
  {color:"#26A69A",bg:"#e0f2f1",light:"#80cbc4"},
  {color:"#5C6BC0",bg:"#e8eaf6",light:"#9fa8da"},
  {color:"#8D6E63",bg:"#efebe9",light:"#bcaaa4"},
];

function MemberCard({person:p,onUpdate}){
  const[expanded,setExpanded]=useState(false);

  return(<div style={{
    background:"var(--bg-card)",borderRadius:"var(--r)",
    boxShadow:"var(--shadow-xs)",border:`1.5px solid ${p.color}18`,overflow:"hidden"}}>

    {/* Header row — always visible */}
    <div onClick={()=>setExpanded(!expanded)} style={{
      padding:"14px 18px",display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
      <div style={{width:36,height:36,borderRadius:10,background:p.bg,
        display:"flex",alignItems:"center",justifyContent:"center"}}>
        <Icon name={p.icon} size={18} color={p.color}/>
      </div>
      <div style={{flex:1}}>
        <div style={{fontWeight:700,fontSize:14}}>{p.name}</div>
        <div style={{fontSize:11,color:"var(--text-3)"}}>
          {p.is_admin?"Admin":"Member"}{p.role==="observer"?" · Observer":""}</div>
      </div>
      <div style={{display:"flex",gap:6}}>
        <button onClick={async(e)=>{e.stopPropagation();
          await onUpdate(p.id,{is_admin:!p.is_admin});
        }} style={{
          padding:"7px 12px",borderRadius:10,border:"none",cursor:"pointer",
          fontSize:11,fontWeight:700,fontFamily:"'Fredoka'",
          background:p.is_admin?"var(--gold-bg)":"var(--bg-muted)",
          color:p.is_admin?"var(--gold)":"var(--text-3)"}}>
          {p.is_admin?"Admin":"Make Admin"}
        </button>
        <button onClick={async(e)=>{e.stopPropagation();
          await onUpdate(p.id,{role:p.role==="observer"?"active":"observer"});
        }} style={{
          padding:"7px 12px",borderRadius:10,border:"none",cursor:"pointer",
          fontSize:11,fontWeight:700,fontFamily:"'Fredoka'",
          background:p.role==="observer"?"var(--green-bg)":"var(--peach-bg)",
          color:p.role==="observer"?"var(--green)":"var(--peach)"}}>
          {p.role==="observer"?"Make Active":"Make Observer"}
        </button>
      </div>
      <Icon name={expanded?"NavArrowUp":"NavArrowDown"} size={16} color="var(--text-3)"/>
    </div>

    {/* Expanded: icon + colour pickers */}
    {expanded&&(
      <div style={{padding:"0 18px 18px",borderTop:"1px solid var(--bg-muted)"}}>
        {/* Icon picker */}
        <div style={{marginTop:14,marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:600,color:"var(--text-3)",marginBottom:8}}>Icon</div>
          <IconPicker value={p.icon} color={p.color} onChange={async(icon)=>{
            await onUpdate(p.id,{icon});
          }}/>
        </div>

        {/* Colour picker */}
        <div>
          <div style={{fontSize:12,fontWeight:600,color:"var(--text-3)",marginBottom:8}}>Colour</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {PERSON_COLORS.map((c,i)=>(
              <button key={i} onClick={async()=>{
                await onUpdate(p.id,{color:c.color,bg:c.bg,light:c.light});
              }} style={{
                width:32,height:32,borderRadius:9,background:c.color,border:"none",cursor:"pointer",
                outline:p.color===c.color?"3px solid var(--text)":"none",
                outlineOffset:2,transition:"all .15s",
              }}/>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   CATEGORY MANAGER (admin — add/delete categories with icon + colour)
   ═══════════════════════════════════════════════════════════════════════ */
function CategoryManager({categories,onCreate,onUpdate,onDelete}){
  const[adding,setAdding]=useState(false);
  const[editing,setEditing]=useState(null); // category object or null
  const[label,setLabel]=useState("");
  const[icon,setIcon]=useState("Cube");
  const[colorIdx,setColorIdx]=useState(0);
  const[confirm,setConfirm]=useState(null);

  const inp={width:"100%",padding:"10px 14px",borderRadius:12,border:"2px solid var(--bg-muted)",
    fontSize:14,background:"var(--bg-card)",outline:"none",fontFamily:"inherit"};

  const startEdit=(cat)=>{
    setEditing(cat);setAdding(false);
    setLabel(cat.label);setIcon(cat.icon);
    const ci=COLOR_OPTIONS.findIndex(c=>c.color===cat.color);
    setColorIdx(ci>=0?ci:0);
  };

  const resetForm=()=>{setLabel("");setIcon("Cube");setColorIdx(0);setAdding(false);setEditing(null);};

  const handleSave=async()=>{
    if(!label.trim())return;
    const col=COLOR_OPTIONS[colorIdx];
    if(editing){
      await onUpdate(editing.id,{label:label.trim(),icon,color:col.color,bg:col.bg});
    }else{
      await onCreate({label:label.trim(),icon,color:col.color,bg:col.bg});
    }
    resetForm();
  };

  const showForm=adding||editing;

  return(
    <div style={{marginBottom:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
        <h3 style={{fontSize:16,color:"var(--text-2)",display:"flex",alignItems:"center",gap:7}}>
          <Icon name="Packages" size={17} color="var(--text-2)"/>Categories
        </h3>
        {!showForm&&<button onClick={()=>{resetForm();setAdding(true);}} style={{
          padding:"8px 14px",borderRadius:10,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
          background:"var(--gold-bg)",color:"var(--gold-deep)",
          display:"flex",alignItems:"center",gap:4,transition:"all .15s"}}>
          <Icon name="plus" size={13} color="var(--gold-deep)" sw={2.5}/>Add
        </button>}
      </div>

      {/* Add / Edit form */}
      {showForm&&(
        <div className="fu" style={{padding:"18px",borderRadius:"var(--r)",background:"var(--bg-card)",
          border:"1.5px solid var(--bg-muted)",boxShadow:"var(--shadow-sm)",marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-2)",marginBottom:12}}>
            {editing?"Edit category":"New category"}</div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:5}}>Name</div>
            <input style={inp} placeholder="e.g. Garden Shed" value={label}
              onChange={e=>setLabel(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter")handleSave();}}/>
          </div>
          <div style={{display:"flex",gap:14,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6}}>Icon</div>
              <IconPicker value={icon} onChange={setIcon} color={COLOR_OPTIONS[colorIdx].color}/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6}}>Colour</div>
              <div style={{display:"flex",gap:6}}>
                {COLOR_OPTIONS.map((col,i)=>(
                  <button key={col.label} onClick={()=>setColorIdx(i)} title={col.label} style={{
                    width:32,height:32,borderRadius:10,background:col.bg,
                    border:colorIdx===i?`3px solid ${col.color}`:"2px solid var(--bg-muted)",
                    cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .12s",
                  }}>
                    <div style={{width:12,height:12,borderRadius:4,background:col.color}}/>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{padding:"6px 14px",borderRadius:10,background:COLOR_OPTIONS[colorIdx].bg,
              display:"flex",alignItems:"center",gap:5,fontSize:13,fontWeight:700,
              color:COLOR_OPTIONS[colorIdx].color,border:`1.5px solid ${COLOR_OPTIONS[colorIdx].color}30`}}>
              <Icon name={icon} size={14} color={COLOR_OPTIONS[colorIdx].color}/>{label||"Preview"}
            </div>
            <div style={{flex:1}}/>
            <button onClick={resetForm} style={{padding:"10px 16px",borderRadius:10,border:"none",
              background:"var(--bg-muted)",fontSize:13,fontWeight:600,cursor:"pointer",color:"var(--text-3)"}}>Cancel</button>
            <button onClick={handleSave} disabled={!label.trim()} style={{
              padding:"10px 20px",borderRadius:10,border:"none",background:"var(--gold)",
              color:"var(--text)",fontSize:13,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",
              opacity:label.trim()?1:.5}}>{editing?"Save":"Add"}</button>
          </div>
        </div>
      )}

      {/* Existing categories */}
      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
        {categories.map(cat=>(
          <div key={cat.id} style={{display:"flex",alignItems:"center",gap:4,padding:"7px 12px",
            borderRadius:10,background:cat.bg,border:`1.5px solid ${cat.color}30`,position:"relative",
            cursor:"pointer"}} onClick={()=>startEdit(cat)}>
            <Icon name={cat.icon} size={13} color={cat.color}/>
            <span style={{fontSize:12,fontWeight:600,color:cat.color}}>{cat.label}</span>
            {confirm===cat.id?(
              <div style={{display:"flex",gap:3,marginLeft:4}} onClick={e=>e.stopPropagation()}>
                <button onClick={async()=>{await onDelete(cat.id);setConfirm(null);}} style={{
                  background:"var(--coral)",border:"none",borderRadius:6,padding:"2px 8px",cursor:"pointer",
                  fontSize:10,fontWeight:700,color:"white"}}>Delete</button>
                <button onClick={()=>setConfirm(null)} style={{
                  background:"var(--bg-muted)",border:"none",borderRadius:6,padding:"2px 6px",cursor:"pointer",
                  fontSize:10,fontWeight:600,color:"var(--text-3)"}}>No</button>
              </div>
            ):(
              <button onClick={e=>{e.stopPropagation();setConfirm(cat.id);}} style={{
                background:"none",border:"none",cursor:"pointer",padding:"0 0 0 4px",opacity:.3,
                display:"flex",alignItems:"center"}} title="Delete category">
                <Icon name="close" size={11} color="var(--text-3)"/>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   ADMIN TAB
   ═══════════════════════════════════════════════════════════════════════ */
function AdminTab({tasks,onEdit,onToggle,onNew,onDelete,onTapTask,people,onUpdatePerson,
  categories,onCreateCategory,onUpdateCategory,onDeleteCategory}){
  const{gc}=useAppCtx();
  const[subTab,setSubTab]=useState("tasks");
  const[fp,setFp]=useState("all");const[fr,setFr]=useState("all");
  let list=[...tasks];
  if(fp!=="all")list=list.filter(t=>t.assignee===fp);
  if(fr!=="all")list=list.filter(t=>t.recurrence===fr);
  const cg={};list.forEach(t=>{if(!cg[t.category])cg[t.category]=[];cg[t.category].push(t);});

  const subTabs=[
    {id:"tasks",label:"Tasks",icon:"list"},
    {id:"categories",label:"Categories",icon:"Packages"},
    {id:"notifications",label:"Notifications",icon:"mail"},
  ];

  return(<div className="fu">
    {/* Header with New Task button */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
      <h2 style={{fontSize:20,display:"flex",alignItems:"center",gap:8}}>
        <Icon name="settings" size={20} color="var(--text)"/>Admin</h2>
      {subTab==="tasks"&&(
        <button onClick={onNew} style={{padding:"10px 18px",borderRadius:12,background:"var(--gold)",border:"none",
          fontSize:14,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",boxShadow:"var(--shadow-sm)",color:"var(--text)",
          display:"flex",alignItems:"center",gap:5,transition:"background .2s"}}
          onMouseEnter={e=>e.currentTarget.style.background="var(--gold-deep)"}
          onMouseLeave={e=>e.currentTarget.style.background="var(--gold)"}>
          <Icon name="plus" size={15} color="var(--text)" sw={2.5}/>New task</button>
      )}
    </div>

    {/* Sub-tab navigation */}
    <div style={{display:"flex",gap:0,marginBottom:20,background:"var(--bg-muted)",borderRadius:14,padding:3}}>
      {subTabs.map(st=>{const active=subTab===st.id;return(
        <button key={st.id} onClick={()=>setSubTab(st.id)} style={{
          flex:1,padding:"10px 8px",borderRadius:11,border:"none",cursor:"pointer",
          background:active?"var(--bg-card)":"transparent",
          boxShadow:active?"var(--shadow-sm)":"none",
          color:active?"var(--text)":"var(--text-3)",fontSize:12,fontWeight:600,
          display:"flex",alignItems:"center",justifyContent:"center",gap:5,
          transition:"all .2s",
        }}>
          <Icon name={st.icon} size={14} color={active?"var(--text)":"var(--text-3)"} sw={active?2.2:1.8}/>
          {st.label}
        </button>
      );})}
    </div>

    {/* ── Tasks sub-tab ── */}
    {subTab==="tasks"&&(<div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
        {[{id:"all",name:"Everyone",icon:"people",color:"var(--text-2)",bg:"var(--bg-card)"},...people].map(p=>(
          <button key={p.id} onClick={()=>setFp(p.id)} style={{padding:"7px 14px",borderRadius:12,cursor:"pointer",
            fontSize:12,fontWeight:600,border:fp===p.id?`2px solid ${p.color}`:"1.5px solid var(--bg-muted)",
            background:fp===p.id?(p.bg||"var(--bg-card)"):"transparent",display:"flex",alignItems:"center",gap:4}}>
            <Icon name={p.icon} size={12} color={fp===p.id?p.color:"var(--text-3)"}/>{p.name}</button>))}</div>
      <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:20}}>
        <button onClick={()=>setFr("all")} style={{padding:"5px 11px",borderRadius:10,cursor:"pointer",fontSize:11,fontWeight:600,
          border:fr==="all"?"2px solid var(--text-3)":"1.5px solid var(--bg-muted)",
          background:fr==="all"?"var(--bg-card)":"transparent"}}>All</button>
        {REC.map(r=>(<button key={r.value} onClick={()=>setFr(r.value)} style={{padding:"5px 11px",borderRadius:10,
          cursor:"pointer",fontSize:11,fontWeight:600,border:fr===r.value?`2px solid ${r.color}`:"1.5px solid var(--bg-muted)",
          background:fr===r.value?r.color+"12":"transparent",display:"flex",alignItems:"center",gap:3}}>
          <Icon name={r.icon} size={11} color={r.color}/>{r.label}</button>))}</div>
      {Object.entries(cg).map(([cv,ct])=>{const cat=gc(cv);return(
        <div key={cv} style={{marginBottom:22}}>
          <div style={{display:"flex",alignItems:"center",gap:6,padding:"0 4px",marginBottom:8}}>
            <span style={{padding:"4px 12px",borderRadius:10,background:cat?.bg,fontSize:12,fontWeight:700,
              display:"flex",alignItems:"center",gap:4,color:cat?.color}}>
              <Icon name={cat?.icon} size={12} color={cat?.color}/>{cat?.label}</span>
            <span style={{fontSize:11,color:"var(--text-3)"}}>{ct.length}</span></div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {ct.map((t,i)=>(
              <div key={t.id} style={{display:"flex",alignItems:"center",gap:6}}>
                <div style={{flex:1}}>
                  <TimelineRow task={t} onToggle={onToggle} onEdit={onEdit}
                    showPerson isLast={i===ct.length-1} onTap={onTapTask}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:4,paddingBottom:i===ct.length-1?0:8}}>
                  <button onClick={()=>onEdit(t)} title="Edit" style={{width:30,height:30,borderRadius:8,
                    border:"none",cursor:"pointer",background:"var(--gold-bg)",
                    display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <Icon name="edit" size={13} color="var(--gold-deep)"/></button>
                  <button onClick={()=>{if(window.confirm(`Delete "${t.title}"?`))onDelete(t.id);}} title="Delete"
                    style={{width:30,height:30,borderRadius:8,border:"none",cursor:"pointer",
                      background:"var(--coral-bg)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <Icon name="trash" size={13} color="var(--coral)"/></button>
                </div>
              </div>
            ))}</div>
        </div>);})}
      {list.length===0&&<div style={{textAlign:"center",padding:40,color:"var(--text-3)"}}>No tasks match</div>}
    </div>)}

    {/* ── Categories sub-tab ── */}
    {subTab==="categories"&&(
      <CategoryManager categories={categories} onCreate={onCreateCategory} onUpdate={onUpdateCategory} onDelete={onDeleteCategory}/>
    )}

    {/* ── Notifications sub-tab ── */}
    {subTab==="notifications"&&(
      <NotificationSettings people={people} onUpdatePerson={onUpdatePerson}/>
    )}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   SWAP REQUEST BADGE (shown on timeline row when a swap is pending)
   ═══════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════
   SWAP REQUEST PANEL (shown in admin, pending swaps to resolve)
   ═══════════════════════════════════════════════════════════════════════ */
function SwapPanel({swaps,people,onReassign,onDismiss,tasks}){
  const pending=swaps.filter(s=>s.status==="pending");
  if(!pending.length) return null;
  const getTitle=(taskId)=>{const t=tasks?.find(t=>t.id===taskId);return t?.title||"Unknown task";};
  return(
    <div style={{marginBottom:24}}>
      <h3 style={{fontSize:16,color:"var(--peach)",marginBottom:12,display:"flex",alignItems:"center",gap:7}}>
        <Icon name="swap" size={17} color="var(--peach)"/>Swap Requests ({pending.length})
      </h3>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {pending.map(s=>{
          const others=people.filter(p=>p.id!==s.from_person);
          return(
            <div key={s.id} style={{background:"var(--bg-card)",borderRadius:"var(--r)",padding:"16px",
              border:"1.5px solid var(--peach-bg)",boxShadow:"var(--shadow-xs)"}}>
              <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>{getTitle(s.task_id)}</div>
              <div style={{fontSize:12,color:"var(--text-3)",marginBottom:8}}>
                <PersonChip id={s.from_person}/> can't do this today
                {s.reason&&<span> — "{s.reason}"</span>}
              </div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6}}>Reassign to:</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {others.map(p=>(
                  <button key={p.id} onClick={()=>onReassign(s.id,s.task_id,p.id)} style={{
                    padding:"8px 14px",borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,
                    background:p.bg,border:`1.5px solid ${p.color}33`,display:"flex",alignItems:"center",gap:4,
                  }}><Icon name={p.icon} size={12} color={p.color}/>{p.name}</button>
                ))}
                <button onClick={()=>onDismiss(s.id)} style={{
                  padding:"8px 14px",borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,
                  background:"var(--bg-muted)",border:"none",color:"var(--text-3)",
                }}>Dismiss</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   NOTE INPUT (shown in detail card after completing)
   ═══════════════════════════════════════════════════════════════════════ */
function NoteInput({note,setNote}){
  return(
    <div style={{marginTop:12}}>
      <div style={{fontSize:12,fontWeight:600,color:"var(--text-3)",marginBottom:5,display:"flex",alignItems:"center",gap:5}}>
        <Icon name="chat" size={12} color="var(--text-3)"/>Leave a note (optional)
      </div>
      <textarea value={note} onChange={e=>setNote(e.target.value)}
        placeholder="e.g. We're out of rinse aid..."
        style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"2px solid var(--bg-muted)",
          fontSize:13,background:"var(--bg)",outline:"none",minHeight:44,resize:"vertical",
          fontFamily:"inherit",transition:"border-color .2s"}}
        onFocus={e=>e.target.style.borderColor="var(--mint)"}
        onBlur={e=>e.target.style.borderColor="var(--bg-muted)"}/>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MEAL PLAN TAB
   ═══════════════════════════════════════════════════════════════════════ */
function MealTab({meals,onSaveMeal,onDeleteMeal,onCopyWeek,
  recipes,onCreateRecipe,onUpdateRecipe,onDeleteRecipe,onToggleFavourite,
  shoppingItems,onGenerateShoppingList,onAddShoppingItem,onToggleChecked,onTogglePantry,onDeleteShoppingItem,onClearChecked,
  themes,personId}){

  const[subTab,setSubTab]=useState("planner");

  // Navigable week offset (0 = this week, 1 = next week, -1 = last week)
  const[weekOffset,setWeekOffset]=useState(0);

  const getMonday=(offset=0)=>{
    const today=new Date();
    const dow=today.getDay();
    const mondayOffset=dow===0?-6:1-dow;
    const mon=new Date(today);
    mon.setDate(today.getDate()+mondayOffset+(offset*7));
    return mon;
  };

  const monday=getMonday(weekOffset);
  const mondayStr=monday.toISOString().slice(0,10);
  const isThisWeek=weekOffset===0;

  // Get meals for the selected week
  const sundayEnd=new Date(monday);sundayEnd.setDate(monday.getDate()+6);
  const weekMeals=meals.filter(m=>{
    const d=m.date;return d>=mondayStr&&d<=sundayEnd.toISOString().slice(0,10);
  });

  // Auto-generate shopping list when switching to shopping tab
  const handleShopTab=()=>{
    setSubTab("shop");
    onGenerateShoppingList(weekMeals,recipes);
  };

  const subTabs=[
    {id:"planner",label:"Planner",icon:"calendar"},
    {id:"recipes",label:"Recipes",icon:"cutlery"},
    {id:"shop",label:"Shopping",icon:"shop"},
    {id:"prep",label:"Prep Day",icon:"clock"},
  ];

  return(<div className="fu">
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
      <Icon name="cutlery" size={20} color="var(--coral)"/>
      <h2 style={{fontSize:20,flex:1}}>Meals</h2>
    </div>

    <div style={{display:"flex",gap:0,marginBottom:20,background:"var(--bg-muted)",borderRadius:14,padding:3}}>
      {subTabs.map(st=>{const active=subTab===st.id;return(
        <button key={st.id} onClick={()=>st.id==="shop"?handleShopTab():setSubTab(st.id)} style={{
          flex:1,padding:"9px 6px",borderRadius:11,border:"none",cursor:"pointer",
          background:active?"var(--bg-card)":"transparent",boxShadow:active?"var(--shadow-sm)":"none",
          color:active?"var(--text)":"var(--text-3)",fontSize:11,fontWeight:600,
          display:"flex",alignItems:"center",justifyContent:"center",gap:4,transition:"all .2s",
        }}><Icon name={st.icon} size={13} color={active?"var(--text)":"var(--text-3)"} sw={active?2.2:1.8}/>{st.label}</button>
      );})}
    </div>

    {subTab==="planner"&&<WeekPlanner meals={meals} onSaveMeal={onSaveMeal} onDeleteMeal={onDeleteMeal}
      onCopyWeek={onCopyWeek} recipes={recipes} themes={themes} monday={monday} mondayStr={mondayStr}
      personId={personId} weekOffset={weekOffset} onPrev={()=>setWeekOffset(w=>w-1)}
      onNext={()=>setWeekOffset(w=>w+1)} onToday={()=>setWeekOffset(0)} isThisWeek={isThisWeek}/>}
    {subTab==="recipes"&&<RecipeBank recipes={recipes} onCreate={onCreateRecipe} onUpdate={onUpdateRecipe}
      onDelete={onDeleteRecipe} onToggleFav={onToggleFavourite} personId={personId}/>}
    {subTab==="shop"&&<ShoppingList items={shoppingItems} onGenerate={()=>onGenerateShoppingList(weekMeals,recipes)}
      onAdd={onAddShoppingItem} onToggleChecked={onToggleChecked} onTogglePantry={onTogglePantry}
      onDelete={onDeleteShoppingItem} onClearChecked={onClearChecked}/>}
    {subTab==="prep"&&<PrepDay meals={meals} recipes={recipes} monday={monday}/>}
  </div>);
}

/* ── Week Planner ──────────────────────────────────────────── */
function WeekPlanner({meals,onSaveMeal,onDeleteMeal,onCopyWeek,recipes,themes,monday,mondayStr,personId,
  weekOffset,onPrev,onNext,onToday,isThisWeek}){
  const today=new Date();const todayStr=today.toISOString().slice(0,10);
  const sundayEnd=new Date(monday);sundayEnd.setDate(monday.getDate()+6);
  const weekLabel=`${monday.toLocaleDateString("en-GB",{day:"numeric",month:"short"})} — ${sundayEnd.toLocaleDateString("en-GB",{day:"numeric",month:"short"})}`;

  const weekDays=Array.from({length:7},(_,i)=>{
    const d=new Date(monday);d.setDate(monday.getDate()+i);
    return{date:d.toISOString().slice(0,10),dayName:d.toLocaleDateString("en-GB",{weekday:"short"}),
      dayNum:d.getDate(),isToday:d.toISOString().slice(0,10)===todayStr,dow:d.getDay(),
      isPast:d<new Date(todayStr)};
  });

  const[editing,setEditing]=useState(null);
  const[editTitle,setEditTitle]=useState("");
  const[editNotes,setEditNotes]=useState("");
  const[editRecipeId,setEditRecipeId]=useState(null);
  const[editPrepAhead,setEditPrepAhead]=useState(false);
  const[showRecipePicker,setShowRecipePicker]=useState(false);
  const[showCopyWeek,setShowCopyWeek]=useState(false);

  const startEdit=(date)=>{
    const m=meals.find(m=>m.date===date);
    setEditTitle(m?.title||"");setEditNotes(m?.notes||"");
    setEditRecipeId(m?.recipe_id||null);setEditPrepAhead(m?.is_prep_ahead||false);
    setEditing(date);setShowRecipePicker(false);
  };

  const pickRecipe=(r)=>{
    setEditTitle(r.title);setEditRecipeId(r.id);
    setEditNotes(r.notes||"");setShowRecipePicker(false);
  };

  const saveEdit=()=>{
    if(!editTitle.trim()){if(meals.find(m=>m.date===editing))onDeleteMeal(editing);setEditing(null);return;}
    onSaveMeal(editing,{title:editTitle.trim(),notes:editNotes.trim(),recipe_id:editRecipeId,
      is_prep_ahead:editPrepAhead},personId);
    setEditing(null);
  };

  const inp={width:"100%",padding:"10px 12px",borderRadius:10,border:"2px solid var(--bg-muted)",
    fontSize:14,background:"var(--bg-card)",outline:"none",fontFamily:"inherit"};

  return(<div>
    {/* Week navigation */}
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
      <button onClick={onPrev} style={{width:36,height:36,borderRadius:10,border:"none",cursor:"pointer",
        background:"var(--bg-muted)",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <Icon name="arrow_left" size={16} color="var(--text-2)"/></button>
      <div style={{textAlign:"center",flex:1}}>
        <div style={{fontSize:14,fontWeight:700,fontFamily:"'Fredoka'",color:"var(--text)"}}>
          {weekOffset===0?"This Week":weekOffset===1?"Next Week":weekOffset===-1?"Last Week":weekLabel}
        </div>
        <div style={{fontSize:11,color:"var(--text-3)"}}>{weekLabel}</div>
      </div>
      <button onClick={onNext} style={{width:36,height:36,borderRadius:10,border:"none",cursor:"pointer",
        background:"var(--bg-muted)",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <Icon name="chevron_right" size={16} color="var(--text-2)"/></button>
    </div>

    {/* Today / Copy buttons */}
    <div style={{display:"flex",gap:6,marginBottom:14}}>
      {!isThisWeek&&(<button onClick={onToday} style={{padding:"6px 12px",borderRadius:8,border:"none",
        cursor:"pointer",fontSize:11,fontWeight:600,background:"var(--gold-bg)",color:"var(--gold-deep)",
        display:"flex",alignItems:"center",gap:4}}>
        <Icon name="calendar" size={12} color="var(--gold-deep)"/>Today</button>)}
      <div style={{flex:1}}/>
      <button onClick={()=>setShowCopyWeek(!showCopyWeek)} style={{padding:"6px 12px",borderRadius:8,border:"none",
        cursor:"pointer",fontSize:11,fontWeight:600,background:showCopyWeek?"var(--sky-bg)":"var(--bg-muted)",
        color:showCopyWeek?"var(--sky)":"var(--text-3)",display:"flex",alignItems:"center",gap:4}}>
        <Icon name="refresh" size={12} color={showCopyWeek?"var(--sky)":"var(--text-3)"}/>Copy previous week
      </button>
    </div>

    {showCopyWeek&&(<div className="fu" style={{padding:"14px",borderRadius:12,background:"var(--sky-bg)",
      border:"1.5px solid var(--sky-light)",marginBottom:14,textAlign:"center"}}>
      <p style={{fontSize:13,color:"var(--text-2)",marginBottom:10}}>Copy last week's meals into this week?</p>
      <div style={{display:"flex",gap:8,justifyContent:"center"}}>
        <button onClick={()=>{
          const lastMon=new Date(monday);lastMon.setDate(lastMon.getDate()-7);
          onCopyWeek(lastMon.toISOString().slice(0,10),mondayStr,personId);setShowCopyWeek(false);
        }} style={{padding:"9px 20px",borderRadius:10,border:"none",background:"var(--sky)",
          color:"white",fontSize:13,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer"}}>Yes, copy</button>
        <button onClick={()=>setShowCopyWeek(false)} style={{padding:"9px 16px",borderRadius:10,
          border:"none",background:"var(--bg-muted)",fontSize:13,fontWeight:600,cursor:"pointer",
          color:"var(--text-3)"}}>Cancel</button>
      </div>
    </div>)}

    {/* Day cards */}
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {weekDays.map(day=>{
        const meal=meals.find(m=>m.date===day.date);
        const isEditing=editing===day.date;
        const recipe=meal?.recipe_id?recipes.find(r=>r.id===meal.recipe_id):null;

        return(<div key={day.date} style={{
          background:day.isToday?"var(--gold-bg)":day.isPast?"var(--bg)":"var(--bg-card)",
          borderRadius:"var(--r)",padding:"14px 16px",
          border:day.isToday?`2px solid var(--gold-light)`:`1.5px solid var(--bg-muted)`,
          boxShadow:day.isToday?"var(--shadow-sm)":"none",opacity:day.isPast?.5:1}}>

          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:isEditing?10:0}}>
            <div style={{minWidth:48,textAlign:"center"}}>
              <div style={{fontSize:10,fontWeight:700,color:day.isToday?"var(--gold-deep)":"var(--text-3)",
                textTransform:"uppercase",letterSpacing:.5}}>{day.dayName}</div>
              <div style={{fontSize:20,fontWeight:700,fontFamily:"'Fredoka'",
                color:day.isToday?"var(--gold-deep)":"var(--text)"}}>{day.dayNum}</div>
            </div>

            {!isEditing&&(
              <div onClick={()=>!day.isPast&&startEdit(day.date)} style={{flex:1,cursor:day.isPast?"default":"pointer",minHeight:32,
                display:"flex",flexDirection:"column",justifyContent:"center"}}>
                {meal?(<>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    {recipe?.image_url&&<img src={recipe.image_url} alt="" style={{width:32,height:32,
                      borderRadius:8,objectFit:"cover",flexShrink:0}}/>}
                    <span style={{fontWeight:600,fontSize:15,color:"var(--text)"}}>{meal.title}</span>
                    {meal.is_prep_ahead&&<span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:6,
                      background:"var(--mint-bg)",color:"var(--mint)"}}>PREP</span>}
                  </div>
                </>):(<div style={{fontSize:13,color:"var(--text-3)",fontStyle:"italic"}}>
                  {day.isPast?"—":"Tap to plan..."}
                </div>)}
              </div>)}

            {!isEditing&&meal&&!day.isPast&&(
              <button onClick={()=>startEdit(day.date)} style={{background:"none",border:"none",cursor:"pointer",
                padding:4,opacity:.4}}><Icon name="edit" size={14} color="var(--text-3)"/></button>)}
          </div>

          {isEditing&&(<div>
            <div style={{display:"flex",gap:6,marginBottom:8}}>
              <button onClick={()=>setShowRecipePicker(!showRecipePicker)} style={{
                padding:"7px 12px",borderRadius:8,border:"1.5px solid var(--coral-light)",cursor:"pointer",
                fontSize:11,fontWeight:600,background:"var(--coral-bg)",color:"var(--coral)",
                display:"flex",alignItems:"center",gap:4}}>
                <Icon name="cutlery" size={12} color="var(--coral)"/>Pick from recipes</button>
              <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,fontWeight:600,
                color:editPrepAhead?"var(--mint)":"var(--text-3)",cursor:"pointer"}}>
                <input type="checkbox" checked={editPrepAhead} onChange={e=>setEditPrepAhead(e.target.checked)}
                  style={{accentColor:"var(--mint)"}}/>Prep ahead</label>
            </div>
            {showRecipePicker&&(<div style={{marginBottom:8,maxHeight:150,overflow:"auto",borderRadius:10,
              border:"1.5px solid var(--bg-muted)",background:"var(--bg)"}}>
              {recipes.filter(r=>r.is_favourite).length>0&&(
                <div style={{fontSize:10,fontWeight:700,color:"var(--gold)",padding:"6px 10px 2px"}}>Favourites</div>)}
              {recipes.filter(r=>r.is_favourite).map(r=>(
                <button key={r.id} onClick={()=>pickRecipe(r)} style={{width:"100%",textAlign:"left",padding:"8px 10px",
                  border:"none",background:"transparent",cursor:"pointer",fontSize:13,fontWeight:500,
                  borderBottom:"1px solid var(--bg-muted)",display:"flex",alignItems:"center",gap:6}}>
                  {r.image_url&&<img src={r.image_url} alt="" style={{width:24,height:24,borderRadius:6,objectFit:"cover"}}/>}
                  <Icon name="star" size={11} color="var(--gold)"/>{r.title}</button>))}
              {recipes.filter(r=>!r.is_favourite).map(r=>(
                <button key={r.id} onClick={()=>pickRecipe(r)} style={{width:"100%",textAlign:"left",padding:"8px 10px",
                  border:"none",background:"transparent",cursor:"pointer",fontSize:13,fontWeight:500,
                  borderBottom:"1px solid var(--bg-muted)",display:"flex",alignItems:"center",gap:6}}>
                  {r.image_url&&<img src={r.image_url} alt="" style={{width:24,height:24,borderRadius:6,objectFit:"cover"}}/>}
                  {r.title}</button>))}
              {recipes.length===0&&<div style={{padding:12,fontSize:12,color:"var(--text-3)",textAlign:"center"}}>
                No recipes yet — add some in the Recipes tab</div>}
            </div>)}
            <input style={inp} placeholder="Meal name" value={editTitle}
              onChange={e=>{setEditTitle(e.target.value);if(editRecipeId)setEditRecipeId(null);}}
              onKeyDown={e=>{if(e.key==="Enter")saveEdit();}}/>
            <input style={{...inp,marginTop:6,fontSize:12}} placeholder="Notes (optional)" value={editNotes}
              onChange={e=>setEditNotes(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveEdit();}}/>
            <div style={{display:"flex",gap:6,marginTop:8}}>
              <button onClick={saveEdit} style={{flex:1,padding:"9px",borderRadius:10,border:"none",
                background:"var(--coral)",color:"white",fontSize:13,fontWeight:700,
                fontFamily:"'Fredoka'",cursor:"pointer"}}>{editTitle.trim()?"Save":"Clear"}</button>
              <button onClick={()=>setEditing(null)} style={{padding:"9px 16px",borderRadius:10,
                border:"none",background:"var(--bg-muted)",fontSize:13,fontWeight:600,cursor:"pointer",
                color:"var(--text-3)"}}>Cancel</button>
            </div>
          </div>)}
        </div>);
      })}
    </div>
  </div>);
}

/* ── Recipe Bank ───────────────────────────────────────────── */
function RecipeBank({recipes,onCreate,onUpdate,onDelete,onToggleFav,personId}){
  const[editing,setEditing]=useState(null); // null, "new", "import", or recipe object
  const[filter,setFilter]=useState("all"); // all, favourites
  const[search,setSearch]=useState("");
  const[importUrl,setImportUrl]=useState("");
  const[importing,setImporting]=useState(false);
  const[importError,setImportError]=useState("");
  const[showImport,setShowImport]=useState(false);

  const handleImport=async()=>{
    if(!importUrl.trim())return;
    setImporting(true);setImportError("");
    try{
      const data=await importRecipeFromUrl(importUrl.trim());
      // Pre-fill a recipe form with the imported data
      setEditing({
        _imported:true,
        title:data.title||"",
        prep_time:data.prep_time||"",
        cook_time:data.cook_time||"",
        servings:data.servings||4,
        notes:data.notes||"",
        is_favourite:false,
        tags:data.tags||[],
        ingredients:(data.ingredients||[]).map(i=>({name:i.name,quantity:i.quantity||"",category:i.category||"other"})),
        source_url:data.source_url||importUrl.trim(),
        image_url:data.image||null,
      });
      setShowImport(false);setImportUrl("");
    }catch(err){
      setImportError(err.message||"Failed to import");
    }
    setImporting(false);
  };

  let list=filter==="favourites"?recipes.filter(r=>r.is_favourite):recipes;
  if(search.trim())list=list.filter(r=>r.title.toLowerCase().includes(search.toLowerCase()));

  return(<div>
    <div style={{display:"flex",gap:6,marginBottom:10}}>
      <input placeholder="Search recipes..." value={search} onChange={e=>setSearch(e.target.value)}
        style={{flex:1,padding:"9px 14px",borderRadius:10,border:"2px solid var(--bg-muted)",
          fontSize:13,background:"var(--bg-card)",outline:"none",fontFamily:"inherit"}}/>
      <button onClick={()=>setFilter(filter==="all"?"favourites":"all")} style={{
        padding:"9px 12px",borderRadius:10,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
        background:filter==="favourites"?"var(--gold-bg)":"var(--bg-muted)",
        color:filter==="favourites"?"var(--gold-deep)":"var(--text-3)",
        display:"flex",alignItems:"center",gap:4}}>
        <Icon name="star" size={13} color={filter==="favourites"?"var(--gold)":"var(--text-3)"}/></button>
    </div>

    {/* Action buttons */}
    <div style={{display:"flex",gap:6,marginBottom:14}}>
      <button onClick={()=>{setShowImport(!showImport);setImportError("");}} style={{
        flex:1,padding:"10px",borderRadius:10,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
        background:showImport?"var(--sky-bg)":"var(--bg-muted)",
        color:showImport?"var(--sky)":"var(--text-3)",
        display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
        <Icon name="link" size={14} color={showImport?"var(--sky)":"var(--text-3)"}/>
        Import from URL
      </button>
      <button onClick={()=>setEditing("new")} style={{flex:1,padding:"10px",borderRadius:10,border:"none",
        cursor:"pointer",background:"var(--coral)",color:"white",fontSize:12,fontWeight:700,
        display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
        <Icon name="plus" size={14} color="white" sw={2.5}/>Add manually</button>
    </div>

    {/* Import URL panel */}
    {showImport&&!editing&&(
      <div className="fu" style={{padding:"16px",borderRadius:12,background:"var(--sky-bg)",
        border:"1.5px solid var(--sky-light)",marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:600,color:"var(--sky)",marginBottom:8,
          display:"flex",alignItems:"center",gap:5}}>
          <Icon name="link" size={13} color="var(--sky)"/>Paste a recipe URL
        </div>
        <div style={{display:"flex",gap:6}}>
          <input value={importUrl} onChange={e=>setImportUrl(e.target.value)}
            placeholder="https://www.bbcgoodfood.com/recipes/..."
            onKeyDown={e=>{if(e.key==="Enter")handleImport();}}
            style={{flex:1,padding:"10px 12px",borderRadius:10,border:"1.5px solid var(--sky-light)",
              fontSize:13,background:"var(--bg-card)",outline:"none",fontFamily:"inherit"}}/>
          <button onClick={handleImport} disabled={importing||!importUrl.trim()} style={{
            padding:"10px 18px",borderRadius:10,border:"none",background:"var(--sky)",
            color:"white",fontSize:13,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",
            opacity:importing||!importUrl.trim()?.5:1,
            display:"flex",alignItems:"center",gap:5}}>
            {importing?<><Icon name="refresh" size={13} color="white"/>Importing...</>:"Import"}
          </button>
        </div>
        {importError&&<div style={{marginTop:8,padding:"8px 12px",borderRadius:8,
          background:"var(--coral-bg)",color:"var(--coral)",fontSize:12,fontWeight:500}}>
          {importError}</div>}
        <div style={{marginTop:8,fontSize:11,color:"var(--text-3)"}}>
          Works with BBC Good Food, Jamie Oliver, Mob Kitchen, Allrecipes, Delicious, and most recipe sites.
        </div>
      </div>
    )}

    {editing&&<RecipeForm recipe={editing==="new"?null:(editing._imported?editing:editing)}
      onSave={async(r,ings)=>{
        if(editing==="new"||editing._imported)await onCreate({...r,created_by:personId},ings);
        else await onUpdate(editing.id,r,ings);
        setEditing(null);}}
      onCancel={()=>setEditing(null)}
      onDelete={editing!=="new"&&!editing._imported?async()=>{await onDelete(editing.id);setEditing(null);}:undefined}/>}

    {!editing&&(<div style={{display:"flex",flexDirection:"column",gap:8}}>
      {list.map(r=>(
        <div key={r.id} onClick={()=>setEditing(r)} style={{
          background:"var(--bg-card)",borderRadius:"var(--r)",cursor:"pointer",overflow:"hidden",
          border:"1.5px solid var(--bg-muted)",boxShadow:"var(--shadow-xs)",transition:"all .15s",
          display:"flex",alignItems:"stretch"}}>
          {r.image_url&&(
            <div style={{width:80,minHeight:70,flexShrink:0}}>
              <img src={r.image_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            </div>
          )}
          <div style={{flex:1,padding:"12px 14px",display:"flex",alignItems:"center",gap:8}}>
            <button onClick={e=>{e.stopPropagation();onToggleFav(r.id,r.is_favourite);}} style={{
              background:"none",border:"none",cursor:"pointer",padding:0}}>
              <Icon name="star" size={18} color={r.is_favourite?"var(--gold)":"var(--bg-muted)"} sw={r.is_favourite?2.5:1.8}/>
            </button>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:15}}>{r.title}</div>
              <div style={{fontSize:11,color:"var(--text-3)",marginTop:2,display:"flex",gap:8}}>
                {r.prep_time&&<span>Prep: {r.prep_time}min</span>}
                {r.cook_time&&<span>Cook: {r.cook_time}min</span>}
                {r.servings&&<span>Serves: {r.servings}</span>}
                <span>{(r.ingredients||[]).length} ingredients</span>
              </div>
            </div>
            <Icon name="chevron_right" size={16} color="var(--text-3)" style={{opacity:.4}}/>
          </div>
        </div>))}
      {list.length===0&&<div style={{textAlign:"center",padding:"40px 20px"}}>
        <Icon name="Book" size={40} color="var(--coral)" style={{opacity:.5,marginBottom:8}}/>
        <h3 style={{fontSize:16,fontFamily:"'Fredoka'",color:"var(--text-2)",marginBottom:6}}>
          {search?"No recipes match":"No recipes yet"}</h3>
        <p style={{fontSize:13,color:"var(--text-3)"}}>
          {search?"Try a different search":"Import from a URL or add one manually"}</p>
      </div>}
    </div>)}
  </div>);
}

/* ── Recipe Form ───────────────────────────────────────────── */
function RecipeForm({recipe,onSave,onCancel,onDelete}){
  const[title,setTitle]=useState(recipe?.title||"");
  const[prepTime,setPrepTime]=useState(recipe?.prep_time||"");
  const[cookTime,setCookTime]=useState(recipe?.cook_time||"");
  const[servings,setServings]=useState(recipe?.servings||4);
  const[notes,setNotes]=useState(recipe?.notes||"");
  const[isFav,setIsFav]=useState(recipe?.is_favourite||false);
  const[tags,setTags]=useState((recipe?.tags||[]).join(", "));
  const[imageUrl]=useState(recipe?.image_url||null);
  const[sourceUrl]=useState(recipe?.source_url||null);
  const[ingredients,setIngredients]=useState(
    (recipe?.ingredients||[]).map(i=>({name:i.name,quantity:i.quantity||"",category:i.category||"other"}))
  );
  const[newIngName,setNewIngName]=useState("");
  const[newIngQty,setNewIngQty]=useState("");
  const[newIngCat,setNewIngCat]=useState("other");

  const AISLE_CATS=["produce","dairy","meat","pantry","frozen","bakery","drinks","other"];

  const inp={width:"100%",padding:"10px 12px",borderRadius:10,border:"2px solid var(--bg-muted)",
    fontSize:13,background:"var(--bg-card)",outline:"none",fontFamily:"inherit"};

  const addIng=()=>{
    if(!newIngName.trim())return;
    setIngredients([...ingredients,{name:newIngName.trim(),quantity:newIngQty.trim(),category:newIngCat}]);
    setNewIngName("");setNewIngQty("");
  };

  return(<div className="fu" style={{background:"var(--bg-card)",borderRadius:"var(--r-lg)",padding:"20px",
    boxShadow:"var(--shadow-md)",marginBottom:14}}>

    {/* Image header if available */}
    {imageUrl&&(
      <div style={{margin:"-20px -20px 16px",borderRadius:"var(--r-lg) var(--r-lg) 0 0",overflow:"hidden",
        position:"relative",height:180}}>
        <img src={imageUrl} alt={title} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
        <div style={{position:"absolute",inset:0,background:"linear-gradient(transparent 50%,rgba(30,20,8,.6))"}}/>
        {sourceUrl&&(
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer"
            style={{position:"absolute",bottom:10,right:10,padding:"4px 10px",borderRadius:8,
              background:"rgba(255,255,255,.9)",fontSize:10,fontWeight:600,color:"var(--text-2)",
              textDecoration:"none",display:"flex",alignItems:"center",gap:4}}>
            <Icon name="link" size={10} color="var(--text-2)"/>Source
          </a>
        )}
      </div>
    )}

    <h3 style={{fontSize:18,marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
      <Icon name="cutlery" size={18} color="var(--coral)"/>{recipe?"Edit Recipe":"New Recipe"}</h3>

    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Name</div>
        <input style={inp} placeholder="e.g. Spaghetti Bolognese" value={title} onChange={e=>setTitle(e.target.value)}/></div>

      <div style={{display:"flex",gap:8}}>
        <div style={{flex:1}}><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Prep (min)</div>
          <input style={inp} type="number" placeholder="15" value={prepTime} onChange={e=>setPrepTime(e.target.value)}/></div>
        <div style={{flex:1}}><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Cook (min)</div>
          <input style={inp} type="number" placeholder="30" value={cookTime} onChange={e=>setCookTime(e.target.value)}/></div>
        <div style={{flex:1}}><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Serves</div>
          <input style={inp} type="number" placeholder="4" value={servings} onChange={e=>setServings(e.target.value)}/></div>
      </div>

      <div><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Method / Notes</div>
        <textarea style={{...inp,minHeight:60,resize:"vertical"}} placeholder="Instructions..." value={notes}
          onChange={e=>setNotes(e.target.value)}/></div>

      <div><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Tags (comma separated)</div>
        <input style={inp} placeholder="quick, pasta, batch-cook" value={tags} onChange={e=>setTags(e.target.value)}/></div>

      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <button onClick={()=>setIsFav(!isFav)} style={{display:"flex",alignItems:"center",gap:4,padding:"7px 12px",
          borderRadius:8,border:"none",cursor:"pointer",background:isFav?"var(--gold-bg)":"var(--bg-muted)",
          fontSize:12,fontWeight:600,color:isFav?"var(--gold-deep)":"var(--text-3)"}}>
          <Icon name="star" size={14} color={isFav?"var(--gold)":"var(--text-3)"} sw={isFav?2.5:1.8}/>
          {isFav?"Favourite":"Mark favourite"}</button>
      </div>

      {/* Ingredients */}
      <div>
        <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6,display:"flex",alignItems:"center",gap:5}}>
          <Icon name="list" size={12} color="var(--text-3)"/>Ingredients ({ingredients.length})
        </div>
        {ingredients.map((ing,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
            <span style={{flex:2,fontSize:13,fontWeight:500}}>{ing.name}</span>
            <span style={{flex:1,fontSize:12,color:"var(--text-3)"}}>{ing.quantity}</span>
            <span style={{fontSize:10,padding:"2px 6px",borderRadius:6,background:"var(--bg-muted)",color:"var(--text-3)"}}>{ing.category}</span>
            <button onClick={()=>setIngredients(ingredients.filter((_,j)=>j!==i))} style={{
              background:"none",border:"none",cursor:"pointer",padding:2}}>
              <Icon name="close" size={12} color="var(--coral)"/></button>
          </div>))}
        <div style={{display:"flex",gap:4,marginTop:6}}>
          <input style={{...inp,flex:2}} placeholder="Ingredient" value={newIngName}
            onChange={e=>setNewIngName(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();addIng();}}}/>
          <input style={{...inp,flex:1}} placeholder="Qty" value={newIngQty}
            onChange={e=>setNewIngQty(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();addIng();}}}/>
          <select value={newIngCat} onChange={e=>setNewIngCat(e.target.value)}
            style={{...inp,flex:1,padding:"8px 4px",fontSize:11}}>
            {AISLE_CATS.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={addIng} style={{padding:"8px 12px",borderRadius:10,background:"var(--mint-bg)",
            border:"1.5px solid var(--mint-light)",cursor:"pointer"}}>
            <Icon name="plus" size={14} color="var(--mint)" sw={2.5}/></button>
        </div>
      </div>

      <div style={{display:"flex",gap:8,marginTop:4}}>
        <button onClick={()=>{if(!title.trim())return;
          onSave({title:title.trim(),prep_time:parseInt(prepTime)||null,cook_time:parseInt(cookTime)||null,
            servings:parseInt(servings)||4,notes:notes.trim(),is_favourite:isFav,
            tags:tags.split(",").map(t=>t.trim()).filter(Boolean),
            image_url:imageUrl,source_url:sourceUrl},ingredients);}}
          style={{flex:1,padding:"12px",borderRadius:12,background:"var(--coral)",border:"none",
            fontSize:14,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",color:"white",
            opacity:title.trim()?1:.5}}>Save recipe</button>
        <button onClick={onCancel} style={{padding:"12px 20px",borderRadius:12,background:"var(--bg-muted)",
          border:"none",fontSize:13,fontWeight:600,cursor:"pointer",color:"var(--text-2)"}}>Cancel</button>
      </div>
      {onDelete&&<button onClick={onDelete} style={{width:"100%",padding:"10px",borderRadius:12,marginTop:4,
        background:"var(--coral-bg)",border:`1.5px solid var(--coral-light)`,color:"var(--coral)",fontSize:12,
        fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
        <Icon name="trash" size={13} color="var(--coral)"/>Delete recipe</button>}
    </div>
  </div>);
}

/* ── Shopping List ─────────────────────────────────────────── */
function ShoppingList({items,onGenerate,onAdd,onToggleChecked,onTogglePantry,onDelete,onClearChecked}){
  const[newName,setNewName]=useState("");
  const[newQty,setNewQty]=useState("");

  const grouped={};
  const AISLE_ORDER=["produce","dairy","meat","bakery","frozen","pantry","drinks","other"];
  items.filter(i=>!i.in_pantry).forEach(i=>{
    const cat=i.category||"other";if(!grouped[cat])grouped[cat]=[];grouped[cat].push(i);
  });
  const pantryItems=items.filter(i=>i.in_pantry);
  const checkedCount=items.filter(i=>i.checked&&!i.in_pantry).length;
  const totalCount=items.filter(i=>!i.in_pantry).length;

  const inp={padding:"9px 12px",borderRadius:10,border:"2px solid var(--bg-muted)",
    fontSize:13,background:"var(--bg-card)",outline:"none",fontFamily:"inherit"};

  return(<div>
    <div style={{display:"flex",gap:8,marginBottom:14}}>
      <button onClick={onGenerate} style={{flex:1,padding:"10px",borderRadius:10,border:"none",
        background:"var(--coral)",color:"white",fontSize:13,fontWeight:700,fontFamily:"'Fredoka'",
        cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
        <Icon name="refresh" size={14} color="white"/>Generate from meals</button>
      {checkedCount>0&&<button onClick={onClearChecked} style={{padding:"10px 14px",borderRadius:10,border:"none",
        background:"var(--bg-muted)",color:"var(--text-3)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
        Clear {checkedCount} done</button>}
    </div>

    {totalCount>0&&<div style={{padding:"8px 14px",borderRadius:10,background:"var(--bg-muted)",marginBottom:14,
      fontSize:12,fontWeight:600,color:"var(--text-2)",display:"flex",alignItems:"center",gap:8}}>
      <div style={{flex:1,height:6,borderRadius:3,background:"var(--bg)",overflow:"hidden"}}>
        <div style={{height:"100%",borderRadius:3,width:`${totalCount?(checkedCount/totalCount)*100:0}%`,
          background:"var(--green)",transition:"width .3s"}}/></div>
      {checkedCount}/{totalCount}
    </div>}

    {/* Grouped by aisle */}
    {AISLE_ORDER.filter(cat=>grouped[cat]?.length).map(cat=>(
      <div key={cat} style={{marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:.5,
          marginBottom:6,padding:"0 4px"}}>{cat}</div>
        {grouped[cat].map(item=>(
          <div key={item.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 4px",
            borderBottom:"1px solid var(--bg-muted)"}}>
            <button onClick={()=>onToggleChecked(item.id,item.checked)} style={{
              width:22,height:22,minWidth:22,borderRadius:6,
              border:item.checked?"none":"2px solid var(--bg-muted)",background:item.checked?"var(--green)":"transparent",
              cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {item.checked&&<Icon name="check" size={12} color="white" sw={3}/>}
            </button>
            <span style={{flex:1,fontSize:14,fontWeight:500,textDecoration:item.checked?"line-through":"none",
              color:item.checked?"var(--text-3)":"var(--text)",opacity:item.checked?.5:1}}>{item.name}</span>
            {item.quantity&&<span style={{fontSize:12,color:"var(--text-3)"}}>{item.quantity}</span>}
            {!item.checked&&<a href={`https://www.tesco.com/groceries/en-GB/search?query=${encodeURIComponent(item.name)}`}
              target="_blank" rel="noopener noreferrer"
              style={{display:"flex",alignItems:"center",justifyContent:"center",
                width:26,height:26,borderRadius:7,background:"#00539F12",border:"1px solid #00539F25",
                flexShrink:0,textDecoration:"none"}}
              title={`Search Tesco for "${item.name}"`}
              onClick={e=>e.stopPropagation()}>
              <span style={{fontSize:11,fontWeight:800,color:"#00539F",lineHeight:1}}>T</span>
            </a>}
            <button onClick={()=>onTogglePantry(item.id,item.in_pantry)} title="Always in pantry" style={{
              background:"none",border:"none",cursor:"pointer",padding:2,opacity:.4}}>
              <Icon name="home" size={13} color="var(--text-3)"/></button>
          </div>))}
      </div>))}

    {pantryItems.length>0&&(<div style={{marginTop:12}}>
      <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",marginBottom:6,display:"flex",alignItems:"center",gap:4}}>
        <Icon name="home" size={11} color="var(--text-3)"/>Always in pantry ({pantryItems.length})
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
        {pantryItems.map(item=>(
          <span key={item.id} onClick={()=>onTogglePantry(item.id,item.in_pantry)} style={{
            padding:"4px 10px",borderRadius:8,background:"var(--bg-muted)",fontSize:11,fontWeight:600,
            color:"var(--text-3)",cursor:"pointer"}}>{item.name}</span>))}
      </div>
    </div>)}

    {/* Add manual item */}
    <div style={{display:"flex",gap:6,marginTop:16}}>
      <input style={{...inp,flex:2}} placeholder="Add item..." value={newName}
        onChange={e=>setNewName(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter"&&newName.trim()){onAdd(newName.trim(),newQty.trim());setNewName("");setNewQty("");}}}/>
      <input style={{...inp,flex:1}} placeholder="Qty" value={newQty}
        onChange={e=>setNewQty(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter"&&newName.trim()){onAdd(newName.trim(),newQty.trim());setNewName("");setNewQty("");}}}/>
      <button onClick={()=>{if(!newName.trim())return;onAdd(newName.trim(),newQty.trim());setNewName("");setNewQty("");}}
        style={{padding:"9px 14px",borderRadius:10,background:"var(--coral-bg)",border:"1.5px solid var(--coral-light)",
          cursor:"pointer"}}><Icon name="plus" size={14} color="var(--coral)" sw={2.5}/></button>
    </div>

    {totalCount===0&&items.length===0&&(
      <div style={{textAlign:"center",padding:"40px 20px",color:"var(--text-3)"}}>
        <Icon name="shop" size={32} color="var(--bg-muted)"/>
        <p style={{fontSize:14,marginTop:12}}>No shopping list yet</p>
        <p style={{fontSize:12,marginTop:4}}>Plan your meals and tap "Generate from meals" to build one</p>
      </div>)}
  </div>);
}

/* ── Prep Day View ─────────────────────────────────────────── */
function PrepDay({meals,recipes,monday}){
  const sundayEnd=new Date(monday);sundayEnd.setDate(monday.getDate()+6);
  const weekMeals=meals.filter(m=>{
    const d=m.date;const ms=monday.toISOString().slice(0,10);
    return d>=ms&&d<=sundayEnd.toISOString().slice(0,10);
  });

  // All meals with recipes this week
  const allMealsWithRecipes=weekMeals.filter(m=>m.recipe_id).map(m=>({
    ...m,recipe:recipes.find(r=>r.id===m.recipe_id),
    dayName:new Date(m.date).toLocaleDateString("en-GB",{weekday:"long"}),
    dayShort:new Date(m.date).toLocaleDateString("en-GB",{weekday:"short"}),
  })).filter(m=>m.recipe);

  // Total prep time
  const totalPrepMin=allMealsWithRecipes.reduce((sum,m)=>{
    if(m.is_prep_ahead||m.recipe.prep_time) return sum+(m.recipe.prep_time||0);return sum;},0);
  const totalCookMin=allMealsWithRecipes.filter(m=>m.is_prep_ahead)
    .reduce((sum,m)=>sum+(m.recipe.cook_time||0),0);

  // Split into prep-ahead (batch cook Sunday) and daily prep
  const batchCook=allMealsWithRecipes.filter(m=>m.is_prep_ahead);
  const dailyPrep=allMealsWithRecipes.filter(m=>!m.is_prep_ahead&&m.recipe.prep_time>0);

  return(<div>
    {/* Sunday prep header */}
    <div style={{padding:"20px",borderRadius:"var(--r-lg)",marginBottom:20,
      background:"linear-gradient(135deg,var(--mint-bg),var(--green-bg))",
      border:"1.5px solid var(--mint-light)"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <Icon name="clock" size={20} color="var(--mint)"/>
        <h3 style={{fontSize:18,color:"var(--mint)",flex:1}}>Sunday Prep Session</h3>
      </div>
      <p style={{fontSize:13,color:"var(--text-2)",marginBottom:10}}>
        Get ahead for the week. Here's everything you can prep in advance.
      </p>
      {(batchCook.length>0||totalPrepMin>0)&&(
        <div style={{display:"flex",gap:12,fontSize:12,fontWeight:600,color:"var(--mint)"}}>
          <span>{batchCook.length} meals to batch cook</span>
          {totalPrepMin>0&&<span>{totalPrepMin+totalCookMin} min total</span>}
        </div>
      )}
    </div>

    {/* Batch cook meals */}
    {batchCook.length>0&&(<div style={{marginBottom:24}}>
      <h3 style={{fontSize:15,color:"var(--text-2)",marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
        <Icon name="cutlery" size={15} color="var(--coral)"/>Batch Cook
      </h3>
      {batchCook.map(m=>(
        <div key={m.id} style={{padding:"14px 16px",background:"var(--bg-card)",borderRadius:"var(--r)",
          borderLeft:"4px solid var(--mint)",marginBottom:8,boxShadow:"var(--shadow-xs)"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {m.recipe.image_url&&<img src={m.recipe.image_url} alt="" style={{width:40,height:40,borderRadius:10,objectFit:"cover"}}/>}
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:15}}>{m.title}</div>
              <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>For {m.dayName}
                {m.recipe.cook_time&&` · ${m.recipe.prep_time||0}+${m.recipe.cook_time} min`}</div>
            </div>
          </div>
          {m.recipe.ingredients?.length>0&&(
            <div style={{marginTop:10,borderTop:"1px solid var(--bg-muted)",paddingTop:8}}>
              {m.recipe.ingredients.map((ing,i)=>(
                <div key={i} style={{fontSize:12,color:"var(--text-2)",padding:"2px 0",display:"flex",gap:6}}>
                  <span style={{color:"var(--text-3)",minWidth:50,textAlign:"right"}}>{ing.quantity||""}</span>
                  <span>{ing.name}</span>
                </div>))}
            </div>)}
        </div>
      ))}
    </div>)}

    {/* Daily prep tasks */}
    {dailyPrep.length>0&&(<div>
      <h3 style={{fontSize:15,color:"var(--text-2)",marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
        <Icon name="clock" size={15} color="var(--text-3)"/>Daily Prep
      </h3>
      <p style={{fontSize:12,color:"var(--text-3)",marginBottom:10}}>
        These meals have prep work to do on the day.
      </p>
      {dailyPrep.map(m=>(
        <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",
          background:"var(--bg-card)",borderRadius:10,marginBottom:6,border:"1px solid var(--bg-muted)"}}>
          <span style={{fontSize:10,fontWeight:700,color:"var(--text-3)",minWidth:36,textTransform:"uppercase"}}>{m.dayShort}</span>
          <span style={{flex:1,fontSize:13,fontWeight:500}}>{m.title}</span>
          <span style={{fontSize:11,color:"var(--text-3)"}}>{m.recipe.prep_time} min prep</span>
        </div>))}
    </div>)}

    {batchCook.length===0&&dailyPrep.length===0&&(
      <div style={{textAlign:"center",padding:"40px 20px",color:"var(--text-3)"}}>
        <Icon name="clock" size={32} color="var(--bg-muted)"/>
        <p style={{fontSize:14,marginTop:12}}>No prep tasks this week</p>
        <p style={{fontSize:12,marginTop:4}}>Toggle "Prep ahead" on meals in the Planner to add them here</p>
      </div>)}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   NAV (now with 4 tabs)
   ═══════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════
   DIY TAB — project backlog with subtasks
   ═══════════════════════════════════════════════════════════════════════ */
function DIYTab({diy}){
  const{projects,createProject,updateProject,deleteProject,toggleSubtask,toggleProject}=diy;
  const[editing,setEditing]=useState(null); // null, "new", or project object
  const[filter,setFilter]=useState("active"); // active, all, done
  const[areaFilter,setAreaFilter]=useState("all");

  const AREAS=[
    {value:"kitchen",label:"Kitchen",icon:"pot"},
    {value:"bathroom",label:"Bathroom",icon:"wash"},
    {value:"bedroom",label:"Bedroom",icon:"moon"},
    {value:"living_room",label:"Living Room",icon:"home"},
    {value:"garden",label:"Garden",icon:"leaf"},
    {value:"outside",label:"Outside",icon:"sun"},
    {value:"other",label:"Other",icon:"box"},
  ];

  const PRIORITIES=[
    {value:"high",label:"High",color:"var(--coral)"},
    {value:"medium",label:"Medium",color:"var(--gold)"},
    {value:"low",label:"Low",color:"var(--green)"},
  ];

  let list=projects;
  if(filter==="active") list=list.filter(p=>!p.is_complete);
  if(filter==="done") list=list.filter(p=>p.is_complete);
  if(areaFilter!=="all") list=list.filter(p=>p.area===areaFilter);

  // Sort: high priority first, then medium, then low
  const priOrder={high:0,medium:1,low:2};
  list=[...list].sort((a,b)=>(priOrder[a.priority]||1)-(priOrder[b.priority]||1));

  const activeCount=projects.filter(p=>!p.is_complete).length;
  const doneCount=projects.filter(p=>p.is_complete).length;

  return(<div className="fu">
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
      <Icon name="box" size={20} color="var(--gold)"/>
      <h2 style={{fontSize:20,flex:1}}>DIY Projects</h2>
      <button onClick={()=>setEditing("new")} style={{padding:"10px 16px",borderRadius:12,background:"var(--gold)",
        border:"none",fontSize:13,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",color:"var(--text)",
        display:"flex",alignItems:"center",gap:5}}>
        <Icon name="plus" size={14} color="var(--text)" sw={2.5}/>Add</button>
    </div>

    {/* Filters */}
    <div style={{display:"flex",gap:6,marginBottom:10}}>
      {[{id:"active",label:`Active (${activeCount})`},{id:"all",label:"All"},{id:"done",label:`Done (${doneCount})`}].map(f=>(
        <button key={f.id} onClick={()=>setFilter(f.id)} style={{padding:"6px 12px",borderRadius:8,border:"none",
          cursor:"pointer",fontSize:11,fontWeight:600,
          background:filter===f.id?"var(--gold-bg)":"var(--bg-muted)",
          color:filter===f.id?"var(--gold-deep)":"var(--text-3)"}}>{f.label}</button>))}
    </div>
    <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:16}}>
      <button onClick={()=>setAreaFilter("all")} style={{padding:"4px 10px",borderRadius:8,
        cursor:"pointer",fontSize:10,fontWeight:600,
        background:areaFilter==="all"?"var(--bg-card)":"transparent",
        color:areaFilter==="all"?"var(--text-2)":"var(--text-3)",
        border:areaFilter==="all"?"1.5px solid var(--text-3)":"1.5px solid transparent"}}>All</button>
      {AREAS.map(a=>(
        <button key={a.value} onClick={()=>setAreaFilter(a.value)} style={{padding:"4px 10px",borderRadius:8,
          border:areaFilter===a.value?"1.5px solid var(--text-3)":"1.5px solid transparent",cursor:"pointer",
          fontSize:10,fontWeight:600,background:areaFilter===a.value?"var(--bg-card)":"transparent",
          color:areaFilter===a.value?"var(--text-2)":"var(--text-3)",
          display:"flex",alignItems:"center",gap:3}}>
          <Icon name={a.icon} size={9} color="var(--text-3)"/>{a.label}</button>))}
    </div>

    {/* Edit/Create form */}
    {editing&&<DIYForm project={editing==="new"?null:editing} areas={AREAS} priorities={PRIORITIES}
      onSave={async(p,subs)=>{
        if(editing==="new") await createProject(p,subs);
        else await updateProject(editing.id,p,subs);
        setEditing(null);}}
      onCancel={()=>setEditing(null)}
      onDelete={editing!=="new"?async()=>{await deleteProject(editing.id);setEditing(null);}:undefined}/>}

    {/* Project list */}
    {!editing&&(<div style={{display:"flex",flexDirection:"column",gap:8}}>
      {list.map(proj=>{
        const pri=PRIORITIES.find(p=>p.value===proj.priority)||PRIORITIES[1];
        const area=AREAS.find(a=>a.value===proj.area);
        const subsDone=(proj.subtasks||[]).filter(s=>s.is_complete).length;
        const subsTotal=(proj.subtasks||[]).length;
        const pct=subsTotal>0?Math.round(subsDone/subsTotal*100):0;

        return(<div key={proj.id} style={{background:proj.is_complete?"var(--bg)":"var(--bg-card)",
          borderRadius:"var(--r)",padding:"16px",
          border:`1.5px solid ${proj.is_complete?"var(--bg-muted)":pri.color+"25"}`,
          borderLeft:`4px solid ${proj.is_complete?"var(--green)":pri.color}`,
          boxShadow:proj.is_complete?"none":"var(--shadow-xs)",opacity:proj.is_complete?.6:1}}>

          <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
            {/* Complete toggle */}
            <button onClick={()=>toggleProject(proj.id,proj.is_complete)} style={{
              width:24,height:24,minWidth:24,borderRadius:8,marginTop:1,
              border:proj.is_complete?"none":`2.5px solid ${pri.color}`,
              background:proj.is_complete?"var(--green)":"transparent",
              cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {proj.is_complete&&<Icon name="check" size={12} color="white" sw={3}/>}
            </button>

            <div style={{flex:1,cursor:"pointer"}} onClick={()=>setEditing(proj)}>
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span style={{fontWeight:600,fontSize:15,color:"var(--text)",
                  textDecoration:proj.is_complete?"line-through":"none"}}>{proj.title}</span>
                <span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:5,
                  background:pri.color+"18",color:pri.color,textTransform:"uppercase"}}>{pri.label}</span>
                {area&&<span style={{fontSize:9,fontWeight:600,padding:"1px 6px",borderRadius:5,
                  background:"var(--bg-muted)",color:"var(--text-3)",display:"flex",alignItems:"center",gap:2}}>
                  <Icon name={area.icon} size={8} color="var(--text-3)"/>{area.label}</span>}
                {proj.estimated_time&&<span style={{fontSize:9,fontWeight:600,padding:"1px 6px",borderRadius:5,
                  background:"var(--bg-muted)",color:"var(--text-3)",display:"flex",alignItems:"center",gap:2}}>
                  <Icon name="clock" size={8} color="var(--text-3)"/>{proj.estimated_time}</span>}
              </div>
              {proj.description&&<div style={{fontSize:12,color:"var(--text-3)",marginTop:4}}>{proj.description}</div>}
            </div>
          </div>

          {/* Subtasks */}
          {subsTotal>0&&!proj.is_complete&&(
            <div style={{marginTop:10,marginLeft:34}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                <div style={{flex:1,height:4,borderRadius:2,background:"var(--bg-muted)",overflow:"hidden"}}>
                  <div style={{height:"100%",borderRadius:2,width:`${pct}%`,
                    background:pct===100?"var(--green)":pri.color,transition:"width .3s"}}/></div>
                <span style={{fontSize:10,fontWeight:600,color:"var(--text-3)"}}>{subsDone}/{subsTotal}</span>
              </div>
              {proj.subtasks.map(sub=>(
                <div key={sub.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0"}}>
                  <button onClick={()=>toggleSubtask(sub.id,sub.is_complete)} style={{
                    width:18,height:18,minWidth:18,borderRadius:5,
                    border:sub.is_complete?"none":"2px solid var(--bg-muted)",
                    background:sub.is_complete?"var(--green)":"transparent",
                    cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {sub.is_complete&&<Icon name="check" size={10} color="white" sw={3}/>}
                  </button>
                  <span style={{fontSize:13,color:sub.is_complete?"var(--text-3)":"var(--text)",
                    textDecoration:sub.is_complete?"line-through":"none"}}>{sub.label}</span>
                </div>))}
            </div>
          )}
        </div>);
      })}

      {list.length===0&&(
        <div style={{textAlign:"center",padding:"40px 20px",color:"var(--text-3)"}}>
          <Icon name="box" size={32} color="var(--bg-muted)"/>
          <p style={{fontSize:14,marginTop:12}}>
            {filter==="done"?"No completed projects yet":"No DIY projects — tap Add to create one"}</p>
        </div>)}
    </div>)}
  </div>);
}

/* ── DIY Form ──────────────────────────────────────────────── */
function DIYForm({project,areas,priorities,onSave,onCancel,onDelete}){
  const[title,setTitle]=useState(project?.title||"");
  const[desc,setDesc]=useState(project?.description||"");
  const[priority,setPriority]=useState(project?.priority||"medium");
  const[area,setArea]=useState(project?.area||"other");
  const[estTime,setEstTime]=useState(project?.estimated_time||"");
  const[subtasks,setSubtasks]=useState(
    (project?.subtasks||[]).map(s=>({label:s.label,is_complete:s.is_complete||false}))
  );
  const[newSub,setNewSub]=useState("");

  const inp={width:"100%",padding:"10px 12px",borderRadius:10,border:"2px solid var(--bg-muted)",
    fontSize:13,background:"var(--bg-card)",outline:"none",fontFamily:"inherit"};

  const addSub=()=>{if(!newSub.trim())return;setSubtasks([...subtasks,{label:newSub.trim(),is_complete:false}]);setNewSub("");};

  const TIME_PRESETS=["30 min","1 hour","2 hours","Half day","Full day","Weekend"];

  return(<div className="fu" style={{background:"var(--bg-card)",borderRadius:"var(--r-lg)",padding:"20px",
    boxShadow:"var(--shadow-md)",marginBottom:14}}>
    <h3 style={{fontSize:18,marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
      <Icon name="box" size={18} color="var(--gold)"/>{project?"Edit Project":"New Project"}</h3>

    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>What needs doing?</div>
        <input style={inp} placeholder="e.g. Fix leaky tap in bathroom" value={title} onChange={e=>setTitle(e.target.value)}/></div>

      <div><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:4}}>Details / notes</div>
        <textarea style={{...inp,minHeight:50,resize:"vertical"}} placeholder="Measurements, parts needed, paint colours..."
          value={desc} onChange={e=>setDesc(e.target.value)}/></div>

      {/* Priority */}
      <div><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6}}>Priority</div>
        <div style={{display:"flex",gap:6}}>
          {priorities.map(p=>(
            <button key={p.value} onClick={()=>setPriority(p.value)} style={{
              flex:1,padding:"10px",borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,textAlign:"center",
              border:priority===p.value?`2.5px solid ${p.color}`:"1.5px solid var(--bg-muted)",
              background:priority===p.value?p.color+"15":"transparent",color:priority===p.value?p.color:"var(--text-3)"}}>
              {p.label}</button>))}
        </div></div>

      {/* Area */}
      <div><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6}}>Area</div>
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {areas.map(a=>(
            <button key={a.value} onClick={()=>setArea(a.value)} style={{
              padding:"7px 12px",borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:600,
              border:area===a.value?"2px solid var(--text-3)":"1.5px solid var(--bg-muted)",
              background:area===a.value?"var(--bg-card)":"transparent",
              color:area===a.value?"var(--text-2)":"var(--text-3)",
              display:"flex",alignItems:"center",gap:3}}>
              <Icon name={a.icon} size={10} color={area===a.value?"var(--text-2)":"var(--text-3)"}/>{a.label}</button>))}
        </div></div>

      {/* Estimated time */}
      <div><div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6}}>How long?</div>
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {TIME_PRESETS.map(t=>(
            <button key={t} onClick={()=>setEstTime(estTime===t?"":t)} style={{
              padding:"6px 10px",borderRadius:8,cursor:"pointer",fontSize:10,fontWeight:600,
              border:estTime===t?"2px solid var(--text-3)":"1.5px solid var(--bg-muted)",
              background:estTime===t?"var(--bg-card)":"transparent",
              color:estTime===t?"var(--text-2)":"var(--text-3)"}}>
              {t}</button>))}
        </div></div>

      {/* Subtasks */}
      <div>
        <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",marginBottom:6,display:"flex",alignItems:"center",gap:4}}>
          <Icon name="list" size={11} color="var(--text-3)"/>Steps ({subtasks.length})
        </div>
        {subtasks.map((s,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
            <span style={{flex:1,fontSize:13}}>{s.label}</span>
            <button onClick={()=>setSubtasks(subtasks.filter((_,j)=>j!==i))} style={{
              background:"none",border:"none",cursor:"pointer",padding:2}}>
              <Icon name="close" size={12} color="var(--coral)"/></button>
          </div>))}
        <div style={{display:"flex",gap:4,marginTop:4}}>
          <input style={{...inp,flex:1}} placeholder="Add a step..." value={newSub}
            onChange={e=>setNewSub(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();addSub();}}}/>
          <button onClick={addSub} style={{padding:"8px 12px",borderRadius:10,background:"var(--mint-bg)",
            border:"1.5px solid var(--mint-light)",cursor:"pointer"}}>
            <Icon name="plus" size={14} color="var(--mint)" sw={2.5}/></button>
        </div>
      </div>

      {/* Save / Cancel / Delete */}
      <div style={{display:"flex",gap:8,marginTop:4}}>
        <button onClick={()=>{if(!title.trim())return;
          onSave({title:title.trim(),description:desc.trim(),priority,area,estimated_time:estTime||null},
            subtasks.map(s=>typeof s==="string"?s:s.label));}}
          style={{flex:1,padding:"12px",borderRadius:12,background:"var(--gold)",border:"none",
            fontSize:14,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",color:"var(--text)",
            opacity:title.trim()?1:.5}}>Save project</button>
        <button onClick={onCancel} style={{padding:"12px 20px",borderRadius:12,background:"var(--bg-muted)",
          border:"none",fontSize:13,fontWeight:600,cursor:"pointer",color:"var(--text-2)"}}>Cancel</button>
      </div>
      {onDelete&&<button onClick={onDelete} style={{width:"100%",padding:"10px",borderRadius:12,marginTop:4,
        background:"var(--coral-bg)",border:`1.5px solid var(--coral-light)`,color:"var(--coral)",fontSize:12,
        fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
        <Icon name="trash" size={13} color="var(--coral)"/>Delete project</button>}
    </div>
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   BIN COLLECTION CARD — shows next bin to put out
   ═══════════════════════════════════════════════════════════════════════ */
function BinCard({collections}){
  if(!collections||collections.length===0) return null;

  const today=new Date();
  const todayStr=today.toISOString().slice(0,10);
  const next=collections[0];
  if(!next) return null;

  const isPutOutToday=next.put_out_date===todayStr;
  const putOutDate=new Date(next.put_out_date);
  const daysUntilPutOut=Math.ceil((putOutDate-new Date(todayStr))/86400000);

  const isPurple=next.bin_type==="purple";
  const binColor=isPurple?"#7B2D8E":"#0077C8";
  const binBg=isPurple?"#f3e8f9":"#e3f2fd";
  const binBorder=isPurple?"#d4b5e0":"#a8d4f2";
  const binLabel=isPurple?"Purple Bin":"Blue Bin";
  const binDesc=isPurple?"General waste":"Recycling";

  if(daysUntilPutOut>6) return null;

  let urgency="";
  if(isPutOutToday) urgency="Put out tonight!";
  else if(daysUntilPutOut===1) urgency="Put out tomorrow evening";
  else urgency=`Put out ${putOutDate.toLocaleDateString("en-GB",{weekday:"long"})} evening`;

  return(
    <div className="fu" style={{marginTop:20,padding:"16px 20px",borderRadius:"var(--r-lg)",
      background:binBg,border:`1.5px solid ${binBorder}`}}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        {/* Bin icon */}
        <div style={{width:44,height:52,borderRadius:6,background:binColor,position:"relative",
          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",
          padding:"0 0 6px",flexShrink:0}}>
          <div style={{position:"absolute",top:-3,left:2,right:2,height:7,borderRadius:"3px 3px 0 0",
            background:binColor,filter:"brightness(0.85)"}}/>
          <div style={{position:"absolute",top:-6,left:"50%",transform:"translateX(-50%)",
            width:12,height:3,borderRadius:2,background:binColor,filter:"brightness(0.75)"}}/>
          <div style={{fontSize:7,fontWeight:800,color:"white",textAlign:"center",lineHeight:1.1}}>
            {isPurple?"GEN":"REC"}
          </div>
        </div>

        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
            <span style={{fontSize:16,fontWeight:700,fontFamily:"'Fredoka'",color:binColor}}>{binLabel}</span>
            {isPutOutToday&&<span style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:6,
              background:binColor,color:"white"}}>TONIGHT</span>}
          </div>
          <div style={{fontSize:13,color:"var(--text-2)",fontWeight:500}}>{binDesc}</div>
          <div style={{fontSize:12,color:"var(--text-3)",marginTop:3,display:"flex",alignItems:"center",gap:4}}>
            <Icon name="calendar" size={11} color="var(--text-3)"/>
            {urgency}
          </div>
        </div>
      </div>

      {collections[1]&&(
        <div style={{marginTop:10,paddingTop:8,borderTop:`1px solid ${binBorder}`,
          fontSize:11,color:"var(--text-3)",display:"flex",alignItems:"center",gap:4}}>
          <Icon name="refresh" size={10} color="var(--text-3)"/>
          Then: {collections[1].bin_type==="purple"?"Purple":"Blue"} bin — {
            new Date(collections[1].put_out_date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"short"})}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   ENERGY CARD — Octopus Energy usage summary (admin only, Me tab)
   ═══════════════════════════════════════════════════════════════════════ */
function EnergyCard({energy}){
  const{yesterday,live,loading}=energy;

  if(loading) return(
    <div className="fu" style={{marginTop:20,padding:"16px 20px",borderRadius:"var(--r-lg)",
      background:"var(--bg-card)",border:"1.5px solid var(--bg-muted)"}}>
      <div style={{fontSize:13,color:"var(--text-3)",display:"flex",alignItems:"center",gap:6}}>
        <Icon name="flash" size={14} color="var(--text-3)"/>Loading energy data...</div>
    </div>
  );

  if(!yesterday&&!live) return null;

  const yElec=yesterday?.electricity_kwh||0;
  const yGas=yesterday?.gas_kwh||0;
  const hasYesterday=yElec>0||yGas>0;

  const currentW=live?.current_watts||0;
  const last30=live?.last_30min_kwh||0;
  const liveReadings=live?.readings||[];
  const maxW=Math.max(...liveReadings.map(r=>r.watts||0),1);

  const liveColor=currentW<500?"var(--green)":currentW<1500?"var(--gold)":currentW<3000?"var(--peach)":"var(--coral)";
  const liveLabel=currentW<500?"Low":currentW<1500?"Normal":currentW<3000?"High":"Very High";

  const UNIT_RATE_P=24.5;
  const costPerHour=(currentW/1000)*UNIT_RATE_P;

  return(
    <div className="fu" style={{marginTop:20}}>

      {/* ── Live electricity consumption ── */}
      {live&&(
        <div style={{padding:"18px 20px",borderRadius:"var(--r-lg)",
          background:`linear-gradient(135deg,${liveColor}15,${liveColor}08)`,
          border:`1.5px solid ${liveColor}30`,marginBottom:hasYesterday?12:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:600,color:liveColor,display:"flex",alignItems:"center",gap:5}}>
              <Icon name="flash" size={14} color={liveColor}/>Electricity — Right Now
            </div>
            <div style={{fontSize:10,fontWeight:600,color:liveColor,padding:"2px 8px",
              borderRadius:6,background:"var(--bg-card)",display:"flex",alignItems:"center",gap:3}}>
              <div style={{width:6,height:6,borderRadius:3,background:liveColor}}/>
              {liveLabel}
            </div>
          </div>

          <div style={{display:"flex",alignItems:"flex-end",gap:16}}>
            {/* Current watts */}
            <div>
              <div style={{display:"flex",alignItems:"baseline",gap:4}}>
                <span style={{fontSize:36,fontWeight:700,fontFamily:"'Fredoka'",color:"var(--text)",lineHeight:1}}>
                  {currentW.toLocaleString()}</span>
                <span style={{fontSize:14,fontWeight:500,color:"var(--text-3)"}}>W</span>
              </div>
              <div style={{fontSize:12,color:"var(--text-3)",marginTop:5,display:"flex",alignItems:"center",gap:4}}>
                <span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 7px",borderRadius:6,
                  background:"var(--bg-card)",fontSize:11,fontWeight:600,color:"var(--text-2)"}}>
                  ~{costPerHour.toFixed(1)}p/hr
                </span>
              </div>
            </div>

            {/* Last 30 min */}
            <div style={{flex:1,textAlign:"right"}}>
              <div style={{fontSize:10,color:"var(--text-3)",marginBottom:2,display:"flex",alignItems:"center",
                justifyContent:"flex-end",gap:3}}>
                <Icon name="clock" size={10} color="var(--text-3)"/>Last 30 min
              </div>
              <div style={{fontSize:18,fontWeight:700,fontFamily:"'Fredoka'",color:"var(--text)"}}>
                {last30.toFixed(2)} <span style={{fontSize:11,fontWeight:500,color:"var(--text-3)"}}>kWh</span>
              </div>
            </div>
          </div>

          {/* Sparkline */}
          {liveReadings.length>1&&(
            <div style={{marginTop:14}}>
              <div style={{fontSize:9,color:"var(--text-3)",marginBottom:4,display:"flex",alignItems:"center",gap:3}}>
                <Icon name="flash" size={9} color="var(--text-3)"/>Electricity usage
              </div>
              <div style={{display:"flex",alignItems:"flex-end",gap:1,height:28}}>
                {liveReadings.map((r,i)=>{
                  const h=Math.max(Math.round((r.watts/maxW)*26),2);
                  return(<div key={i} style={{flex:1,height:h,borderRadius:2,background:liveColor,opacity:.5}}
                    title={`${r.watts}W`}/>);
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Yesterday ── */}
      {hasYesterday&&(
        <div style={{padding:"14px 18px",borderRadius:"var(--r-lg)",background:"var(--bg-card)",
          border:"1.5px solid var(--bg-muted)",display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:11,fontWeight:600,color:"var(--text-3)",minWidth:60}}>
            {yesterday?.label||"Yesterday"}</div>
          <div style={{flex:1,display:"flex",gap:14}}>
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <Icon name="flash" size={12} color="var(--sky)"/>
              <span style={{fontSize:15,fontWeight:700,fontFamily:"'Fredoka'",color:"var(--text)"}}>{yElec.toFixed(1)}</span>
              <span style={{fontSize:10,color:"var(--text-3)"}}>kWh</span>
            </div>
            {yGas>0&&(
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                <Icon name="spark" size={12} color="var(--lilac)"/>
                <span style={{fontSize:15,fontWeight:700,fontFamily:"'Fredoka'",color:"var(--text)"}}>{yGas.toFixed(1)}</span>
                <span style={{fontSize:10,color:"var(--text-3)"}}>kWh</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   RUNNING LOW — stock check visible to everyone
   ═══════════════════════════════════════════════════════════════════════ */
function RunningLowTab({items,onAdd,onToggleChecked,onDelete,onClearChecked,personId,people}){
  const{gp}=useAppCtx();
  const[newItem,setNewItem]=useState("");
  const active=items.filter(i=>!i.checked);
  const checked=items.filter(i=>i.checked);

  const handleAdd=()=>{
    if(!newItem.trim())return;
    onAdd(newItem.trim(),personId);
    setNewItem("");
  };

  return(<div className="fu">
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:18}}>
      <Icon name="shop" size={20} color="var(--peach)"/>
      <h2 style={{fontSize:20,flex:1}}>Running Low</h2>
      {checked.length>0&&(
        <button onClick={onClearChecked} style={{padding:"7px 14px",borderRadius:10,border:"none",
          cursor:"pointer",fontSize:12,fontWeight:600,background:"var(--bg-muted)",color:"var(--text-3)"}}>
          Clear {checked.length} done</button>)}
    </div>

    {/* Add item */}
    <div style={{display:"flex",gap:6,marginBottom:18}}>
      <input value={newItem} onChange={e=>setNewItem(e.target.value)}
        placeholder="What's running low?" onKeyDown={e=>{if(e.key==="Enter")handleAdd();}}
        style={{flex:1,padding:"12px 16px",borderRadius:14,border:"2px solid var(--bg-muted)",
          fontSize:15,background:"var(--bg-card)",outline:"none",fontFamily:"inherit"}}/>
      <button onClick={handleAdd} disabled={!newItem.trim()} style={{padding:"12px 18px",borderRadius:14,
        border:"none",background:"var(--peach)",color:"white",fontSize:14,fontWeight:700,
        fontFamily:"'Fredoka'",cursor:"pointer",opacity:newItem.trim()?1:.5,
        display:"flex",alignItems:"center",gap:5}}>
        <Icon name="plus" size={15} color="white" sw={2.5}/>Add</button>
    </div>

    {/* Active items */}
    {active.length>0&&(<div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
      {active.map(item=>{
        const person=item.flagged_by?gp(item.flagged_by):null;
        return(
          <div key={item.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",
            background:"var(--bg-card)",borderRadius:"var(--r)",border:"1.5px solid var(--bg-muted)",
            boxShadow:"var(--shadow-xs)"}}>
            <button onClick={()=>onToggleChecked(item.id,item.checked)} style={{
              width:24,height:24,minWidth:24,borderRadius:8,border:"2.5px solid var(--peach)",
              background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            </button>
            <span style={{flex:1,fontSize:15,fontWeight:500}}>{item.name}</span>
            {person&&<span style={{fontSize:10,fontWeight:600,color:person.color,padding:"2px 8px",
              borderRadius:8,background:person.bg}}>{person.name}</span>}
            <button onClick={()=>onDelete(item.id)} style={{background:"none",border:"none",cursor:"pointer",
              padding:2,opacity:.3}}><Icon name="close" size={13} color="var(--text-3)"/></button>
          </div>);
      })}
    </div>)}

    {/* Checked items */}
    {checked.length>0&&(<div>
      <div style={{fontSize:12,fontWeight:600,color:"var(--text-3)",marginBottom:8,
        display:"flex",alignItems:"center",gap:5}}>
        <Icon name="check" size={12} color="var(--text-3)"/>Got it ({checked.length})</div>
      <div style={{display:"flex",flexDirection:"column",gap:4}}>
        {checked.map(item=>(
          <div key={item.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",
            background:"var(--bg)",borderRadius:12,opacity:.5}}>
            <button onClick={()=>onToggleChecked(item.id,item.checked)} style={{
              width:24,height:24,minWidth:24,borderRadius:8,border:"none",
              background:"var(--green)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Icon name="check" size={12} color="white" sw={3}/>
            </button>
            <span style={{flex:1,fontSize:14,textDecoration:"line-through",color:"var(--text-3)"}}>{item.name}</span>
          </div>))}
      </div>
    </div>)}

    {active.length===0&&checked.length===0&&(
      <div style={{textAlign:"center",padding:"40px 20px",color:"var(--text-3)"}}>
        <Icon name="check" size={36} color="var(--green-light)"/>
        <p style={{fontSize:16,fontWeight:600,marginTop:12,color:"var(--green)"}}>All stocked up!</p>
        <p style={{fontSize:13,marginTop:4}}>When something's running low, add it here</p>
      </div>)}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════════════
   NAV
   ═══════════════════════════════════════════════════════════════════════ */
function Nav({tab,setTab,swapCount,items,runningLowCount}){
  const navItems=items||[{id:"me",icon:"home",label:"Me"},{id:"today",icon:"calendar",label:"Today"},
    {id:"meals",icon:"cutlery",label:"Meals"},{id:"admin",icon:"settings",label:"Admin"}];
  return(<nav style={{position:"fixed",bottom:0,left:0,right:0,background:"var(--bg-card)",
    borderTop:"1px solid var(--bg-muted)",display:"flex",justifyContent:"center",
    padding:"4px 0 max(4px, env(safe-area-inset-bottom))",zIndex:100,boxShadow:"0 -2px 12px rgba(30,20,8,.04)"}}>
    <div style={{display:"flex",maxWidth:420,width:"100%",justifyContent:"space-around"}}>
      {navItems.map(it=>{const a=tab===it.id;return(
        <button key={it.id} onClick={()=>setTab(it.id)} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,
          background:"none",border:"none",cursor:"pointer",padding:"8px 18px",borderRadius:14,
          color:a?"var(--gold-deep)":"var(--text-3)",transition:"all .2s",fontSize:10,fontWeight:600,
          position:"relative"}}>
          <Icon name={it.icon} size={20} color={a?"var(--gold-deep)":"var(--text-3)"} sw={a?2.2:1.8}/>
          <span>{it.label}</span>
          {a&&<div style={{width:4,height:4,borderRadius:"50%",background:"var(--gold)",marginTop:1}}/>}
          {it.id==="admin"&&swapCount>0&&(
            <div style={{position:"absolute",top:2,right:10,width:16,height:16,borderRadius:8,
              background:"var(--peach)",display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:9,fontWeight:800,color:"white"}}>{swapCount}</div>
          )}
          {it.id==="low"&&runningLowCount>0&&(
            <div style={{position:"absolute",top:2,right:10,width:16,height:16,borderRadius:8,
              background:"var(--coral)",display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:9,fontWeight:800,color:"white"}}>{runningLowCount}</div>
          )}
        </button>);})}
    </div>
  </nav>);
}

/* ═══════════════════════════════════════════════════════════════════════
   APP
   ═══════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════
   LOGIN SCREEN
   ═══════════════════════════════════════════════════════════════════════ */
function LoginScreen({onSignIn}){
  const[email,setEmail]=useState("");
  const[sent,setSent]=useState(false);
  const[error,setError]=useState("");
  const[loading,setLoading]=useState(false);

  const handleSubmit=async(e)=>{
    e.preventDefault();
    if(!email.trim())return;
    setLoading(true);setError("");
    const{error:err}=await onSignIn(email.trim());
    setLoading(false);
    if(err){setError(err.message);}else{setSent(true);}
  };

  const inp={width:"100%",padding:"14px 16px",borderRadius:14,border:"2px solid var(--bg-muted)",
    fontSize:16,background:"var(--bg-card)",outline:"none",fontFamily:"inherit",transition:"border-color .2s"};

  return(
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:380,textAlign:"center"}}>
        <div style={{width:64,height:64,borderRadius:20,background:"var(--gold-bg)",border:"3px solid var(--gold-light)",
          display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px"}}>
          <Icon name="home" size={32} color="var(--gold)"/></div>
        <h1 style={{fontSize:28,color:"var(--text)",marginBottom:6}}>Home HQ</h1>
        <p style={{fontSize:14,color:"var(--text-3)",marginBottom:32}}>Sign in to see your tasks</p>

        {!sent?(
          <div>
            <input style={inp} type="email" placeholder="Your email address" value={email}
              onChange={e=>setEmail(e.target.value)}
              onFocus={e=>e.target.style.borderColor="var(--gold)"}
              onBlur={e=>e.target.style.borderColor="var(--bg-muted)"}
              onKeyDown={e=>{if(e.key==="Enter")handleSubmit(e);}}/>
            <button onClick={handleSubmit} disabled={loading} style={{
              width:"100%",padding:"14px",borderRadius:14,border:"none",background:"var(--gold)",
              color:"var(--text)",fontSize:16,fontWeight:700,fontFamily:"'Fredoka'",cursor:"pointer",
              marginTop:12,opacity:loading?.6:1,transition:"all .2s",
              display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              {loading?"Sending...":"Send magic link"}
            </button>
            {error&&<p style={{color:"var(--coral)",fontSize:13,marginTop:12}}>{error}</p>}
          </div>
        ):(
          <div className="fu" style={{padding:"28px 24px",background:"var(--green-bg)",borderRadius:"var(--r-lg)",
            border:"1.5px solid var(--green-light)"}}>
            <Icon name="mail" size={28} color="var(--green)"/>
            <h3 style={{fontSize:18,color:"var(--green)",marginTop:12,marginBottom:6}}>Check your email</h3>
            <p style={{fontSize:14,color:"var(--text-2)"}}>
              We sent a magic link to <strong>{email}</strong>. Click it to sign in.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   APP — wired to Supabase
   ═══════════════════════════════════════════════════════════════════════ */
export default function App(){
  // Lazy import hooks to avoid issues when supabaseClient isn't configured yet
  const auth = useAuthHook();
  const{people,updatePerson}=usePeopleHook();
  const{tasks,createTask,updateTask:updateTaskDB,deleteTask:deleteTaskDB,
    toggleComplete}=useTasksHook();
  const{swaps,requestSwap,reassignSwap,dismissSwap}=useSwapsHook();
  const{meals,saveMeal,deleteMeal,copyWeek}=useMealsHook();
  const{categories,createCategory,updateCategory,deleteCategory}=useCategoriesHook();
  const{recipes,createRecipe,updateRecipe,deleteRecipe,toggleFavourite}=useRecipesHook();
  const{items:shoppingItems,generateFromMeals,addItem:addShoppingItem,toggleChecked:toggleShoppingChecked,
    togglePantry,deleteItem:deleteShoppingItem,clearChecked}=useShoppingListHook();
  const{themes}=useMealThemesHook();
  const{items:runningLowItems,addItem:addRunningLow,toggleChecked:toggleRunningLowChecked,
    deleteItem:deleteRunningLow,clearChecked:clearRunningLowChecked}=useRunningLowHook();
  const energy=useEnergyHook();
  const{collections:binCollections}=useBinCollectionsHook();
  const diy=useDIYHook();
  const{hearts,sendHeart}=useHeartsHook();
  const turnips=useTurnipsHook();
  const petHook=usePets(supabase);

  const personId=auth.person?.id;
  const isAdmin=auth.person?.is_admin;

  // Active people (excludes observers) — used for task assignment
  const activePeople=people.filter(p=>p.role!=="observer");
  const me=people.find(p=>p.id===personId);
  const isObserver=me?.role==="observer";
  const cats=categories.length?categories:DEFAULT_CATS;
  const gcFn=useCallback((v)=>cats.find(c=>c.value===v),[cats]);

  const[tab,setTab]=useState("me");
  const[editing,setEditing]=useState(null);
  const[detailTask,setDetailTask]=useState(null);
  const[bitsChecked,setBitsChecked]=useState([]);
  const[fromCheckbox,setFromCheckbox]=useState(false);
  const[completionNote,setCompletionNote]=useState("");

  // Find a person by id from the people array
  const gp=useCallback((id)=>people.find(p=>p.id===id),[people]);

  // Pending swap count
  const pendingSwapCount=swaps.filter(s=>s.status==="pending").length;
  const getSwap=(taskId)=>swaps.find(s=>s.task_id===taskId&&s.status==="pending");

  // Get note for a task today from completions
  const getNote=(taskId)=>{
    const task=tasks.find(t=>t.id===taskId);
    if(!task)return"";
    const comp=task.completions_today?.find(c=>c.person_id===personId);
    return comp?.note||"";
  };

  const openDetail=(task,viaCheckbox=false)=>{
    const done=task.completions_today?.some(c=>c.person_id===personId);
    const bits=task.bits||[];
    setBitsChecked(bits.map(()=>!!done));setDetailTask(task);setFromCheckbox(viaCheckbox);
    setCompletionNote(getNote(task.id));
  };

  const closeDetail=()=>{
    setDetailTask(null);setBitsChecked([]);setFromCheckbox(false);setCompletionNote("");
  };
  const toggleBit=(i)=>{setBitsChecked(prev=>{const n=[...prev];n[i]=!n[i];return n;});};

  const toggle=useCallback(async(id)=>{
    if(!personId)return;
    const task=tasks.find(t=>t.id===id);
    const alreadyDone=task?.completions_today?.some(c=>c.person_id===personId);
    await toggleComplete(id,personId,completionNote||null);
    if(task&&!alreadyDone){
      const today=new Date().toISOString().slice(0,10);
      const alreadyEarned=turnips.ledger.some(e=>e.task_id===id&&e.person_id===personId&&e.date===today);
      if(!alreadyEarned){
        const amount=task.turnip_value||5;
        turnips.earnTurnips(personId,id,amount);
      }
      // Increment pet tasks
      const myPet=petHook.getPersonPet(personId);
      if(myPet) petHook.incrementTasks(myPet.id,myPet.tasks_completed);
    } else if(task&&alreadyDone){
      turnips.revokeTurnips(personId,id);
    }
  },[personId,toggleComplete,completionNote,tasks,turnips,petHook]);

  const save=useCallback(async(taskData)=>{
    const bits=(taskData.bits||[]);
    if(taskData.id&&tasks.find(t=>t.id===taskData.id)){
      await updateTaskDB(taskData.id,taskData,bits);
    }else{
      await createTask(taskData,bits);
    }
    setEditing(null);
  },[tasks,createTask,updateTaskDB]);

  const del=useCallback(async(id)=>{
    await deleteTaskDB(id);
    setEditing(null);
  },[deleteTaskDB]);

  // Update person wrapper for admin notifications panel
  const handleUpdatePerson=useCallback(async(id,updates)=>{
    await updatePerson(id,updates);
  },[updatePerson]);

  // Today's meal
  const todayMeal=meals.find(m=>m.date===tkey());

  // Loading state
  if(auth.loading){
    return(
      <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <style>{CSS}</style>
        <div style={{textAlign:"center"}}>
          <Icon name="home" size={40} color="var(--gold)"/>
          <p style={{fontSize:14,color:"var(--text-3)",marginTop:12,fontFamily:"'Fredoka'"}}>Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if(!auth.session){
    return(<div><style>{CSS}</style><LoginScreen onSignIn={auth.signIn}/></div>);
  }

  // Logged in but no person record found
  if(!auth.person){
    return(
      <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <style>{CSS}</style>
        <div style={{textAlign:"center",maxWidth:400}}>
          <Icon name="home" size={40} color="var(--coral)"/>
          <h2 style={{fontSize:20,marginTop:16,color:"var(--text)"}}>Account not found</h2>
          <p style={{fontSize:14,color:"var(--text-3)",marginTop:8}}>
            Your email ({auth.session.user.email}) doesn't match any family member in Home HQ.
            Ask the admin to add you.
          </p>
          <button onClick={auth.signOut} style={{marginTop:20,padding:"12px 24px",borderRadius:12,
            border:"none",background:"var(--bg-muted)",fontSize:14,fontWeight:600,cursor:"pointer",
            color:"var(--text-2)"}}>Sign out</button>
        </div>
      </div>
    );
  }

  // Nav items — only show admin if is_admin
  const runningLowCount=runningLowItems.filter(i=>!i.checked).length;

  const isSuperAdmin=personId==="00000000-0000-0000-0000-000000000001";
  const navTabs=isSuperAdmin
    ?[{id:"me",icon:"home",label:"Me"},{id:"today",icon:"calendar",label:"Today"},
      {id:"low",icon:"ChatBubbleWarning",label:"Low"},{id:"rules",icon:"Book",label:"Rules"},
      {id:"meals",icon:"cutlery",label:"Meals"},
      {id:"diy",icon:"Hammer",label:"DIY"},{id:"admin",icon:"settings",label:"Admin"}]
    :isAdmin
    ?[{id:"me",icon:"home",label:"Me"},{id:"today",icon:"calendar",label:"Today"},
      {id:"low",icon:"ChatBubbleWarning",label:"Low"},{id:"rules",icon:"Book",label:"Rules"},
      {id:"admin",icon:"settings",label:"Admin"}]
    :isObserver
    ?[{id:"me",icon:"home",label:"Me"},{id:"today",icon:"calendar",label:"Today"},
      {id:"low",icon:"ChatBubbleWarning",label:"Low"},{id:"rules",icon:"Book",label:"Rules"}]
    :[{id:"me",icon:"home",label:"Me"},{id:"today",icon:"calendar",label:"Today"},
      {id:"low",icon:"ChatBubbleWarning",label:"Low"},{id:"rules",icon:"Book",label:"Rules"}];

  return(
    <AppContext.Provider value={{people,gp,cats,gc:gcFn}}>
    <div>
      <style>{CSS}</style>
      <div style={{minHeight:"100vh",background:"var(--bg)",paddingBottom:76}}>
        <main style={{maxWidth:600,margin:"0 auto",padding:"16px 16px 0"}}>
          {tab==="me" && (
            <MeWrapper tasks={tasks} onToggle={toggle} onTapTask={openDetail}
              personId={personId} people={people} gp={gp}
              todayMeal={todayMeal} recipes={recipes}
              binCollections={binCollections} energy={energy}
              isObserver={isObserver} hearts={hearts} onSendHeart={sendHeart}
              activePeople={activePeople} turnips={turnips}
              onUpdatePerson={handleUpdatePerson} petHook={petHook}/>
          )}
          {tab==="today" && <TodayTab tasks={tasks} onToggle={toggle} onTapTask={openDetail}
            personId={personId} people={people} gp={gp}/>}
          {tab==="low" && <RunningLowTab items={runningLowItems} onAdd={addRunningLow}
            onToggleChecked={toggleRunningLowChecked} onDelete={deleteRunningLow}
            onClearChecked={clearRunningLowChecked} personId={personId} people={people}/>}
          {tab==="rules" && <HouseRulesTab/>}
          {tab==="meals" && <MealTab meals={meals} onSaveMeal={(d,data)=>saveMeal(d,data,personId)} onDeleteMeal={deleteMeal}
            onCopyWeek={(src,tgt)=>copyWeek(src,tgt,personId)}
            recipes={recipes} onCreateRecipe={createRecipe} onUpdateRecipe={updateRecipe}
            onDeleteRecipe={deleteRecipe} onToggleFavourite={toggleFavourite}
            shoppingItems={shoppingItems} onGenerateShoppingList={generateFromMeals}
            onAddShoppingItem={addShoppingItem} onToggleChecked={toggleShoppingChecked}
            onTogglePantry={togglePantry} onDeleteShoppingItem={deleteShoppingItem} onClearChecked={clearChecked}
            themes={themes} personId={personId}/>}
          {tab==="diy" && isAdmin && <DIYTab diy={diy}/>}
          {tab==="admin" && isAdmin && !editing && (
            <div>
              <SwapPanel swaps={swaps} people={people} tasks={tasks}
                onReassign={(sid,tid,to)=>reassignSwap(sid,tid,to,personId)}
                onDismiss={(sid)=>dismissSwap(sid,personId)}/>
              <AdminTab tasks={tasks} onEdit={setEditing} onToggle={toggle}
                onNew={()=>setEditing("new")} onDelete={del} onTapTask={openDetail}
                people={people} onUpdatePerson={handleUpdatePerson}
                categories={categories} onCreateCategory={createCategory} onUpdateCategory={updateCategory} onDeleteCategory={deleteCategory}/>
            </div>)}
          {tab==="admin" && isAdmin && editing && (<div>
            <button onClick={()=>setEditing(null)} style={{background:"none",border:"none",cursor:"pointer",
              fontSize:14,fontWeight:600,color:"var(--text-2)",marginBottom:12,padding:0,
              display:"flex",alignItems:"center",gap:4}}>
              <Icon name="arrow_left" size={16} color="var(--text-2)"/>Back</button>
            <TaskForm task={editing==="new"?null:editing} onSave={save} people={activePeople}
              onCancel={()=>setEditing(null)} onDelete={editing!=="new"?del:undefined}/></div>)}
        </main>
        <Nav tab={tab} setTab={t=>{setTab(t);setEditing(null);}} swapCount={pendingSwapCount}
          items={navTabs} runningLowCount={runningLowCount}/>
      </div>
      {detailTask && (
        <TaskDetailCard task={detailTask} onClose={closeDetail} personId={personId}
          onToggle={toggle} bitsChecked={bitsChecked} onToggleBit={toggleBit}
          fromCheckbox={fromCheckbox} completionNote={completionNote}
          setCompletionNote={setCompletionNote}
          onRequestSwap={(tid,from,reason)=>requestSwap(tid,from,reason)}
          onSkipTurn={async(taskId,nextPerson)=>{
            // For shared tasks: reassign so the next person's turn is today
            const task=tasks.find(t=>t.id===taskId);
            if(!task)return;
            // Rotate: put nextPerson as assignee, shift others
            const rotation=[task.assignee,task.sharedWith,task.sharedWith2].filter(Boolean);
            const nextIdx=rotation.indexOf(nextPerson);
            const reordered=[...rotation.slice(nextIdx),...rotation.slice(0,nextIdx)];
            await updateTaskDB(taskId,{...task,assignee:reordered[0],
              sharedWith:reordered[1]||null,sharedWith2:reordered[2]||null},
              (task.bits||[]).map(b=>typeof b==="string"?b:b.label));
          }}
          swap={getSwap(detailTask.id)} getNote={getNote} people={people} gp={gp}
          shoppingItems={shoppingItems} runningLowItems={runningLowItems}/>)}
    </div>
    </AppContext.Provider>
  );
}